import { useMemo, useState, useEffect, useCallback, useRef, type ChangeEvent, type FormEvent } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    listHappenings,
    countHappenings,
    listPeople,
    listUsers,
    listThings,
    listPlaces,
    listHappeningTemplates,
    listHappeningParticipants,
    listParticipantsByRole,
    listParticipantRoles,
    createHappeningParticipant,
    deleteHappeningParticipant,
    updateHappeningParticipant,
    createHappening,
    updateHappening,
    deleteHappening,
    deleteHappenings,
    getHappeningImageSharing,
    type HappeningImageSharing,
    bulkUpdateHappeningStatus,
    updateTask,
    createTask,
    deleteTask,
    checkTaskDependencies,
    summarizeHappening,
    searchGenres,
    checkPlaceAvailability,
    applyTemplate,
    getTasksForHappening,
    listTasks,
    checkHappeningDependencies,
    checkBulkDependencies,
    createHappeningSeries,
    getHappeningSeriesById,
    updateHappeningSeries,
    batchGenerateHappenings,
    getSeriesInstances,
    syncHappeningArtists,
    listHappeningRoleRequirements,
    setHappeningRoleRequirements,
    setHappeningTags,
    listPlaceRoleDefaults,
    listPlaceRoleDayDefaults,
    applyChangesToFutureInstances,
    renumberSeriesEpisodes,
    getArtistsFromHappening,
    type Happening,
    type HappeningCreate,
    type HappeningUpdate,
    type HappeningType,
    type HappeningStatus,
    type RadioShowType,
    type HappeningArtist,
    type HappeningWithArtists,
    type GearRequest,
    type Person,
    type User,
    type Thing,
    type Place,
    type HappeningTemplate,
    type HappeningParticipantSummary,
    type PlaceAvailability,
    type Task,
    type TaskCreate,
    type TaskUpdate,
    type TaskType,
    type TaskStatus,
    type TaskPriority,
    type RoleAssignment,
    type TemplateRole,
    type DependencyCheckResult,
    type ArchiveLink,
    type TracklistItem,
    type RecurrencePattern,
    getCompletionReminders,
    createCompletionReminder,
    deleteCompletionReminder,
    deleteCompletionReminders,
    getProfileRemindersForHappening,
    deleteProfileRemindersForHappening,
    getCombinedRemindersForHappening,
    type CompletionReminder,
    sendHappeningCompletionReminder,
    updatePerson,
    createPerson,
    getPerson,
    summarizePerson,
    getHappeningReminders,
    createHappeningReminder,
    deleteHappeningReminders,
    deleteHappeningReminder,
    type HappeningReminder,
    getCompletionNotificationRoles,
    setCompletionNotificationRoles,
    getPostShowRemindersForHappening,
    deletePostShowRemindersForHappening,
    queueImmediateCompletionReminder,
    getHappeningById,
    listPrograms,
    listProgramPlaces,
    getProgram,
    type Program,
    type ShowSet,
} from '@littlebooker/dal';
import { TagPicker } from '@/components/TagPicker';
import { TagBadge } from '@/components/TagBadge';
import { InviteLinkButton } from '@/components/InviteLinkButton';
import { EmailInviteButton } from '@/components/EmailInviteButton';
import { SendInviteDialog } from '@/components/SendInviteDialog';
import { FormSectionHeading } from '@/components/FormSectionHeading';
import { FormSectionNav, useMeasuredHeight, type FormSectionNavItem } from '@/components/FormSectionNav';
import { pickShowFillerArtist } from '@/components/inviteLinks';
import { ImageFocusSelect } from '@/components/ImageFocusSelect';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { SocialCardsManager } from '@/components/SocialCardsManager';
import {
    ShowCardsPanel,
    type ShowCardsPanelHandle,
} from '@/components/CardEditor/ShowCardsPanel';
import { isSocialCardsEnabled } from '@/config/features';
import { saveShowSocialCards } from '@/lib/socialCards';
import { usePreferredImageRatio } from '@/hooks/usePreferredImageRatio';
import { notifyShowsUpdated } from '@/lib/webhook';
import { MarkdownGuide } from '@/components/MarkdownGuide';
import { RunningOrderEditor } from '@/components/RunningOrderEditor';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { Calendar as CalendarComponent, type CalendarItem, type VisibleRange } from '@/components/Calendar';
import { KanbanBoard } from '@/components/KanbanBoard';
import { SearchableSelect, type SelectOption } from '@/components/SearchableSelect';
import { AsyncSearchableSelect } from '@/components/AsyncSearchableSelect';
import { RecurrencePatternPicker } from '@/components/RecurrencePatternPicker';
import { useViewStore } from '@/lib/viewStore';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { utcToLocalDatetimeLocal, localDatetimeLocalToUtc, formatDateInTimezone } from '@/lib/dateUtils';
import { getAccount } from '@littlebooker/dal';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { RenumberEpisodesDialog } from '@/components/RenumberEpisodesDialog';
import { ExtendSeriesDialog } from '@/components/ExtendSeriesDialog';
import { DangerZone } from '@/components/DangerZone';
import { ApplyToSeriesDialog } from '@/components/ApplyToSeriesDialog';
import { RoleRequirementsEditor, type RoleRequirementRow } from '@/components/staffing/RoleRequirementsEditor';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertTriangle,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    Bell,
    Calendar,
    CheckCircle2,
    Clock,
    User as UserIcon,
    FileText,
    Lock,
    ListMusic,
    ListTodo,
    MapPin,
    Image,
    Music,
    Plus,
    Radio,
    Repeat,
    Search,
    Sparkles,
    Settings,
    Trash2,
    Users,
    X,
    Mail,
    ChevronDown,
    ChevronUp,
    GripVertical,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Common timezones for the dropdown (shared with AccountSettingsPage)
const COMMON_TIMEZONES = [
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam' },
    { value: 'Europe/London', label: 'Europe/London' },
    { value: 'Europe/Paris', label: 'Europe/Paris' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin' },
    { value: 'America/New_York', label: 'America/New_York' },
    { value: 'America/Chicago', label: 'America/Chicago' },
    { value: 'America/Denver', label: 'America/Denver' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney' },
];

type ShowFormState = {
    title: string;
    slug: string;
    showType: RadioShowType;
    episodeNumber: number;
    description: string;
    imageUrl: string;
    imageFocus: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
    genres: string[];
    genreInput: string;
    tagIds: string[];
    selectedArtists: HappeningArtist[];
    gearRequests: GearRequest[];
    riderNotes: string;
    archiveLinks: ArchiveLink[];
    sets: ShowSet[];
    tracklist: TracklistItem[];
    isPublic: boolean;
    isStaffPick: boolean;
    status: HappeningStatus;
    generateStubLink: boolean;
    completionReminders: Array<{ offset_hours: number }>;
    timezone: string | null; // If null, uses organization default timezone
    placeId: string | null;

    // Invite email strategy
    inviteEmailStrategy: 'combined' | 'separate' | 'none';
    combinedInviteArtistId: string | null; // person_id of artist who receives combined invite
    sendStubEmailToArtist: boolean; // Legacy field, kept for backward compatibility

    // Participants staged during creation (persisted via createHappeningParticipant after the happening is saved).
    // Only populated in create mode; edit mode uses the live participants section that mutates directly.
    stagedParticipants: Array<{
        role: string;
        source: 'users' | 'people';
        assignee_id: string | null;
        assignee_name: string;
    }>;
};

// The role the series programmer rotation fills. Named once so the save gate, the
// required-roles hints and the submitted payload cannot drift apart.
const PROGRAMMER_ROTATION_ROLE = 'programmer';

const HAPPENING_TYPE_META: Record<
    HappeningType,
    {
        label: string;
        description: string;
        icon: typeof Radio;
    }
> = {
    show: {
        label: 'Radio Show',
        description: 'Live or pre-recorded radio broadcasts with artists and hosts.',
        icon: Radio,
    },
    event: {
        label: 'Event',
        description: 'In-person events, parties, and gatherings.',
        icon: Users,
    },
    recording: {
        label: 'Recording',
        description: 'Studio sessions and recordings.',
        icon: Music,
    },
    festival: {
        label: 'Festival',
        description: 'Multi-day or multi-stage festival programming.',
        icon: Calendar,
    },
    mix: {
        label: 'Mix',
        description: 'DJ mixes, podcasts, and compilations.',
        icon: Music,
    },
    performance: {
        label: 'Performance',
        description: 'A festival or club performance scheduled on a stage.',
        icon: Music,
    },
    tech: {
        label: 'Tech / Internal',
        description: 'Soundchecks, changeovers, setup, and other internal/tech blocks. Can link staff and optional artists.',
        icon: Settings,
    },
};

const SHOW_TYPE_OPTIONS: { value: RadioShowType; label: string; description: string }[] = [
    {
        value: 'resident',
        label: 'Resident Show',
        description: 'Regular recurring show by resident artists. Title is mandatory.',
    },
    {
        value: 'guest',
        label: 'Guest Show',
        description: 'One-off or special guest appearances. Title is optional.',
    },
    {
        value: 'takeover',
        label: 'Takeover',
        description: 'Special takeover by a group of artists or collective. Title is optional.',
    },
];

const SLUG_REGEX = /[^a-z0-9-]/g;

// Module-level stable empty-array references. Used as fallbacks when a
// useQuery is disabled (data === undefined) so that we don't hand a fresh
// array into a dependency list every render — see comment at allPeople
// below for the bug history this prevents.
const EMPTY_PEOPLE: Person[] = [];
// Stable empty default, same reasoning as EMPTY_PEOPLE below.
const EMPTY_ROLES: string[] = [];
const EMPTY_NOTIFICATION_ROLES: Array<{ notify_role: string }> = [];
const EMPTY_POSTSHOW_REMINDERS: CompletionReminder[] = [];

/**
 * Status of a one-off ("send now") reminder row. Immediate rows are kept apart from the
 * scheduled lists in the edit dialog, so this is the only place their state shows.
 */
function ImmediateReminderStatus({ reminder }: { reminder?: CompletionReminder }) {
    if (!reminder) return null;
    if (reminder.sent_at) {
        return (
            <span className="text-xs text-muted-foreground">
                Sent {new Date(reminder.sent_at).toLocaleString()}
            </span>
        );
    }
    if (reminder.error_message) {
        return (
            <span className="text-xs text-destructive" title={reminder.error_message}>
                Not sent — {reminder.error_message}
            </span>
        );
    }
    return <span className="text-xs text-amber-700">Queued — goes out within 15 minutes</span>;
}

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');

const generateSlugWithDateAndTime = (title: string, startTime: string, episodeNumber?: number): string => {
    const truncatedTitle = title.length > 30 ? title.slice(0, 30) : title;
    let slug = slugify(truncatedTitle);

    // Add episode number for resident shows
    if (episodeNumber) {
        slug += `-ep${episodeNumber}`;
    }

    // Add date and time suffix
    if (startTime) {
        const date = new Date(startTime);
        const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
        const timeStr = date.toISOString().slice(11, 16).replace(':', '').toUpperCase();
        slug += `-${dateStr}-${timeStr}`;
    } else {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        const timeStr = now.toISOString().slice(11, 16).replace(':', '').toUpperCase();
        slug += `-${dateStr}-${timeStr}`;
    }

    return slug;
};

const formatArtistNames = (artists: HappeningArtist[]): string => {
    if (artists.length === 0) return '';
    if (artists.length === 1) return artists[0].name || '';

    const artistNames = artists.map(a => a.name || '').filter(name => name.trim() !== '');
    if (artistNames.length === 0) return '';
    if (artistNames.length === 1) return artistNames[0];

    // Join all but last with comma, then add & before the last one
    const allButLast = artistNames.slice(0, -1).join(', ');
    const last = artistNames[artistNames.length - 1];
    return `${allButLast} & ${last}`;
};

const defaultFormState = (): ShowFormState => ({
    title: '',
    slug: '',
    showType: 'guest',
    episodeNumber: 1,
    description: '',
    imageUrl: '',
    imageFocus: 'center',
    startTime: '',
    endTime: '',
    durationMinutes: 60,
    genres: [],
    genreInput: '',
    tagIds: [],
    selectedArtists: [],
    gearRequests: [],
    riderNotes: '',
    archiveLinks: [],
    sets: [],
    tracklist: [],
    isPublic: false,
    isStaffPick: false,
    status: 'draft',
    generateStubLink: true,
    sendStubEmailToArtist: false,
    completionReminders: [],
    timezone: null, // null means use organization default
    placeId: null,
    inviteEmailStrategy: 'none', // default to manual choice
    combinedInviteArtistId: null,
    stagedParticipants: [],
});

// Participant filter dropdown component with searchable sections
interface ParticipantFilterDropdownProps {
    participantFilter: string;
    onFilterChange: (value: string) => void;
    linkedPeople: Array<{ id: string; name: string }>;
    linkedUsers: Array<{ id: string; name: string }>;
    uniqueRoles: string[];
}

function ParticipantFilterDropdown({
    participantFilter,
    onFilterChange,
    linkedPeople,
    linkedUsers,
    uniqueRoles,
}: ParticipantFilterDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input when opening
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Get display label for current filter
    const getFilterLabel = () => {
        if (participantFilter === 'all') return 'All Happenings';
        if (participantFilter.startsWith('person:')) {
            const personId = participantFilter.replace('person:', '');
            const person = linkedPeople.find(p => p.id === personId);
            return person ? person.name : 'Filter by person...';
        }
        if (participantFilter.startsWith('user:')) {
            const userId = participantFilter.replace('user:', '');
            const user = linkedUsers.find(u => u.id === userId);
            return user ? user.name : 'Filter by user...';
        }
        if (participantFilter.startsWith('role:')) {
            const role = participantFilter.replace('role:', '');
            return role;
        }
        return 'Filter...';
    };

    // Filter options based on search query
    const filteredPeople = useMemo(() => {
        if (!searchQuery.trim()) return linkedPeople;
        const query = searchQuery.toLowerCase();
        return linkedPeople.filter(p => p.name.toLowerCase().includes(query));
    }, [linkedPeople, searchQuery]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return linkedUsers;
        const query = searchQuery.toLowerCase();
        return linkedUsers.filter(u => u.name.toLowerCase().includes(query));
    }, [linkedUsers, searchQuery]);

    const filteredRoles = useMemo(() => {
        if (!searchQuery.trim()) return uniqueRoles;
        const query = searchQuery.toLowerCase();
        return uniqueRoles.filter(r => r.toLowerCase().includes(query));
    }, [uniqueRoles, searchQuery]);

    const handleSelect = (value: string) => {
        onFilterChange(value);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[200px] hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
                <span className="truncate">{getFilterLabel()}</span>
                <ArrowDown className={cn('h-4 w-4 transition-transform ml-2 flex-shrink-0', isOpen && 'rotate-180')} />
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full min-w-[300px] rounded-md border bg-popover shadow-lg">
                    {/* Search input */}
                    <div className="border-b p-2">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Search people, users, or roles..."
                                className="w-full rounded-md border-0 bg-transparent py-1.5 pl-8 pr-8 text-sm focus:outline-none"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options list */}
                    <div className="max-h-60 overflow-y-auto p-1">
                        {/* All Happenings option */}
                        <button
                            type="button"
                            onClick={() => handleSelect('all')}
                            className={cn(
                                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                                'hover:bg-accent focus:bg-accent focus:outline-none',
                                participantFilter === 'all' && 'bg-accent font-medium'
                            )}
                        >
                            <span>All Happenings</span>
                            {participantFilter === 'all' && <CheckCircle2 className="h-4 w-4 text-primary" />}
                        </button>

                        {/* People section (programmers only) */}
                        {filteredPeople.length > 0 && (
                            <>
                                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    People (Programmers)
                                </div>
                                {filteredPeople.map((person) => {
                                    const value = `person:${person.id}`;
                                    const isSelected = participantFilter === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => handleSelect(value)}
                                            className={cn(
                                                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                                                'hover:bg-accent focus:bg-accent focus:outline-none',
                                                isSelected && 'bg-accent font-medium'
                                            )}
                                        >
                                            <span>{person.name}</span>
                                            {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                                        </button>
                                    );
                                })}
                            </>
                        )}

                        {/* Users section (programmers only) */}
                        {filteredUsers.length > 0 && (
                            <>
                                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Users (Programmers)
                                </div>
                                {filteredUsers.map((user) => {
                                    const value = `user:${user.id}`;
                                    const isSelected = participantFilter === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => handleSelect(value)}
                                            className={cn(
                                                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                                                'hover:bg-accent focus:bg-accent focus:outline-none',
                                                isSelected && 'bg-accent font-medium'
                                            )}
                                        >
                                            <span>{user.name}</span>
                                            {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                                        </button>
                                    );
                                })}
                            </>
                        )}

                        {/* Roles section */}
                        {filteredRoles.length > 0 && (
                            <>
                                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                    Roles
                                </div>
                                {filteredRoles.map((role) => {
                                    const value = `role:${role}`;
                                    const isSelected = participantFilter === value;
                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => handleSelect(value)}
                                            className={cn(
                                                'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                                                'hover:bg-accent focus:bg-accent focus:outline-none',
                                                isSelected && 'bg-accent font-medium'
                                            )}
                                        >
                                            <span>{role}</span>
                                            {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                                        </button>
                                    );
                                })}
                            </>
                        )}

                        {/* No results */}
                        {filteredPeople.length === 0 && filteredUsers.length === 0 && filteredRoles.length === 0 && searchQuery && (
                            <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const mapHappeningToFormState = (happening: Happening | HappeningWithArtists, orgTimezone: string = 'UTC'): ShowFormState => {
    // Use happening timezone if set, otherwise use organization timezone
    const effectiveTimezone = happening.timezone || orgTimezone;

    // Use getArtistsFromHappening to get fresh artist data from joined participants,
    // falling back to metadata.artists for backward compatibility
    const artists = getArtistsFromHappening(happening as HappeningWithArtists);

    return {
        title: happening.title,
        slug: happening.slug,
        showType: happening.metadata?.show_type || 'guest',
        episodeNumber: (happening.metadata?.episode_number as number) || 1,
        description: happening.description || '',
        imageUrl: happening.image_url || '',
        imageFocus: happening.metadata?.image_focus || 'center',
        startTime: happening.start_time ? utcToLocalDatetimeLocal(happening.start_time, effectiveTimezone) : '',
        endTime: happening.end_time ? utcToLocalDatetimeLocal(happening.end_time, effectiveTimezone) : '',
        durationMinutes: happening.metadata?.duration_minutes || 60,
        genres: happening.metadata?.genres || [],
        genreInput: '',
        // Tags come from the happening_tags join, which the list and detail read paths
        // both embed. Falls back to empty when a caller loaded the happening without it.
        tagIds: (happening.tags ?? []).map((t) => t.id),
        selectedArtists: artists,
        gearRequests: happening.metadata?.gear_requests || [],
        riderNotes: happening.metadata?.rider_notes || '',
        archiveLinks: (happening.metadata?.archive_links as ArchiveLink[]) || [],
        sets: happening.metadata?.sets || [],
        tracklist: (happening.metadata?.tracklist as TracklistItem[]) || [],
        isPublic: happening.is_public,
        isStaffPick: happening.metadata?.is_staff_pick === true,
        status: happening.status,
        generateStubLink: false,
        sendStubEmailToArtist: false,
        completionReminders: [], // Will be loaded separately
        timezone: happening.timezone || null, // Store the override, or null if using org default
        placeId: happening.place_id || (happening.metadata?.place_id as string | null) || null,
        // A show that has been given a strategy before reopens on that choice. Otherwise
        // 'none' here and the reminder-loading effect preselects the org default once it
        // has confirmed nothing has been emailed for this show yet.
        inviteEmailStrategy:
            (happening.metadata?.invite_email_strategy as ShowFormState['inviteEmailStrategy'] | undefined) ?? 'none',
        combinedInviteArtistId: null,
        stagedParticipants: [],
    };
};

const getTaskTypeLabel = (type: TaskType): string => {
    const labels: Record<TaskType, string> = {
        booking: 'Booking',
        preparation: 'Preparation',
        follow_up: 'Follow Up',
        technical: 'Technical',
    };
    return labels[type] || type;
};

const getStatusBadgeColor = (status: TaskStatus): string => {
    switch (status) {
        case 'done': return 'bg-green-badge text-green-badge-foreground';
        case 'in_progress': return 'bg-blue-badge text-blue-badge-foreground';
        case 'blocked': return 'bg-red-badge text-red-badge-foreground';
        case 'todo': return 'bg-muted text-muted-foreground';
        default: return 'bg-muted text-muted-foreground';
    }
};

const getPriorityBadgeColor = (priority: TaskPriority): string => {
    switch (priority) {
        case 'urgent': return 'bg-red-badge text-red-badge-foreground';
        case 'high': return 'bg-orange-badge text-orange-badge-foreground';
        case 'medium': return 'bg-yellow-badge text-yellow-badge-foreground';
        case 'low': return 'bg-muted text-muted-foreground';
        default: return 'bg-muted text-muted-foreground';
    }
};

function SortableArtistChip({
    artist,
    displayName,
    displayPicture,
    onRemove,
    onNavigate,
    onUsePhoto,
    showUsePhoto,
}: {
    artist: HappeningArtist;
    displayName: string;
    displayPicture: string | null | undefined;
    onRemove: (personId: string) => void;
    onNavigate: (personId: string) => void;
    onUsePhoto: (url: string) => void;
    showUsePhoto: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: artist.person_id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-3 py-1"
        >
            <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground" {...attributes} {...listeners}>
                <GripVertical className="h-3 w-3" />
            </button>
            {displayPicture && (
                <img src={displayPicture} alt="" className="h-5 w-5 rounded-full object-cover" />
            )}
            <button
                type="button"
                onClick={() => onNavigate(artist.person_id)}
                className="text-sm hover:underline hover:text-primary"
            >
                {displayName}
            </button>
            <button
                type="button"
                onClick={() => onRemove(artist.person_id)}
                className="text-muted-foreground hover:text-foreground"
            >
                <X className="h-3 w-3" />
            </button>
            {showUsePhoto && displayPicture && (
                <button
                    type="button"
                    onClick={() => onUsePhoto(displayPicture)}
                    className="text-xs text-primary hover:underline"
                >
                    Use photo
                </button>
            )}
        </div>
    );
}

export default function HappeningsPage() {
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState<'type' | 'template' | 'recurrence' | 'form'>('type');
    const [selectedType, setSelectedType] = useState<HappeningType>('show');
    // Recurrence state
    const [creationMode, setCreationMode] = useState<'single' | 'series' | 'batch'>('single');
    const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern | null>(null);
    const [recurrenceStartDate, setRecurrenceStartDate] = useState('');
    const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
    const [recurrenceStartTime, setRecurrenceStartTime] = useState('19:00');
    const [seriesName, setSeriesName] = useState('');
    const [seriesDefaultTitle, setSeriesDefaultTitle] = useState('');
    const [seriesDefaultDescription, setSeriesDefaultDescription] = useState('');
    const [seriesDefaultShowType, setSeriesDefaultShowType] = useState<RadioShowType>('resident');
    const [seriesStartingEpisode, setSeriesStartingEpisode] = useState(1);
    const [seriesDefaultArtists, setSeriesDefaultArtists] = useState<HappeningArtist[]>([]);
    const [seriesDefaultGenres, setSeriesDefaultGenres] = useState<string[]>([]);
    const [seriesDefaultParticipants, setSeriesDefaultParticipants] = useState<Array<{ person_id: string; role: string; person_name: string }>>([]);
    /**
     * Off by default. A series is normally a set of independent shows that share a schedule
     * (guest radio: a different artist every date), and there "apply to all future shows" only
     * ever fires by accident — one stray click copies this guest's artist, description and
     * image over every remaining date. Tick this only for a genuine template-style series.
     */
    const [seriesAllowBulkEdits, setSeriesAllowBulkEdits] = useState(false);
    // Round-robin programmer pool. Array order IS rotation order.
    const [seriesProgrammerPool, setSeriesProgrammerPool] = useState<Array<{ source: 'people' | 'users'; id: string; name: string }>>([]);
    // Steps to advance the pool before the first instance. Lets a second series over the
    // same dates (e.g. a 20:00 slot beside a 19:00 one) avoid handing both to one person.
    const [seriesProgrammerOffset, setSeriesProgrammerOffset] = useState(0);
    // Template wizard state
    const [useTemplate, setUseTemplate] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [templatePlaceId, setTemplatePlaceId] = useState<string | null>(null);
    const [templateStartTime, setTemplateStartTime] = useState('');
    const [templateEndTime, setTemplateEndTime] = useState('');
    const [templateDuration, setTemplateDuration] = useState<number | null>(null); // in minutes
    const [useEndTime, setUseEndTime] = useState(false); // toggle between duration vs end time
    const [templateTitle, setTemplateTitle] = useState(''); // optional custom title
    const [templateTimezone, setTemplateTimezone] = useState<string | null>(null); // null means use org default
    const [templateRoleAssignments, setTemplateRoleAssignments] = useState<Record<string, { id: string; name: string; source: 'users' | 'people' }[]>>({}); // role -> assignments
    const [skipRequiredRoles, setSkipRequiredRoles] = useState(false); // allow batch creation without required roles
    const [isGenerating, setIsGenerating] = useState(false); // loading state for batch/series generation
    const [availability, setAvailability] = useState<PlaceAvailability | null>(null);
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [showMarkdownGuide, setShowMarkdownGuide] = useState(false);
    const [formState, setFormState] = useState<ShowFormState>(() => defaultFormState());
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [editingHappening, setEditingHappening] = useState<Happening | null>(null);
    // The card generator is opt-in per dialog: two Konva canvases (plus their back
    // plates and font) are not worth mounting for every show edit, and an admin who
    // never opens it should never trigger a card upload on save.
    const [showCardGenerator, setShowCardGenerator] = useState(false);
    const cardsPanelRef = useRef<ShowCardsPanelHandle>(null);
    const [cardsError, setCardsError] = useState<string | null>(null);
    const [showApplyToSeriesDialog, setShowApplyToSeriesDialog] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<HappeningCreate | null>(null);
    const [showInviteConfirmDialog, setShowInviteConfirmDialog] = useState(false);
    /**
     * The answer to the invite question, held in a ref rather than form state because the
     * save runs immediately after the dialog closes — a setState would not have landed yet
     * and the emails would go out against a stale strategy.
     */
    const inviteDecisionRef = useRef<ShowFormState['inviteEmailStrategy'] | null>(null);
    /** True once this show has had any invite emailed, so we stop asking. */
    const [showHasBeenInvited, setShowHasBeenInvited] = useState(false);
    // Staffing role requirements for this happening (count = 0 means "not needed").
    const [happeningRoleReqs, setHappeningRoleReqs] = useState<RoleRequirementRow[]>([]);
    const [showMakeArtistsPublicDialog, setShowMakeArtistsPublicDialog] = useState(false);
    const [privateArtistsToPublish, setPrivateArtistsToPublish] = useState<Person[]>([]);
    const [applyToFutureLoading, setApplyToFutureLoading] = useState(false);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [newParticipantRole, setNewParticipantRole] = useState('');
    const [newParticipantId, setNewParticipantId] = useState('');
    const [newParticipantSource, setNewParticipantSource] = useState<'users' | 'people'>('users');
    const [rolesExpanded, setRolesExpanded] = useState(false);
    const [copiedEditLink, setCopiedEditLink] = useState(false);
    const [createAnother, setCreateAnother] = useState(false); // Keep form open after save
    // Program-aware creation: when set, the next created happening is stamped with this program_id
    // and (on save/close) we navigate back to `returnTo`.
    const [programIdForCreation, setProgramIdForCreation] = useState<string | null>(null);
    const [returnTo, setReturnTo] = useState<string | null>(null);
    const [programFilter, setProgramFilter] = useState<string>('all'); // 'all' or a program id
    // Track whether we already consumed the URL params on this mount.
    const consumedUrlParamsRef = useRef(false);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false); // Track photo upload state
    const [imageMismatched, setImageMismatched] = useState(false);
    const preferredImageRatio = usePreferredImageRatio();
    const [artistSearch, setArtistSearch] = useState('');
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [creatingNewArtist, setCreatingNewArtist] = useState(false);
    const [newArtistEmail, setNewArtistEmail] = useState('');
    const [isCreatingArtist, setIsCreatingArtist] = useState(false);
    const [genreSuggestions, setGenreSuggestions] = useState<string[]>([]);
    const [showGenreDropdown, setShowGenreDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortByDate, setSortByDate] = useState<'asc' | 'desc'>('asc');
    const [showCompleted, setShowCompleted] = useState(false); // Hide completed happenings by default
    const [participantFilter, setParticipantFilter] = useState<string>('all'); // 'all', 'person:{id}', 'user:{id}', or 'role:{role}'
    const storedViewMode = useViewStore((state) => state.getViewMode('happenings'));
    const setViewMode = useViewStore((state) => state.setViewMode);
    const calendarViewType = useViewStore((state) => state.getCalendarViewType('happenings'));
    const isMobile = useIsMobile();

    // On mobile, only allow 'list' and 'card' views - fallback calendar/kanban to 'card'
    const viewMode = isMobile && (storedViewMode === 'calendar' || storedViewMode === 'kanban')
        ? 'card'
        : storedViewMode;
    const setCalendarViewType = useViewStore((state) => state.setCalendarViewType);
    const [calendarVisibleRange, setCalendarVisibleRange] = useState<VisibleRange | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 50;
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [tracklistPasteText, setTracklistPasteText] = useState('');
    const [archiveForm, setArchiveForm] = useState<{ type: 'soundcloud' | 'custom'; url: string; name: string; iconSlug: string }>({
        type: 'soundcloud',
        url: '',
        name: '',
        iconSlug: '',
    });

    // Track artists eligible for combined invite (have incomplete profiles)
    const [combinedInviteCandidates, setCombinedInviteCandidates] = useState<
        Array<{ artist: HappeningArtist; person: Person; missingFields: string[] }>
    >([]);
    const [combinedReminderForHappening, setCombinedReminderForHappening] = useState<CompletionReminder | null>(null);
    // Which "send now" action is in flight ('show' or a person id), and the last one's failure.
    const [sendingNowKey, setSendingNowKey] = useState<string | null>(null);
    const [sendNowError, setSendNowError] = useState<{ key: string; message: string } | null>(null);
    const creationInitialRef = useRef<ShowFormState | null>(null);
    // Sticky section headings sit directly under the dialog header, whose height varies with
    // the title and buttons — so measure it rather than hardcoding an offset.
    const dialogHeaderRef = useRef<HTMLDivElement>(null);
    const dialogFormRef = useRef<HTMLFormElement>(null);
    const initialCompletionRemindersRef = useRef<Array<{ id?: string; offset_hours: number }>>([]);
    const initialHappeningRemindersRef = useRef<Array<{ id?: string; offset_hours: number }>>([]);
    const initialProfileRemindersRef = useRef<Array<{ id?: string; offset_hours: number; person_id: string }>>([]);
    const [completionReminders, setCompletionReminders] = useState<Array<{ id?: string; offset_hours: number }>>([]);
    const loadedRemindersForHappeningId = useRef<string | null>(null);
    const [happeningReminders, setHappeningReminders] = useState<Array<{ id?: string; offset_hours: number }>>([]);
    const loadedHappeningRemindersForHappeningId = useRef<string | null>(null);
    const [profileReminders, setProfileReminders] = useState<Array<{ id?: string; offset_hours: number; person_id: string }>>([]);
    const loadedProfileRemindersForHappeningId = useRef<string | null>(null);
    const [notifyRoles, setNotifyRoles] = useState<string[]>([]);
    const loadedNotificationRolesForHappeningId = useRef<string | null>(null);
    const [postShowEnabled, setPostShowEnabled] = useState(true);
    const [postShowOffsetHours, setPostShowOffsetHours] = useState(24);
    const [postShowFeedbackUrl, setPostShowFeedbackUrl] = useState('');
    const initialPostShowRemindersRef = useRef<Array<{ id?: string; offset_hours: number; entity_id: string }>>([]);
    const loadedPostShowRemindersForHappeningId = useRef<string | null>(null);

    // Load completion reminders when editing a happening
    const { data: existingCompletionReminders = [], isFetched: completionRemindersFetched } = useQuery({
        queryKey: ['completion-reminders', editingHappening?.id, 'happening'],
        queryFn: () => editingHappening ? getCompletionReminders('happening', editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });

    // Load happening reminders when editing a happening
    const { data: existingHappeningReminders = [], isFetched: happeningRemindersFetched } = useQuery({
        queryKey: ['happening-reminders', editingHappening?.id],
        queryFn: () => editingHappening ? getHappeningReminders(editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });

    // Load notification roles when editing a happening. Stable EMPTY fallback —
    // see comment at allPeople below for the dep-loop bug pattern this avoids.
    const { data: existingNotificationRolesData, isFetched: notificationRolesFetched } = useQuery({
        queryKey: ['completion-notification-roles', 'happening', editingHappening?.id],
        queryFn: () => editingHappening ? getCompletionNotificationRoles('happening', editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });
    const existingNotificationRoles = existingNotificationRolesData ?? EMPTY_NOTIFICATION_ROLES;

    // Load profile reminders linked to this happening
    const { data: existingProfileReminders = [], isFetched: profileRemindersFetched } = useQuery({
        queryKey: ['profile-reminders', editingHappening?.id],
        queryFn: () => editingHappening ? getProfileRemindersForHappening(editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });

    // Load post-show reminders linked to this happening. Stable EMPTY fallback —
    // see comment at allPeople below for the dep-loop bug pattern this avoids.
    const { data: existingPostShowRemindersData, isFetched: postShowRemindersFetched } = useQuery({
        queryKey: ['post-show-reminders', editingHappening?.id],
        queryFn: () => editingHappening ? getPostShowRemindersForHappening(editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });
    const existingPostShowReminders = existingPostShowRemindersData ?? EMPTY_POSTSHOW_REMINDERS;

    // Load this happening's role requirements when editing.
    const { data: loadedHappeningRoleReqs } = useQuery({
        queryKey: ['happening-role-requirements', editingHappening?.id],
        queryFn: () => editingHappening ? listHappeningRoleRequirements(editingHappening.id) : Promise.resolve([]),
        enabled: !!editingHappening,
    });

    /**
     * The series behind the show being edited, loaded only to read `allow_bulk_edits`.
     * When it is off (the default for new series) saving never offers "apply to all future
     * shows" — the edit just applies to this show.
     */
    const { data: editingSeries = null } = useQuery({
        queryKey: ['happening-series', editingHappening?.series_id],
        queryFn: () => editingHappening?.series_id
            ? getHappeningSeriesById(editingHappening.series_id)
            : Promise.resolve(null),
        enabled: !!editingHappening?.series_id,
    });
    const seriesAllowsBulkEdits = editingSeries?.allow_bulk_edits === true;

    /** Lets someone opt a series into (or back out of) the apply-to-future prompt after the fact. */
    const seriesBulkEditToggle = useMutation({
        mutationFn: ({ seriesId, allow }: { seriesId: string; allow: boolean }) =>
            updateHappeningSeries(seriesId, { allow_bulk_edits: allow }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['happening-series', variables.seriesId] });
        },
    });
    useEffect(() => {
        if (editingHappening && loadedHappeningRoleReqs) {
            setHappeningRoleReqs(loadedHappeningRoleReqs.map((r) => ({ role_id: r.role_id, count: r.count })));
        }
    }, [loadedHappeningRoleReqs, editingHappening?.id]);

    // The selected stage's default requirements (base + per-day overrides). On
    // create these auto-prefill; in both modes they back the "Apply stage
    // defaults" button.
    const { data: stageRoleBase = [] } = useQuery({
        queryKey: ['place-role-defaults', formState.placeId],
        queryFn: () => formState.placeId ? listPlaceRoleDefaults(formState.placeId) : Promise.resolve([]),
        enabled: !!formState.placeId,
    });
    const { data: stageRoleDayDefaults = [] } = useQuery({
        queryKey: ['place-role-day-defaults', formState.placeId],
        queryFn: () => formState.placeId ? listPlaceRoleDayDefaults(formState.placeId) : Promise.resolve([]),
        enabled: !!formState.placeId,
    });
    // Effective defaults = base, overridden by the override for the happening's
    // day (date taken from the start-time field; falls back to base if no date).
    const stageRoleDefaults = useMemo(() => {
        const eff = new Map<string, number>();
        for (const b of stageRoleBase) eff.set(b.role_id, b.count);
        const date = formState.startTime ? formState.startTime.split('T')[0] : '';
        if (date) {
            for (const dd of stageRoleDayDefaults) if (dd.day === date) eff.set(dd.role_id, dd.count);
        }
        return Array.from(eff, ([role_id, count]) => ({ role_id, count }));
    }, [stageRoleBase, stageRoleDayDefaults, formState.startTime]);

    useEffect(() => {
        if (!editingHappening && stageRoleDefaults.length > 0) {
            setHappeningRoleReqs((prev) => (prev.length === 0 ? stageRoleDefaults : prev));
        }
    }, [stageRoleDefaults, editingHappening]);

    // Reset reminders and notification roles when happening changes
    useEffect(() => {
        if (!editingHappening) {
            // Default reminders ON for new shows
            setCompletionReminders([{ offset_hours: -120 }]);
            initialCompletionRemindersRef.current = [{ offset_hours: -120 }];
            setNotifyRoles([]);
            loadedNotificationRolesForHappeningId.current = null;
            loadedRemindersForHappeningId.current = null;
            setHappeningReminders([{ offset_hours: -72 }]);
            initialHappeningRemindersRef.current = [{ offset_hours: -72 }];
            loadedHappeningRemindersForHappeningId.current = null;
            setProfileReminders([]);
            initialProfileRemindersRef.current = [];
            loadedProfileRemindersForHappeningId.current = null;
            setPostShowEnabled(true);
            setPostShowOffsetHours(24); // Will be updated by account settings effect
            setPostShowFeedbackUrl('');
            initialPostShowRemindersRef.current = [];
            loadedPostShowRemindersForHappeningId.current = null;
            return;
        }

        const currentHappeningId = editingHappening.id;
        if (loadedRemindersForHappeningId.current !== currentHappeningId) {
            loadedRemindersForHappeningId.current = currentHappeningId;
            setCompletionReminders([]);
        }
        if (loadedHappeningRemindersForHappeningId.current !== currentHappeningId) {
            loadedHappeningRemindersForHappeningId.current = currentHappeningId;
            setHappeningReminders([]);
        }
        if (loadedProfileRemindersForHappeningId.current !== currentHappeningId) {
            loadedProfileRemindersForHappeningId.current = currentHappeningId;
            setProfileReminders([]);
        }
        if (loadedPostShowRemindersForHappeningId.current !== currentHappeningId) {
            loadedPostShowRemindersForHappeningId.current = currentHappeningId;
            setPostShowEnabled(true);
            setPostShowOffsetHours(24); // Will be updated by account settings effect
            setPostShowFeedbackUrl('');
        }
    }, [editingHappening?.id]);

    // Load completion reminders when query completes
    useEffect(() => {
        if (!editingHappening || loadedRemindersForHappeningId.current !== editingHappening.id) {
            return;
        }

        if (completionRemindersFetched) {
            const hasExistingReminderIds = completionReminders.some(r => r.id);
            if (!hasExistingReminderIds) {
                // offset 0 rows are one-off sends (the stub invite, "send now"), not part of
                // the schedule — the dialog shows them separately and the save leaves them be.
                const scheduled = existingCompletionReminders.filter(r => r.offset_hours !== 0);
                if (scheduled.length > 0) {
                    const loadedReminders = scheduled.map(r => ({ id: r.id, offset_hours: r.offset_hours }));
                    setCompletionReminders(loadedReminders);
                    initialCompletionRemindersRef.current = JSON.parse(JSON.stringify(loadedReminders));
                } else {
                    // No existing reminders — pre-populate with defaults so they're ON when artists are present
                    const defaults = [{ offset_hours: -120 }];
                    setCompletionReminders(defaults);
                    initialCompletionRemindersRef.current = JSON.parse(JSON.stringify(defaults));
                }
            }
        }
    }, [editingHappening?.id, completionRemindersFetched]);

    // Load happening reminders when query completes
    useEffect(() => {
        if (!editingHappening || loadedHappeningRemindersForHappeningId.current !== editingHappening.id) {
            return;
        }

        if (happeningRemindersFetched) {
            const hasExistingReminderIds = happeningReminders.some(r => r.id);
            if (!hasExistingReminderIds) {
                if (existingHappeningReminders.length > 0) {
                    // Group by offset_hours to avoid duplicates (we store per artist but show unique offsets)
                    const uniqueOffsets = Array.from(new Set(existingHappeningReminders.map(r => r.offset_hours)))
                        .map(offset_hours => {
                            const reminder = existingHappeningReminders.find(r => r.offset_hours === offset_hours);
                            return { id: reminder?.id, offset_hours };
                        });
                    setHappeningReminders(uniqueOffsets);
                    initialHappeningRemindersRef.current = JSON.parse(JSON.stringify(uniqueOffsets));
                } else {
                    // No existing reminders — pre-populate with defaults
                    const defaults = [{ offset_hours: -72 }];
                    setHappeningReminders(defaults);
                    initialHappeningRemindersRef.current = JSON.parse(JSON.stringify(defaults));
                }
            }
        }
    }, [editingHappening?.id, happeningRemindersFetched]);

    // Load profile reminders when query completes
    useEffect(() => {
        if (!editingHappening || loadedProfileRemindersForHappeningId.current !== editingHappening.id) {
            return;
        }

        if (profileRemindersFetched) {
            const hasExistingReminderIds = profileReminders.some(r => r.id);
            if (!hasExistingReminderIds) {
                const loadedReminders = existingProfileReminders.filter(r => r.offset_hours !== 0).map(r => ({
                    id: r.id,
                    offset_hours: r.offset_hours,
                    person_id: r.entity_id, // entity_id is person_id for profile reminders
                }));
                setProfileReminders(loadedReminders);
                initialProfileRemindersRef.current = JSON.parse(JSON.stringify(loadedReminders)); // Deep copy for comparison
            }
        }
    }, [editingHappening?.id, profileRemindersFetched]);

    // Load post-show reminders when query completes
    useEffect(() => {
        if (!editingHappening || loadedPostShowRemindersForHappeningId.current !== editingHappening.id) {
            return;
        }

        if (postShowRemindersFetched) {
            /*
             * Skip "Send Now" rows (offset 0). Reading one back as the delay meant the next
             * save recreated it unsent, and the scheduler sends unsent immediate rows — so
             * every save after a Send Now emailed the post-show message again.
             */
            const scheduledPostShow = existingPostShowReminders.filter(r => r.offset_hours !== 0);
            if (scheduledPostShow.length > 0) {
                const loadedReminders = scheduledPostShow.map(r => ({
                    id: r.id,
                    offset_hours: r.offset_hours,
                    entity_id: r.entity_id,
                }));
                initialPostShowRemindersRef.current = JSON.parse(JSON.stringify(loadedReminders));
                setPostShowEnabled(true);
                setPostShowOffsetHours(scheduledPostShow[0].offset_hours);
                // Load per-happening feedback URL from metadata
                const metadata = editingHappening.metadata as any;
                setPostShowFeedbackUrl(metadata?.post_show_feedback_url || '');
            } else {
                // No existing post-show reminders — default to ON so they're created when artists are present
                initialPostShowRemindersRef.current = [];
                setPostShowEnabled(true);
                setPostShowOffsetHours(accountPostShowDefaultOffset);
                const metadata = editingHappening.metadata as any;
                setPostShowFeedbackUrl(metadata?.post_show_feedback_url || '');
            }
            loadedPostShowRemindersForHappeningId.current = editingHappening.id;
        }
    }, [editingHappening?.id, postShowRemindersFetched, existingPostShowReminders]);

    // Load notification roles when query completes
    useEffect(() => {
        if (!editingHappening) return;

        if (notificationRolesFetched && loadedNotificationRolesForHappeningId.current !== editingHappening.id) {
            loadedNotificationRolesForHappeningId.current = editingHappening.id;
            setNotifyRoles(existingNotificationRoles.map(r => r.notify_role));
        }
    }, [editingHappening?.id, notificationRolesFetched, existingNotificationRoles]);



    /**
     * Who can receive the combined invite.
     *
     * Deliberately permissive: any artist on the bill with an email address qualifies.
     * This used to additionally require an incomplete profile AND an existing stub link,
     * which meant the combined option silently vanished whenever everyone's profile
     * happened to be filled in — training programmers to reach for the single-purpose
     * emails instead. The scheduler already degrades a combined reminder on its own
     * (profile-only, show-only, or skipped when both halves are done), so offering it
     * unconditionally is safe; `missingFields` is kept purely to annotate the picker.
     */
    useEffect(() => {
        const detectCandidates = async () => {
            if (formState.selectedArtists.length === 0) {
                setCombinedInviteCandidates([]);
                return;
            }

            const candidates: Array<{ artist: HappeningArtist; person: Person; missingFields: string[] }> = [];

            for (const artist of formState.selectedArtists) {
                if (!artist.person_id) continue;

                try {
                    const person = await getPerson(artist.person_id);
                    if (!person || !person.email) continue;

                    const summary = summarizePerson(person);
                    candidates.push({
                        artist,
                        person,
                        missingFields: summary.missingFields ?? [],
                    });
                } catch (error) {
                    console.error(`Failed to load artist ${artist.person_id} for invites:`, error);
                }
            }

            setCombinedInviteCandidates(candidates);

            if (candidates.length > 0 && formState.inviteEmailStrategy === 'combined' && !formState.combinedInviteArtistId) {
                setFormState(prev => ({
                    ...prev,
                    combinedInviteArtistId: candidates[0].person.id,
                }));
            }
        };

        detectCandidates();
    }, [formState.selectedArtists, formState.inviteEmailStrategy, formState.combinedInviteArtistId]);

    const resetArchiveForm = () =>
        setArchiveForm({
            type: 'soundcloud',
            url: '',
            name: '',
            iconSlug: '',
        });

    const handleArchiveSave = () => {
        const url = archiveForm.url.trim();
        if (!url) return;

        if (archiveForm.type === 'soundcloud') {
            setFormState((prev) => ({
                ...prev,
                archiveLinks: [...prev.archiveLinks, { type: 'soundcloud', url }],
            }));
            resetArchiveForm();
            setShowArchiveDialog(false);
            return;
        }

        const name = archiveForm.name.trim();
        if (!name) return;

        setFormState((prev) => ({
            ...prev,
            archiveLinks: [
                ...prev.archiveLinks,
                {
                    type: 'custom',
                    name,
                    url,
                    icon_slug: archiveForm.iconSlug.trim() || null,
                },
            ],
        }));
        resetArchiveForm();
        setShowArchiveDialog(false);
    };
    const isHappeningDirty = useMemo(() => {
        if (!creationInitialRef.current) return false;
        const formStateChanged = JSON.stringify(formState) !== JSON.stringify(creationInitialRef.current);
        const completionRemindersChanged = JSON.stringify(completionReminders) !== JSON.stringify(initialCompletionRemindersRef.current);
        const happeningRemindersChanged = JSON.stringify(happeningReminders) !== JSON.stringify(initialHappeningRemindersRef.current);
        const profileRemindersChanged = JSON.stringify(profileReminders) !== JSON.stringify(initialProfileRemindersRef.current);
        const postShowChanged = postShowEnabled !== (initialPostShowRemindersRef.current.length > 0) || (postShowEnabled && postShowOffsetHours !== (initialPostShowRemindersRef.current[0]?.offset_hours ?? 24));
        return formStateChanged || completionRemindersChanged || happeningRemindersChanged || profileRemindersChanged || postShowChanged;
    }, [formState, completionReminders, happeningReminders, profileReminders, postShowEnabled, postShowOffsetHours]);
    const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

    // Delete functionality
    const { user, account } = useAuth();
    const canDelete = !user || user.role === 'owner' || user.role === 'admin';

    // "My Happenings" filter toggle
    const isMyHappeningsFilter = user && participantFilter === `user:${user.id}`;
    const toggleMyHappenings = useCallback(() => {
        if (user) {
            setParticipantFilter(isMyHappeningsFilter ? 'all' : `user:${user.id}`);
        }
    }, [user, isMyHappeningsFilter]);

    // Keyboard shortcut for "My Happenings" (Cmd+Shift+M / Ctrl+Shift+M)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'm') {
                e.preventDefault();
                toggleMyHappenings();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [toggleMyHappenings]);

    // Fetch full account data to get timezone + org defaults. Account settings
    // change rarely, but several useMemos here key on `accountData?.settings`
    // — without a staleTime each refetch returns a new object reference and
    // cascades into a re-render of everything derived from those settings.
    const { data: accountData } = useQuery({
        queryKey: ['account', account?.id],
        queryFn: () => (account?.id ? getAccount(account.id) : null),
        enabled: !!account?.id,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const orgTimezone = accountData?.timezone || 'UTC';

    // Get account-level default post-show offset and apply to new shows
    const accountPostShowDefaultOffset = useMemo(() => {
        if (accountData?.settings && typeof accountData.settings === 'object') {
            const settings = accountData.settings as Record<string, unknown>;
            if (typeof settings.postShowDefaultOffsetHours === 'number') {
                return settings.postShowDefaultOffsetHours;
            }
        }
        return 24;
    }, [accountData?.settings]);

    // Update post-show offset when account settings load (for new shows)
    useEffect(() => {
        if (!editingHappening && accountPostShowDefaultOffset !== 24) {
            setPostShowOffsetHours(accountPostShowDefaultOffset);
        }
    }, [accountPostShowDefaultOffset, editingHappening]);

    // Extract organization recurrence presets
    const orgRecurrencePresets = useMemo(() => {
        if (accountData?.settings && typeof accountData.settings === 'object') {
            const settings = accountData.settings as Record<string, unknown>;
            if (settings.recurrence_presets && Array.isArray(settings.recurrence_presets)) {
                return settings.recurrence_presets as Array<{ name: string; pattern: RecurrencePattern }>;
            }
        }
        return [];
    }, [accountData?.settings]);

    // Which roles a round-robin pool fills, and with how many members. Keyed by role
    // so adding a second rotating role later needs no new gate logic.
    const rotationCoverage = useMemo<Record<string, number>>(() => {
        const coverage: Record<string, number> = {};
        if (seriesProgrammerPool.length > 0) {
            coverage[PROGRAMMER_ROTATION_ROLE] = seriesProgrammerPool.length;
        }
        return coverage;
    }, [seriesProgrammerPool]);

    // A template's required role can be met two ways: assigned statically here, or
    // covered by a rotation that assigns someone per instance at generation time.
    // One pool member is enough — each instance takes exactly one person from it.
    const isRequiredRoleSatisfiedByRotation = useCallback((role: string) => (
        creationMode === 'series' && (rotationCoverage[role] ?? 0) > 0
    ), [creationMode, rotationCoverage]);

    const isRequiredRoleSatisfied = useCallback((role: { role: string; min_count: number }) => {
        if (role.min_count <= 0) return true;
        const assignedCount = templateRoleAssignments[role.role]?.length ?? 0;
        if (assignedCount >= role.min_count) return true;
        return isRequiredRoleSatisfiedByRotation(role.role);
    }, [templateRoleAssignments, isRequiredRoleSatisfiedByRotation]);

    // Extract organization default invite settings
    const orgDefaultGenerateArtistLink = useMemo(() => {
        if (accountData?.settings && typeof accountData.settings === 'object') {
            const settings = accountData.settings as Record<string, unknown>;
            if (typeof settings.defaultGenerateArtistLink === 'boolean') {
                return settings.defaultGenerateArtistLink;
            }
        }
        return true; // default to true if not set
    }, [accountData?.settings]);

    /**
     * The artist the show link belongs to.
     *
     * A show has exactly one stub token, and whoever holds it can edit the whole show, so
     * the combined link is aimed at a single designated filler rather than at everyone on
     * the bill. That is the rule the invite emails have always followed (the 'separate'
     * strategy mails the stub to the first artist only); the copy buttons now follow it too.
     */
    /** Candidates whose profile is actually unfinished — who the profile-only emails go to. */
    const artistsWithIncompleteProfiles = useMemo(
        () => combinedInviteCandidates.filter((candidate) => candidate.missingFields.length > 0),
        [combinedInviteCandidates],
    );

    /**
     * True when nothing is left for anyone to fill in. The scheduler marks a combined
     * reminder sent-but-skipped in that case, so the dialog says so rather than promising
     * an email that will never arrive.
     */
    const everythingAlreadyComplete = useMemo(() => {
        if (combinedInviteCandidates.length === 0) return false;
        const profilesDone = artistsWithIncompleteProfiles.length === 0;
        const showDone = editingHappening
            ? summarizeHappening(editingHappening).missingFields.length === 0
            : false;
        return profilesDone && showDone;
    }, [combinedInviteCandidates, artistsWithIncompleteProfiles, editingHappening]);

    const showFillerArtist = useMemo(() => {
        const withPerson = formState.selectedArtists.filter((artist) => artist.person_id);
        if (withPerson.length === 0) return null;
        const designated = withPerson.find(
            (artist) => artist.person_id === formState.combinedInviteArtistId,
        );
        if (designated) return designated;
        return [...withPerson].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        )[0];
    }, [formState.selectedArtists, formState.combinedInviteArtistId]);

    const showFillerPersonId = showFillerArtist?.person_id ?? null;

    /**
     * Everyone else on the bill, offered a profile-only link. Deliberately not combined:
     * handing a co-artist the combined URL would hand them the show's stub token and with
     * it edit rights over the whole show.
     */
    const coArtistProfileAlternates = useMemo(
        () =>
            formState.selectedArtists
                .filter((artist) => artist.person_id && artist.person_id !== showFillerPersonId)
                .map((artist) => ({
                    personId: artist.person_id,
                    profileOnly: true,
                    label: `Profile link for ${artist.name ?? artist.slug}`,
                })),
        [formState.selectedArtists, showFillerPersonId],
    );

    /**
     * The jump bar's contents. Must mirror exactly which sections the form actually renders —
     * several are conditional on edit-vs-create — or a chip scrolls to nothing.
     */
    useMeasuredHeight(dialogHeaderRef, dialogFormRef, '--lb-dialog-header-h');

    const formSectionNavItems = useMemo<FormSectionNavItem[]>(() => {
        const items: FormSectionNavItem[] = [
            { id: 'section-basic', label: 'Basics' },
            { id: 'section-schedule', label: 'Schedule' },
            { id: 'section-content', label: 'Content' },
        ];
        if (editingHappening) items.push({ id: 'section-tasks', label: 'Tasks' });
        items.push({ id: 'section-reminders', label: 'Reminders' });
        items.push({ id: 'section-resources', label: 'Resources' });
        items.push({ id: 'section-staffing-requirements', label: 'Staffing' });
        items.push({
            id: editingHappening ? 'section-participants' : 'section-participants-create',
            label: 'Participants',
        });
        return items;
    }, [editingHappening]);

    const orgDefaultInviteEmailStrategy = useMemo((): ShowFormState['inviteEmailStrategy'] => {
        if (accountData?.settings && typeof accountData.settings === 'object') {
            const settings = accountData.settings as Record<string, unknown>;
            if (settings.defaultInviteEmailStrategy === 'combined' || settings.defaultInviteEmailStrategy === 'separate' || settings.defaultInviteEmailStrategy === 'none') {
                return settings.defaultInviteEmailStrategy;
            }
        }
        return 'combined'; // default to combined if not set
    }, [accountData?.settings]);

    // Helper to create form state with org defaults applied
    const defaultFormStateWithOrgDefaults = useCallback(() => ({
        ...defaultFormState(),
        generateStubLink: orgDefaultGenerateArtistLink,
        inviteEmailStrategy: orgDefaultInviteEmailStrategy,
    }), [orgDefaultGenerateArtistLink, orgDefaultInviteEmailStrategy]);

    // Load existing combined-invite reminder for the happening being edited (so we can show "sent" status)
    useEffect(() => {
        let cancelled = false;
        // A fresh dialog is a fresh decision — never carry the last show's answer over.
        inviteDecisionRef.current = null;
        const happeningId = editingHappening?.id;
        if (!happeningId) {
            setCombinedReminderForHappening(null);
            setShowHasBeenInvited(false);
            return;
        }
        (async () => {
            try {
                // Both kinds of show-side invite: 'combined_artist_show' covers the combined
                // strategy, 'happening' the stub email the separate strategy sends. Between
                // them they answer "has anyone already been emailed about this show?".
                const [combined, stubEmails] = await Promise.all([
                    getCombinedRemindersForHappening(happeningId),
                    getCompletionReminders('happening', happeningId),
                ]);
                if (cancelled) return;
                setCombinedReminderForHappening(combined[0] ?? null);

                // A resend should reach whoever got the invite, not whichever artist is listed first.
                const previousRecipient = combined[0]?.entity_id;
                if (previousRecipient && editingHappening?.metadata?.artists?.some(a => a.person_id === previousRecipient)) {
                    setFormState(prev => ({ ...prev, combinedInviteArtistId: previousRecipient }));
                    if (creationInitialRef.current) {
                        creationInitialRef.current = { ...creationInitialRef.current, combinedInviteArtistId: previousRecipient };
                    }
                }

                /*
                 * Preselect the org's default strategy when opening a show that has never had
                 * an invite sent. Without this the edit dialog always opened on "don't email",
                 * so the recommended default only ever applied at creation — and most invites
                 * go out on a later edit, once the artist is confirmed.
                 *
                 * Guarded deliberately: if anything has already been emailed for this show, we
                 * stay on 'none'. Re-saving a show for an unrelated reason must never queue a
                 * second email to a real artist.
                 */
                const alreadyInvited = combined.length > 0 || stubEmails.length > 0;
                setShowHasBeenInvited(alreadyInvited);
                const persisted = editingHappening?.metadata?.invite_email_strategy;
                if (!alreadyInvited && !persisted && orgDefaultInviteEmailStrategy !== 'none') {
                    setFormState(prev => {
                        // Only preselect while the field is untouched.
                        if (prev.inviteEmailStrategy !== 'none') return prev;
                        const next = { ...prev, inviteEmailStrategy: orgDefaultInviteEmailStrategy };
                        // Keep the dirty-check baseline in step, or simply opening the dialog
                        // would look like an unsaved change and trigger the discard prompt.
                        if (creationInitialRef.current) {
                            creationInitialRef.current = {
                                ...creationInitialRef.current,
                                inviteEmailStrategy: orgDefaultInviteEmailStrategy,
                            };
                        }
                        return next;
                    });
                }
            } catch (error) {
                console.error('Failed to load combined reminder for happening:', error);
                if (!cancelled) setCombinedReminderForHappening(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [editingHappening?.id, editingHappening?.metadata?.invite_email_strategy, orgDefaultInviteEmailStrategy]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingHappening, setDeletingHappening] = useState<Happening | null>(null);
    const [deleteDependencies, setDeleteDependencies] = useState<DependencyCheckResult | null>(null);
    const [deleteImageSharing, setDeleteImageSharing] = useState<HappeningImageSharing | null>(null);
    // How many later episodes a delete would leave stranded one number too high.
    const [deleteLaterEpisodeCount, setDeleteLaterEpisodeCount] = useState(0);
    const [renumberDialogOpen, setRenumberDialogOpen] = useState(false);
    const [extendSeriesDialogOpen, setExtendSeriesDialogOpen] = useState(false);
    const [selectedHappeningIds, setSelectedHappeningIds] = useState<Set<string>>(new Set());
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteDependencies, setBulkDeleteDependencies] = useState<Map<string, DependencyCheckResult>>(new Map());
    const [checkingBulkDependencies, setCheckingBulkDependencies] = useState(false);
    const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
    const [bulkStatusValue, setBulkStatusValue] = useState<HappeningStatus>('scheduled');

    // Translate the participant dropdown selection into database filters. The
    // person/user options mean "this programmer"; the role options mean "anyone
    // in this role".
    const participantQueryFilter = useMemo((): Record<string, string> => {
        if (participantFilter.startsWith('person:')) {
            return {
                participant_person_id: participantFilter.slice('person:'.length),
                participant_role: PROGRAMMER_ROTATION_ROLE,
            };
        }
        if (participantFilter.startsWith('user:')) {
            return {
                participant_user_id: participantFilter.slice('user:'.length),
                participant_role: PROGRAMMER_ROTATION_ROLE,
            };
        }
        if (participantFilter.startsWith('role:')) {
            return { participant_role: participantFilter.slice('role:'.length) };
        }
        return {};
    }, [participantFilter]);

    // Compute query filters based on view mode
    const happeningsQueryFilter = useMemo(() => {
        // Always include artists for fresh data from people table
        const baseFilter: Record<string, unknown> = { includeArtists: true, ...participantQueryFilter };
        if (programFilter !== 'all') {
            baseFilter.program_id = programFilter;
        }

        if (viewMode === 'calendar' && calendarVisibleRange) {
            // Calendar view: filter by visible date range (always show completed)
            const start = new Date(calendarVisibleRange.start);
            start.setDate(start.getDate() - 1);
            const end = new Date(calendarVisibleRange.end);
            end.setDate(end.getDate() + 2);
            return {
                ...baseFilter,
                start_time_gte: start.toISOString(),
                start_time_lte: end.toISOString(),
            };
        }
        if (viewMode === 'list') {
            // List view: use pagination with server-side sorting
            // Hide completed happenings by default (unless toggle is on)
            return {
                ...baseFilter,
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                order: sortByDate,
                ...(showCompleted ? {} : { status_not: 'completed' }),
            };
        }
        // Other views (kanban): optionally hide completed
        return showCompleted ? baseFilter : { ...baseFilter, status_not: 'completed' };
    }, [viewMode, calendarVisibleRange, currentPage, pageSize, sortByDate, showCompleted, programFilter, participantQueryFilter]);

    const { data: happenings = [], isLoading } = useQuery({
        queryKey: ['happenings', happeningsQueryFilter],
        queryFn: () => listHappenings(happeningsQueryFilter),
    });

    // Count total happenings for pagination (only needed in list view).
    // Must mirror the list query's filters — status_not, program, and the
    // participant filter — or the page count describes a different result set.
    const countFilter = useMemo(() => ({
        ...participantQueryFilter,
        ...(programFilter !== 'all' ? { program_id: programFilter } : {}),
        ...(showCompleted ? {} : { status_not: 'completed' }),
    }), [participantQueryFilter, programFilter, showCompleted]);
    const { data: totalHappeningsCount = 0 } = useQuery({
        queryKey: ['happeningsCount', countFilter],
        queryFn: () => countHappenings(countFilter),
        enabled: viewMode === 'list',
    });

    const totalPages = Math.ceil(totalHappeningsCount / pageSize);

    // Reset to page 1 when switching to list view or changing filters
    useEffect(() => {
        if (viewMode !== 'list') {
            setCurrentPage(1);
        }
    }, [viewMode]);

    // Reset to page 1 when showCompleted changes (affects result count)
    useEffect(() => {
        setCurrentPage(1);
    }, [showCompleted]);

    // Same for the participant filter — it now narrows the query itself, so a
    // page number from the unfiltered list would land past the end.
    useEffect(() => {
        setCurrentPage(1);
    }, [participantFilter]);

    // Search functions for async dropdowns - only fetch matching results server-side
    const searchPeople = useCallback(async (query: string): Promise<SelectOption[]> => {
        const results = await listPeople({ query, limit: 20 });
        return results.map(p => ({ id: p.id, name: p.name, subtitle: p.type }));
    }, []);

    const searchArtists = useCallback(async (query: string): Promise<SelectOption[]> => {
        const results = await listPeople({ type: 'artist', query, limit: 20 });
        return results.map(p => ({ id: p.id, name: p.name }));
    }, []);

    const searchUsers = useCallback(async (query: string): Promise<SelectOption[]> => {
        const results = await listUsers({ is_active: true, query, limit: 20 });
        return results.map(u => ({ id: u.id, name: u.name, subtitle: u.role }));
    }, []);

    const searchThings = useCallback(async (query: string): Promise<SelectOption[]> => {
        const results = await listThings({ query, limit: 20 });
        return results.map(t => ({ id: t.id, name: t.name, subtitle: t.category }));
    }, []);

    // Only fetch when dialog is open
    const { data: places = [] } = useQuery({
        queryKey: ['places'],
        queryFn: () => listPlaces(),
        enabled: dialogOpen,
    });

    // Programs for the filter dropdown above the list.
    const { data: programs = [] } = useQuery<Program[]>({
        queryKey: ['programs'],
        queryFn: () => listPrograms(),
    });

    // Fetch templates - needed for calendar view to show template names
    const { data: templates = [] } = useQuery({
        queryKey: ['happeningTemplates'],
        queryFn: () => listHappeningTemplates(),
    });

    // Fetch tasks for the currently editing happening (backlinks)
    const { data: happeningTasks = [] } = useQuery({
        queryKey: ['tasksForHappening', editingHappening?.id],
        queryFn: () => getTasksForHappening(editingHappening!.id),
        enabled: !!editingHappening,
    });

    // Fetch all tasks to get incomplete task counts for happenings
    const { data: allTasks = [] } = useQuery({
        queryKey: ['tasks', { forHappeningCounts: true }],
        queryFn: () => listTasks(),
    });

    // Create a map of happening_id -> { completed: number, total: number }
    const taskCountsByHappening = useMemo(() => {
        const map = new Map<string, { completed: number; total: number }>();
        allTasks.forEach(task => {
            if (task.related_happening_id) {
                const current = map.get(task.related_happening_id) || { completed: 0, total: 0 };
                current.total += 1;
                if (task.status === 'done') {
                    current.completed += 1;
                }
                map.set(task.related_happening_id, current);
            }
        });
        return map;
    }, [allTasks]);

    // Fetch participants for the currently editing happening
    const { data: happeningParticipants = [] } = useQuery({
        queryKey: ['happeningParticipants', editingHappening?.id],
        queryFn: () => listHappeningParticipants(editingHappening!.id),
        enabled: !!editingHappening,
    });

    // Get unique roles from participants for notification selection
    const uniqueParticipantRolesForNotification = useMemo(() => {
        return Array.from(new Set(happeningParticipants.map(p => p.role)));
    }, [happeningParticipants]);

    // Programmers across the whole account, used solely to populate the
    // participant filter dropdown (linked people / users).
    //
    // This query is keyed on nothing but the role, and that is now honest: it
    // does not depend on which happenings are on screen. The previous version
    // passed the visible happening IDs to listHappeningParticipantsBatch under
    // this same fixed key, so the cached answer belonged to whichever page
    // loaded first and never refetched for later pages, calendar ranges, or
    // program filters. It also built a multi-kilobyte `in.(...)` URL that
    // tripped CORS preflights and froze the UI. Row rendering no longer reads
    // this query at all — see participantsByHappening below.
    const { data: allProgrammers = [] } = useQuery({
        queryKey: ['participantsByRole', PROGRAMMER_ROTATION_ROLE],
        queryFn: () => listParticipantsByRole(PROGRAMMER_ROTATION_ROLE),
        staleTime: 5 * 60_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
    });

    // Extract unique people and users from the programmer participants
    const linkedPeople = useMemo(() => {
        const peopleMap = new Map<string, { id: string; name: string }>();
        allProgrammers.forEach(p => {
            if (p.person_id && p.person_name && !peopleMap.has(p.person_id)) {
                peopleMap.set(p.person_id, { id: p.person_id, name: p.person_name });
            }
        });
        return Array.from(peopleMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allProgrammers]);

    const linkedUsers = useMemo(() => {
        const usersMap = new Map<string, { id: string; name: string }>();
        allProgrammers.forEach(p => {
            if (p.user_id && p.user_name && !usersMap.has(p.user_id)) {
                usersMap.set(p.user_id, { id: p.user_id, name: p.user_name });
            }
        });
        return Array.from(usersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [allProgrammers]);

    // Staff directory for assignee dropdowns; only load when dialogs are open
    const { data: users = [] } = useQuery({
        queryKey: ['users', { is_active: true }],
        queryFn: () => listUsers({ is_active: true }),
        enabled: dialogOpen || !!editingHappening || !!editingTask,
    });

    // Fetch all people for series default participant selection
    const { data: allPeopleData } = useQuery({
        queryKey: ['people'],
        queryFn: () => listPeople({}),
        enabled: dialogOpen || !!editingHappening || !!editingTask,
    });
    // IMPORTANT: don't write `data: allPeople = []` directly. When the query
    // is disabled, data is undefined and the `= []` default produces a fresh
    // array reference on every render. With this array landing in a useEffect
    // dep below, the effect re-fired on every render, called
    // setProfileReminders([]) (also a fresh ref), forced another render, and
    // we got a 25 render/sec loop with HappeningsPage as the updater. Use the
    // module-level EMPTY_PEOPLE so the reference stays stable across renders.
    const allPeople = allPeopleData ?? EMPTY_PEOPLE;

    // Auto-populate profile reminders when artists are added (both new and editing)
    useEffect(() => {
        if (formState.selectedArtists.length === 0) {
            // Only clear if not editing (when editing, keep DB-loaded reminders for removed artists cleanup on save)
            if (!editingHappening && profileReminders.length > 0) {
                setProfileReminders([]);
            }
            return;
        }
        // For each artist with incomplete profile, add default reminder if not already present
        const defaultOffset = -120; // 5 days before
        const newProfileReminders: Array<{ id?: string; offset_hours: number; person_id: string }> = [];
        for (const artist of formState.selectedArtists) {
            if (!artist.person_id) continue;
            const person = allPeople.find(p => p.id === artist.person_id);
            if (!person) continue;
            const summary = summarizePerson(person);
            if (summary.missingFields.length === 0) continue;
            // Keep existing reminders (including DB-loaded ones with id), add default for new artists
            const existing = profileReminders.filter(r => r.person_id === artist.person_id);
            if (existing.length > 0) {
                newProfileReminders.push(...existing);
            } else {
                newProfileReminders.push({ offset_hours: defaultOffset, person_id: artist.person_id });
            }
        }
        // Only update if different to avoid loops
        const currentIds = profileReminders.map(r => `${r.person_id}:${r.offset_hours}`).sort().join(',');
        const newIds = newProfileReminders.map(r => `${r.person_id}:${r.offset_hours}`).sort().join(',');
        if (currentIds !== newIds) {
            setProfileReminders(newProfileReminders);
        }
    }, [editingHappening, formState.selectedArtists, allPeople]);

    // Handle deep-link from global search: open the happening's edit dialog
    // directly. Fetch it by id (rather than looking in the loaded list) so this
    // works for drafts and any happening not on the current filtered/paginated
    // page — otherwise the lookup misses and nothing opens.
    useEffect(() => {
        const state = location.state as { openEntityId?: string; entityType?: string } | null;
        if (state?.openEntityId && state?.entityType === 'happening' && !editingHappening) {
            const id = state.openEntityId;
            // Clear the navigation state up front so refresh / back doesn't reopen.
            navigate(location.pathname, { replace: true, state: null });
            getHappeningById(id).then((h) => {
                if (h) openEditFlow(h);
            });
        }
    }, [location.state, editingHappening, navigate, location.pathname]);

    // Handle opening happening from URL query parameter (e.g., from task modal "View happening" button)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const happeningId = searchParams.get('id');

        if (happeningId && happenings.length > 0 && !editingHappening) {
            const happening = happenings.find((h) => h.id === happeningId);
            if (happening) {
                setEditingHappening(happening);
                setDialogOpen(true);
                setDialogStep('form');
                // Clear the query parameter to avoid reopening on refresh
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location.search, happenings, editingHappening, navigate, location.pathname]);

    // happening_id -> participants, built from the happenings query itself.
    // listHappenings embeds every participant, so this map covers exactly the
    // happenings on screen and can never disagree with them. Reading it from a
    // second, separately cached query is what made programmer initials appear
    // only after a few refreshes.
    const participantsByHappening = useMemo(() => {
        const map = new Map<string, HappeningParticipantSummary[]>();
        happenings.forEach(h => {
            const participants = (h as HappeningWithArtists).participants;
            if (participants && participants.length > 0) {
                map.set(h.id, participants);
            }
        });
        return map;
    }, [happenings]);

    // Every role in use across the account, for the role filter dropdown.
    // Account-wide rather than page-wide: the filter now runs in the database,
    // so a role absent from the current page still has results to find.
    const { data: uniqueRoles = EMPTY_ROLES } = useQuery({
        queryKey: ['participantRoles'],
        queryFn: () => listParticipantRoles(),
        staleTime: 5 * 60_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
    });

    // Filter and sort happenings.
    //
    // The participant filter is NOT applied here — it runs in the database as
    // part of the happenings query (see participantQueryFilter). Filtering it
    // in the browser only ever saw the loaded page, so a programmer whose shows
    // fell outside it got an empty result and no way to reach them.
    const filteredHappenings = useMemo(() => {
        let filtered = happenings;

        // Apply search filter
        if (searchQuery.trim()) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (happening) =>
                    happening.title.toLowerCase().includes(lowerQuery) ||
                    happening.slug.toLowerCase().includes(lowerQuery) ||
                    happening.type.toLowerCase().includes(lowerQuery) ||
                    happening.status.toLowerCase().includes(lowerQuery) ||
                    happening.description?.toLowerCase().includes(lowerQuery)
            );
        }

        // Note: Sorting is now handled by the DAL on the server side for list view
        // For other views (calendar, kanban), sorting happens in their respective render functions
        return filtered;
    }, [happenings, searchQuery]);

    const createMutation = useMutation({
        mutationFn: (data: HappeningCreate) => createHappening(data),
        onSuccess: (newHappening) => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            // Notify website to revalidate
            notifyShowsUpdated(newHappening?.slug);
            // Only close if "create another" is not checked
            if (!createAnother) {
                closeDialog();
            } else {
                // Reset form for next happening
                setFormState(defaultFormStateWithOrgDefaults());
                setSelectedType('show');
                setCompletionReminders([]);
                setHappeningReminders([]);
                setProfileReminders([]);
                setEditingHappening(null);
            }
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: HappeningUpdate }) => updateHappening(id, patch),
        onSuccess: (updatedHappening) => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            // Notify website to revalidate
            notifyShowsUpdated(updatedHappening?.slug || editingHappening?.slug);
            // Don't close dialog here - let handleSubmit close it after reminders are saved
        },
    });

    const applyTemplateMutation = useMutation({
        mutationFn: async ({
            templateId,
            title,
            placeId,
            startTime,
            endTime,
            timezone,
            roleAssignments,
        }: {
            templateId: string;
            title: string;
            placeId: string | null;
            startTime: string;
            endTime: string | null;
            timezone: string | null;
            roleAssignments: RoleAssignment[];
        }) => {
            return applyTemplate(templateId, {
                title, // Will use template name as default if empty
                type: 'show', // Will be overridden by template
                start_time: startTime,
                end_time: endTime,
                timezone: timezone,
                metadata: {
                    place_id: placeId,
                },
            }, roleAssignments);
        },
        onSuccess: (newHappening) => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Notify website to revalidate
            notifyShowsUpdated(newHappening?.slug);
            closeDialog();
        },
    });

    const createTaskMutation = useMutation({
        mutationFn: (data: TaskCreate) => createTask(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['tasksForHappening', editingHappening?.id] });
            setEditingTask(null);
        },
    });

    const updateTaskMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: TaskUpdate }) => updateTask(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['tasksForHappening', editingHappening?.id] });
            setEditingTask(null);
        },
    });

    // Task delete (from nested task edit modal danger zone)
    const [taskDeleteDialogOpen, setTaskDeleteDialogOpen] = useState(false);
    const [taskDeleteDependencies, setTaskDeleteDependencies] = useState<DependencyCheckResult | null>(null);
    const [deletingTaskRef, setDeletingTaskRef] = useState<Task | null>(null);
    const deleteTaskMutation = useMutation({
        mutationFn: (id: string) => deleteTask(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['tasksForHappening', editingHappening?.id] });
            setTaskDeleteDialogOpen(false);
            setDeletingTaskRef(null);
            setTaskDeleteDependencies(null);
            setEditingTask(null);
        },
    });
    const handleTaskDeleteFromModal = async (task: Task) => {
        setDeletingTaskRef(task);
        const deps = await checkTaskDependencies(task.id);
        setTaskDeleteDependencies(deps);
        setTaskDeleteDialogOpen(true);
    };

    // Participant mutations
    const addParticipantMutation = useMutation({
        mutationFn: createHappeningParticipant,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happeningParticipants', editingHappening?.id] });
            // The list/calendar read programmer initials off the participants
            // embedded in the happenings query, so that has to refresh too.
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['participantsByRole'] });
            queryClient.invalidateQueries({ queryKey: ['participantRoles'] });
            // Also invalidate tasks since auto-assignment trigger may have fired
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['tasksForHappening', editingHappening?.id] });
        },
    });

    const removeParticipantMutation = useMutation({
        mutationFn: deleteHappeningParticipant,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happeningParticipants', editingHappening?.id] });
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['participantsByRole'] });
            queryClient.invalidateQueries({ queryKey: ['participantRoles'] });
        },
    });

    const updateParticipantMutation = useMutation({
        mutationFn: async ({ id, patch }: { id: string; patch: Partial<{ user_id: string | null; person_id: string | null }> }) => {
            return updateHappeningParticipant(id, patch);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happeningParticipants', editingHappening?.id] });
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['participantsByRole'] });
            queryClient.invalidateQueries({ queryKey: ['participantRoles'] });
            // Also invalidate tasks since auto-assignment may have fired on update
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['tasksForHappening', editingHappening?.id] });
        },
    });

    // Delete mutations
    const deleteMutation = useMutation({
        mutationFn: async ({ id, cascadeDeleteTaskIds, cascadeDeletePersonIds, deleteMedia, renumberFrom }: { id: string; cascadeDeleteTaskIds?: string[]; cascadeDeletePersonIds?: string[]; deleteMedia?: boolean; renumberFrom?: { seriesId: string; fromDate: string } }) => {
            await deleteHappening(id, { cascadeDeleteTaskIds, cascadeDeletePersonIds, deleteMedia });
            // Close the gap the delete just opened. Runs after the delete so the resequence sees
            // the series as it now is; a failure here must not undo the delete, so it only warns.
            if (renumberFrom) {
                try {
                    await renumberSeriesEpisodes({
                        seriesId: renumberFrom.seriesId,
                        fromDate: renumberFrom.fromDate,
                    });
                } catch (err) {
                    console.error('[HappeningsPage] Failed to renumber episodes after delete:', err);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Notify website to revalidate
            notifyShowsUpdated();
            setDeleteDialogOpen(false);
            setDeletingHappening(null);
            setDeleteDependencies(null);
            setDeleteImageSharing(null);
            setDeleteLaterEpisodeCount(0);
            // Close the edit dialog too, in case the danger zone triggered the delete
            closeDialog();
        },
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: (ids: string[]) => deleteHappenings(ids),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            // Notify website to revalidate
            notifyShowsUpdated();
            setBulkDeleteDialogOpen(false);
            setSelectedHappeningIds(new Set());
            setBulkDeleteDependencies(new Map());
        },
    });

    const bulkStatusMutation = useMutation({
        mutationFn: ({ ids, status }: { ids: string[]; status: HappeningStatus }) =>
            bulkUpdateHappeningStatus(ids, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
            notifyShowsUpdated();
            setBulkStatusDialogOpen(false);
            setSelectedHappeningIds(new Set());
        },
    });

    // Delete handlers
    const [checkingSingleDependencies, setCheckingSingleDependencies] = useState<Set<string>>(new Set());
    const handleDeleteClick = async (happening: Happening, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        if (!canDelete) return;

        setCheckingSingleDependencies((prev) => new Set(prev).add(happening.id));
        try {
            setDeletingHappening(happening);
            const isNumberedInstance =
                !!happening.series_id &&
                !!happening.start_time &&
                typeof happening.metadata?.episode_number === 'number';
            const [dependencies, imageSharing, seriesInstances] = await Promise.all([
                checkHappeningDependencies(happening.id),
                getHappeningImageSharing(happening.id),
                isNumberedInstance ? getSeriesInstances(happening.series_id as string) : Promise.resolve([]),
            ]);
            setDeleteDependencies(dependencies);
            setDeleteImageSharing(imageSharing);
            setDeleteLaterEpisodeCount(
                seriesInstances.filter(
                    (h) =>
                        h.id !== happening.id &&
                        !!h.start_time &&
                        new Date(h.start_time) > new Date(happening.start_time as string) &&
                        typeof h.metadata?.episode_number === 'number',
                ).length,
            );
            setDeleteDialogOpen(true);
        } finally {
            setCheckingSingleDependencies((prev) => {
                const next = new Set(prev);
                next.delete(happening.id);
                return next;
            });
        }
    };

    const handleDeleteConfirm = (
        selected?: Record<string, string[]>,
        options?: { deleteImage?: boolean; renumberEpisodes?: boolean },
    ) => {
        if (deletingHappening) {
            const shouldRenumber =
                options?.renumberEpisodes &&
                deleteLaterEpisodeCount > 0 &&
                !!deletingHappening.series_id &&
                !!deletingHappening.start_time;
            deleteMutation.mutate({
                id: deletingHappening.id,
                cascadeDeleteTaskIds: selected?.tasks,
                cascadeDeletePersonIds: selected?.participants,
                deleteMedia: options?.deleteImage,
                renumberFrom: shouldRenumber
                    ? {
                          seriesId: deletingHappening.series_id as string,
                          // The deleted episode's slot: the next instance takes its number.
                          fromDate: deletingHappening.start_time as string,
                      }
                    : undefined,
            });
        }
    };

    const handleBulkDeleteClick = async () => {
        if (selectedHappeningIds.size === 0 || !canDelete) return;

        setCheckingBulkDependencies(true);
        try {
            const ids = Array.from(selectedHappeningIds);
            const dependencies = await checkBulkDependencies('happening', ids);
            setBulkDeleteDependencies(dependencies);
            setBulkDeleteDialogOpen(true);
        } finally {
            setCheckingBulkDependencies(false);
        }
    };

    const handleBulkDeleteConfirm = () => {
        if (selectedHappeningIds.size > 0) {
            bulkDeleteMutation.mutate(Array.from(selectedHappeningIds));
        }
    };

    const toggleHappeningSelection = (happeningId: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setSelectedHappeningIds((prev) => {
            const next = new Set(prev);
            if (next.has(happeningId)) {
                next.delete(happeningId);
            } else {
                next.add(happeningId);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedHappeningIds.size === filteredHappenings.length) {
            setSelectedHappeningIds(new Set());
        } else {
            setSelectedHappeningIds(new Set(filteredHappenings.map((h) => h.id)));
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending || applyTemplateMutation.isPending;
    const mutationError = (createMutation.error ?? updateMutation.error ?? applyTemplateMutation.error) as Error | null;
    const photoIdentifier = editingHappening?.slug || formState.slug || formState.title;

    // Async search for artists (server-side); no bulk preload
    const [artistResults, setArtistResults] = useState<Person[]>([]);
    const [isSearchingArtists, setIsSearchingArtists] = useState(false);
    useEffect(() => {
        const q = artistSearch.trim();
        if (q.length < 2) {
            setArtistResults([]);
            return;
        }
        setIsSearchingArtists(true);
        const timer = setTimeout(async () => {
            try {
                const results = await listPeople({ type: 'artist', query: q, limit: 10 });
                setArtistResults(results);
            } finally {
                setIsSearchingArtists(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [artistSearch]);

    // Search genres as user types
    useEffect(() => {
        if (!formState.genreInput.trim()) {
            setGenreSuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            const results = await searchGenres(formState.genreInput);
            setGenreSuggestions(results);
        }, 200);
        return () => clearTimeout(timer);
    }, [formState.genreInput]);

    // Intelligently prefill episode number for resident shows
    useEffect(() => {
        // Only run when creating a new show (not editing)
        if (editingHappening) return;
        // Don't update if slug was manually edited
        if (slugManuallyEdited) return;

        if (selectedType === 'show' && formState.showType === 'resident' && formState.title.trim()) {
            // Find existing shows with the same title
            const existingShows = happenings.filter(
                (h) =>
                    h.type === 'show' &&
                    h.metadata?.show_type === 'resident' &&
                    h.title.trim().toLowerCase() === formState.title.trim().toLowerCase()
            );

            let nextEpisode: number;
            if (existingShows.length > 0) {
                // Find the highest episode number
                const maxEpisode = existingShows.reduce((max, show) => {
                    const episode = (show.metadata?.episode_number as number) || 0;
                    return Math.max(max, episode);
                }, 0);
                // Set to next episode number
                nextEpisode = maxEpisode + 1;
            } else {
                // No existing shows with this title, reset to episode 1
                nextEpisode = 1;
            }

            // Update episode number and regenerate slug if needed
            if (formState.episodeNumber !== nextEpisode) {
                setFormState((prev) => {
                    const newSlug = generateSlugWithDateAndTime(prev.title, prev.startTime, nextEpisode);

                    return {
                        ...prev,
                        episodeNumber: nextEpisode,
                        slug: newSlug,
                    };
                });
            }
        }
    }, [formState.title, formState.showType, formState.startTime, selectedType, happenings, editingHappening, slugManuallyEdited]);

    // Check place availability when template wizard fields change
    useEffect(() => {
        if (!useTemplate || !templatePlaceId || !templateStartTime) {
            setAvailability(null);
            return;
        }

        // Convert datetime-local values to ISO strings
        const startIso = new Date(templateStartTime).toISOString();

        // Calculate end time from duration if not explicitly set
        let endIso = '';
        if (templateEndTime) {
            endIso = new Date(templateEndTime).toISOString();
        } else if (templateDuration && templateStartTime) {
            const start = new Date(templateStartTime);
            const end = new Date(start.getTime() + templateDuration * 60 * 1000);
            endIso = end.toISOString();
        }

        // Only check availability if we have both start and end times
        if (!endIso) {
            setAvailability(null);
            return;
        }

        const checkAvailability = async () => {
            setCheckingAvailability(true);
            try {
                const result = await checkPlaceAvailability(
                    templatePlaceId,
                    startIso,
                    endIso
                );
                setAvailability(result);
            } catch (error) {
                console.error('Failed to check availability:', error);
                setAvailability(null);
            } finally {
                setCheckingAvailability(false);
            }
        };

        const timer = setTimeout(checkAvailability, 300);
        return () => clearTimeout(timer);
    }, [useTemplate, templatePlaceId, templateStartTime, templateEndTime, templateDuration]);

    const startCreateFlow = () => {
        setEditingHappening(null);
        setSelectedType('show');
        setUseTemplate(false);
        setSelectedTemplateId(null);
        setTemplatePlaceId(null);
        setTemplateStartTime('');
        setTemplateEndTime('');
        setTemplateDuration(null);
        setUseEndTime(false);
        setTemplateTitle('');
        setTemplateTimezone(null);
        setTemplateRoleAssignments({});
        setAvailability(null);
        const initial = defaultFormStateWithOrgDefaults();
        setFormState(initial);
        creationInitialRef.current = JSON.parse(JSON.stringify(initial)); // Deep copy for comparison
        initialCompletionRemindersRef.current = [];
        initialHappeningRemindersRef.current = [];
        setSlugManuallyEdited(false);
        setDialogStep('type');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setTimeout(() => {
            setDialogStep('type');
            setEditingHappening(null);
            setUseTemplate(false);
            setSelectedTemplateId(null);
            setTemplatePlaceId(null);
            setTemplateStartTime('');
            setTemplateEndTime('');
            setTemplateDuration(null);
            setUseEndTime(false);
            setTemplateTitle('');
            setTemplateTimezone(null);
            setTemplateRoleAssignments({});
            setSkipRequiredRoles(false);
            setAvailability(null);
            setFormState(defaultFormStateWithOrgDefaults());
            setHappeningRoleReqs([]);
            setSlugManuallyEdited(false);
            setArtistSearch('');
            setShowArtistDropdown(false);
            setShowCardGenerator(false);
            setCardsError(null);
            creationInitialRef.current = null;
            setCompletionReminders([]);
            initialCompletionRemindersRef.current = [];
            setHappeningReminders([]);
            initialHappeningRemindersRef.current = [];
            setProfileReminders([]);
            initialProfileRemindersRef.current = [];
            setCreateAnother(false);
            // Reset recurrence state
            setCreationMode('single');
            setRecurrencePattern(null);
            setRecurrenceStartDate('');
            setRecurrenceEndDate('');
            setRecurrenceStartTime('19:00');
            setSeriesName('');
            setSeriesDefaultTitle('');
            setSeriesDefaultDescription('');
            setSeriesDefaultShowType('resident');
            setSeriesStartingEpisode(1);
            setSeriesDefaultArtists([]);
            setSeriesDefaultGenres([]);
            setSeriesDefaultParticipants([]);
            setSeriesProgrammerPool([]);
            setSeriesProgrammerOffset(0);
            setSeriesAllowBulkEdits(false);
        }, 200);

        // Navigate back if we were opened from another page (e.g. program detail).
        if (returnTo) {
            const target = returnTo;
            setReturnTo(null);
            setProgramIdForCreation(null);
            navigate(target);
        }
    };

    // Read URL params on mount to support deep-link create/edit from the program detail page:
    //   ?createInProgram=<id>&place=<id>&start=<iso>&returnTo=<path>
    //   ?edit=<happeningId>&returnTo=<path>
    useEffect(() => {
        if (consumedUrlParamsRef.current) return;
        const params = new URLSearchParams(location.search);
        const editId = params.get('edit');
        const createInProgram = params.get('createInProgram');
        const placeParam = params.get('place');
        const startParam = params.get('start');
        const endParam = params.get('end');
        const returnToParam = params.get('returnTo');
        if (!editId && !createInProgram) return;
        consumedUrlParamsRef.current = true;

        if (returnToParam) setReturnTo(returnToParam);

        if (editId) {
            getHappeningById(editId).then((h) => {
                if (h) openEditFlow(h);
            });
        } else if (createInProgram) {
            setProgramIdForCreation(createInProgram);
            // Fetch the program so we can seed the happening type from its default.
            getProgram(createInProgram).then((prog) => {
                startCreateFlow();
                setDialogStep('form');
                if (prog?.default_happening_type) {
                    setSelectedType(prog.default_happening_type as HappeningType);
                }
                // If dragged, derive duration + end from the start/end range.
                const dragDuration =
                    startParam && endParam
                        ? Math.max(15, Math.round((Date.parse(endParam) - Date.parse(startParam)) / 60000))
                        : null;
                setFormState((prev) => ({
                    ...prev,
                    placeId: placeParam ?? prev.placeId,
                    startTime: startParam
                        ? utcToLocalDatetimeLocal(startParam, orgTimezone || 'UTC')
                        : prev.startTime,
                    endTime: endParam
                        ? utcToLocalDatetimeLocal(endParam, orgTimezone || 'UTC')
                        : prev.endTime,
                    ...(dragDuration ? { durationMinutes: dragDuration } : {}),
                }));
            });
        }

        // Clean the URL so refresh / back doesn't re-trigger.
        navigate({ pathname: location.pathname }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTypeContinue = () => {
        if (useTemplate && creationMode !== 'single') {
            // Template + series/batch mode: go to recurrence configuration
            setDialogStep('recurrence');
        } else if (useTemplate) {
            // Template + single mode: go to template wizard
            setDialogStep('template');
        } else if (creationMode !== 'single') {
            // No template, series or batch mode: go to recurrence configuration
            setDialogStep('recurrence');
        } else {
            // No template, single mode: go to form
            setDialogStep('form');
        }
    };

    const handleTemplateSubmit = () => {
        if (!selectedTemplateId || !templateStartTime) return;

        // Determine effective timezone
        const effectiveTimezone = templateTimezone || orgTimezone;

        // Convert local datetime to UTC using the selected timezone
        const startTimeUtc = localDatetimeLocalToUtc(templateStartTime, effectiveTimezone);

        // Calculate end time from duration if using duration mode
        let endTimeUtc: string | null = null;
        if (!useEndTime && templateDuration && templateStartTime) {
            const start = new Date(startTimeUtc);
            const end = new Date(start.getTime() + templateDuration * 60 * 1000);
            endTimeUtc = end.toISOString();
        } else if (templateEndTime) {
            endTimeUtc = localDatetimeLocalToUtc(templateEndTime, effectiveTimezone);
        }

        // Flatten role assignments to array of {role, person_id or user_id}
        const roleAssignments: RoleAssignment[] = [];
        for (const [role, assignments] of Object.entries(templateRoleAssignments)) {
            for (const assignment of assignments) {
                if (assignment.source === 'users') {
                    roleAssignments.push({ role, user_id: assignment.id });
                } else {
                    roleAssignments.push({ role, person_id: assignment.id });
                }
            }
        }

        applyTemplateMutation.mutate({
            templateId: selectedTemplateId,
            title: templateTitle,
            placeId: templatePlaceId,
            startTime: startTimeUtc,
            endTime: endTimeUtc,
            timezone: templateTimezone,
            roleAssignments,
        });
    };

    const attemptCloseDialog = () => {
        if (isHappeningDirty) {
            setShowUnsavedConfirm(true);
            return;
        }
        closeDialog();
        creationInitialRef.current = null;
    };

    // Get currently selected template
    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

    const handleFieldChange =
        (field: keyof ShowFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const { value } = event.target;
            setFormState((prev) => {
                if (field === 'title' && !slugManuallyEdited) {
                    const isResident = selectedType === 'show' && prev.showType === 'resident';
                    if (isResident) {
                        // For resident shows, generate slug with title + episode + date + time
                        const newSlug = value ? generateSlugWithDateAndTime(value, prev.startTime, prev.episodeNumber) : '';
                        return {
                            ...prev,
                            title: value,
                            slug: newSlug,
                        };
                    } else {
                        // For guest shows, slug will be generated on backend
                        return {
                            ...prev,
                            title: value,
                            slug: '',
                        };
                    }
                }
                if (field === 'slug') {
                    setSlugManuallyEdited(true);
                    return { ...prev, slug: value.replace(SLUG_REGEX, '') };
                }
                if (field === 'episodeNumber' && !slugManuallyEdited) {
                    const numValue = parseInt(value) || 1;
                    const isResident = selectedType === 'show' && prev.showType === 'resident';
                    if (isResident && prev.title) {
                        // Update slug with new episode number
                        const newSlug = generateSlugWithDateAndTime(prev.title, prev.startTime, numValue);
                        return { ...prev, episodeNumber: numValue, slug: newSlug };
                    }
                    return { ...prev, episodeNumber: numValue };
                }
                if (field === 'startTime' && !slugManuallyEdited) {
                    const isResident = selectedType === 'show' && prev.showType === 'resident';
                    if (isResident && prev.title) {
                        // Update slug with new date and time
                        const newSlug = generateSlugWithDateAndTime(prev.title, value, prev.episodeNumber);
                        return { ...prev, startTime: value, slug: newSlug };
                    }
                    return { ...prev, startTime: value };
                }
                return { ...prev, [field]: value };
            });
        };

    const handleCheckboxChange = (field: keyof ShowFormState) => (event: ChangeEvent<HTMLInputElement>) => {
        setFormState((prev) => ({ ...prev, [field]: event.target.checked }));
    };

    const handleAddArtist = async (person: Person) => {
        const alreadyAdded = formState.selectedArtists.some((a) => a.person_id === person.id);
        if (alreadyAdded) return;

        // Auto-publish artist when adding to a public show
        if (formState.isPublic && !person.is_public) {
            try {
                await updatePerson(person.id, { is_public: true });
                queryClient.invalidateQueries({ queryKey: ['people'] });
            } catch (err) {
                console.error('Failed to auto-publish artist:', err);
            }
        }

        const artist: HappeningArtist = {
            person_id: person.id,
            name: person.name,
            slug: person.slug,
            picture_url: person.picture_url,
        };
        setFormState((prev) => ({
            ...prev,
            selectedArtists: [...prev.selectedArtists, artist],
        }));
        setArtistSearch('');
        setShowArtistDropdown(false);
        setCreatingNewArtist(false);
        setNewArtistEmail('');
    };

    const handleRemoveArtist = (personId: string) => {
        setFormState((prev) => ({
            ...prev,
            selectedArtists: prev.selectedArtists.filter((a) => a.person_id !== personId),
        }));
    };

    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    // Stable list of sortable artist IDs. Passing a fresh
    // formState.selectedArtists.map(...) into <SortableContext items={...}>
    // makes dnd-kit think the items list changed every render, which fans out
    // to useSyncExternalStoreWithSelector on every useSortable subscriber and
    // produces a render storm that was 50%+ of CPU in production traces.
    const sortableArtistIds = useMemo(
        () => formState.selectedArtists.map((a) => a.person_id),
        [formState.selectedArtists],
    );

    // useCallback so DndContext receives a stable onDragEnd prop reference
    // and doesn't tear down + rebuild its internal subscribers each render.
    const handleReorderArtists = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setFormState(prev => {
            const oldIndex = prev.selectedArtists.findIndex(a => a.person_id === active.id);
            const newIndex = prev.selectedArtists.findIndex(a => a.person_id === over.id);
            return { ...prev, selectedArtists: arrayMove(prev.selectedArtists, oldIndex, newIndex) };
        });
    }, []);

    const handleUseArtistPhoto = (pictureUrl: string) => {
        setFormState((prev) => ({ ...prev, imageUrl: pictureUrl }));
    };

    const handleAddGenre = (genre: string) => {
        const trimmed = genre.trim();
        if (!trimmed || formState.genres.length >= 4) return;
        if (formState.genres.some((g) => g.toLowerCase() === trimmed.toLowerCase())) return;

        setFormState((prev) => ({
            ...prev,
            genres: [...prev.genres, trimmed],
            genreInput: '',
        }));
        setShowGenreDropdown(false);
    };

    const handleRemoveGenre = (genre: string) => {
        setFormState((prev) => ({
            ...prev,
            genres: prev.genres.filter((g) => g !== genre),
        }));
    };

    const handleGenreInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            handleAddGenre(formState.genreInput);
        }
    };

    const handleAddGearRequest = (thing: Thing) => {
        const alreadyAdded = formState.gearRequests.some((g) => g.thing_id === thing.id);
        if (alreadyAdded) return;

        const gearRequest: GearRequest = {
            thing_id: thing.id,
            name: thing.name,
            quantity: 1,
        };
        setFormState((prev) => ({
            ...prev,
            gearRequests: [...prev.gearRequests, gearRequest],
        }));
    };

    // Add gear request from async select option (without preloading all things)
    const handleAddGearRequestOption = (option: SelectOption) => {
        const alreadyAdded = formState.gearRequests.some((g) => g.thing_id === option.id);
        if (alreadyAdded) return;
        setFormState((prev) => ({
            ...prev,
            gearRequests: [...prev.gearRequests, { thing_id: option.id, name: option.name, quantity: 1 }],
        }));
    };

    const handleRemoveGearRequest = (thingId: string) => {
        setFormState((prev) => ({
            ...prev,
            gearRequests: prev.gearRequests.filter((g) => g.thing_id !== thingId),
        }));
    };

    const checkAndPromptPublishArtists = (wasPublicBefore: boolean, isPublicNow: boolean) => {
        if (!isPublicNow || wasPublicBefore) return;
        const privateArtists = formState.selectedArtists
            .map(a => allPeople.find(p => p.id === a.person_id))
            .filter((p): p is Person => !!p && !p.is_public);
        if (privateArtists.length > 0) {
            setPrivateArtistsToPublish(privateArtists);
            setShowMakeArtistsPublicDialog(true);
        }
    };

    const handlePublishArtists = async () => {
        try {
            await Promise.all(privateArtistsToPublish.map(p => updatePerson(p.id, { is_public: true })));
            queryClient.invalidateQueries({ queryKey: ['people'] });
        } catch (err) {
            console.error('Failed to publish artists:', err);
        } finally {
            setShowMakeArtistsPublicDialog(false);
            setPrivateArtistsToPublish([]);
        }
    };

    // Does the pending save move this instance's episode number? Drives the extra explanation in
    // the apply-to-series dialog, since "future" means resequence rather than copy.
    const pendingEpisodeRenumber = useMemo(() => {
        const to = pendingPayload?.metadata?.episode_number;
        const from = editingHappening?.metadata?.episode_number;
        if (typeof to !== 'number' || to === from) return null;
        return { from: typeof from === 'number' ? from : null, to };
    }, [pendingPayload, editingHappening]);

    const saveHappening = (payload: HappeningCreate, applyToFuture: boolean) => {
        if (!editingHappening) return;

        updateMutation.mutate(
            { id: editingHappening.id, patch: payload },
            {
                onSuccess: async (updatedHappening) => {
                    // Invalidate queries to refresh the list
                    queryClient.invalidateQueries({ queryKey: ['happenings'] });

                    // Sync artists to happening_participants table for normalized storage
                    await syncHappeningArtists(
                        editingHappening.id,
                        formState.selectedArtists.map(a => ({ person_id: a.person_id }))
                    );

                    // Persist staffing role requirements (instance-specific; not propagated to the series)
                    await setHappeningRoleRequirements(editingHappening.id, happeningRoleReqs);
                    queryClient.invalidateQueries({ queryKey: ['happening-role-requirements', editingHappening.id] });

                    // Persist tags. Done here rather than inside updateMutation because the
                    // kanban board reuses that mutation for a bare status change and must not
                    // clear a show's tags as a side effect of being dragged.
                    await setHappeningTags(editingHappening.id, formState.tagIds);

                    // Check if show just went public and has private artists
                    checkAndPromptPublishArtists(!!editingHappening.is_public, !!payload.is_public);

                    // If applying to future, propagate only shareable fields to future instances
                    if (applyToFuture && editingHappening.series_id && editingHappening.start_time) {
                        setApplyToFutureLoading(true);
                        try {
                            // Only propagate fields that make sense across instances —
                            // exclude instance-specific fields like start_time, end_time, slug, place_id, timezone, type
                            const propagatePatch: HappeningUpdate = {};
                            if (payload.description !== undefined) propagatePatch.description = payload.description;
                            if (payload.image_url !== undefined) propagatePatch.image_url = payload.image_url;
                            if (payload.title !== undefined) propagatePatch.title = payload.title;
                            if (payload.status !== undefined) propagatePatch.status = payload.status;
                            if (payload.is_public !== undefined) propagatePatch.is_public = payload.is_public;
                            // Only propagate series-relevant metadata (genres, show_type, artists, gear_requests)
                            // Exclude instance-specific metadata (episode_number, template_id, archive_links, is_auto_generated_title)
                            if (payload.metadata) {
                                propagatePatch.metadata = {};
                                if (payload.metadata.genres) propagatePatch.metadata.genres = payload.metadata.genres;
                                if (payload.metadata.show_type) propagatePatch.metadata.show_type = payload.metadata.show_type;
                                if (payload.metadata.artists) propagatePatch.metadata.artists = payload.metadata.artists;
                                if (payload.metadata.gear_requests) propagatePatch.metadata.gear_requests = payload.metadata.gear_requests;
                                if (payload.metadata.duration_minutes) propagatePatch.metadata.duration_minutes = payload.metadata.duration_minutes;
                            }

                            await applyChangesToFutureInstances({
                                seriesId: editingHappening.series_id,
                                patch: propagatePatch,
                                fromDate: editingHappening.start_time,
                                artistPersonIds: formState.selectedArtists.map(a => a.person_id),
                            });

                            // Episode numbers are deliberately NOT in propagatePatch — copying one
                            // number onto every future episode would be nonsense. "Apply to future"
                            // for a renumbered episode means "continue the sequence from here", so
                            // resequence instead: this episode keeps its new number and each later
                            // one follows on. Without this the edit silently only moved this show.
                            const newEpisodeNumber = payload.metadata?.episode_number;
                            if (
                                typeof newEpisodeNumber === 'number' &&
                                newEpisodeNumber !== editingHappening.metadata?.episode_number
                            ) {
                                await renumberSeriesEpisodes({
                                    seriesId: editingHappening.series_id,
                                    fromDate: editingHappening.start_time,
                                    startAt: newEpisodeNumber,
                                });
                            }
                        } finally {
                            setApplyToFutureLoading(false);
                        }
                    }

                    // Send invite emails based on selected strategy
                    if (updatedHappening) {
                        await handleSendInviteEmails(updatedHappening);
                    }

                    // Save completion reminders if artists are present
                    await handleSaveCompletionReminders(editingHappening.id, formState.selectedArtists, updatedHappening?.start_time ?? editingHappening.start_time);

                    // Save happening reminders if artists are present
                    await handleSaveHappeningReminders(editingHappening.id, formState.selectedArtists, updatedHappening?.start_time ?? editingHappening.start_time);

                    // Save post-show reminders if artists are present
                    await handleSavePostShowReminders(editingHappening.id, formState.selectedArtists, updatedHappening?.start_time ?? editingHappening.start_time);

                    // Save profile reminders for artists with incomplete profiles
                    // (but not if using combined strategy, which handles profile reminders itself)
                    if (formState.inviteEmailStrategy !== 'combined') {
                        await handleSaveProfileReminders(editingHappening.id, formState.selectedArtists, updatedHappening?.start_time ?? editingHappening.start_time);
                    }

                    // Save notification roles
                    if (updatedHappening?.account_id) {
                        await handleSaveNotificationRoles(editingHappening.id, updatedHappening.account_id);
                    }

                    // Save post-show feedback URL in happening metadata if changed
                    if (postShowFeedbackUrl) {
                        const currentMetadata = (updatedHappening?.metadata || {}) as Record<string, unknown>;
                        if (currentMetadata.post_show_feedback_url !== postShowFeedbackUrl) {
                            await updateHappening(editingHappening.id, {
                                metadata: { ...currentMetadata, post_show_feedback_url: postShowFeedbackUrl },
                            });
                        }
                    }

                    // Announcement cards, if the generator is open. Awaited so the cards
                    // are stored before the dialog closes; a failure keeps the dialog up
                    // with the error rather than losing the crops silently.
                    setCardsError(null);
                    const cardsResult = await cardsPanelRef.current?.saveIfNeeded();
                    if (cardsResult?.error) {
                        setCardsError(cardsResult.error);
                        setShowApplyToSeriesDialog(false);
                        setPendingPayload(null);
                        return;
                    }

                    // Close dialog after all operations complete
                    setShowApplyToSeriesDialog(false);
                    setPendingPayload(null);
                    closeDialog();
                },
            }
        );
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const isShow = selectedType === 'show';
        const isResidentShow = isShow && formState.showType === 'resident';
        // Types whose end_time is derived from start + duration_minutes (no manual end-time field in the UI).
        const derivesEndFromDuration = selectedType === 'show' || selectedType === 'performance' || selectedType === 'tech';
        // Preserve template_id from existing happening if editing
        const existingTemplateId = editingHappening?.metadata?.template_id as string | undefined;

        // Auto-generate title for guest shows without a title (MOVED TO TOP)
        let finalTitle = formState.title.trim();
        let isAutoGeneratedTitle = false;

        // Check if we're editing a happening that had an auto-generated title
        const existingWasAutoGenerated = editingHappening?.metadata?.is_auto_generated_title === true;
        const autoGeneratedTitleWouldBe = formState.selectedArtists.length > 0
            ? formatArtistNames(formState.selectedArtists)
            : '';

        console.log('[HappeningsPage] Title check:', {
            originalTitle: formState.title,
            trimmedTitle: finalTitle,
            isShow,
            showType: formState.showType,
            artistCount: formState.selectedArtists.length,
            existingWasAutoGenerated,
            autoGeneratedTitleWouldBe
        });

        if (!finalTitle && isShow && formState.showType === 'guest' && formState.selectedArtists.length > 0) {
            // No title provided, auto-generate it
            finalTitle = autoGeneratedTitleWouldBe;
            isAutoGeneratedTitle = true;
            console.log('[HappeningsPage] Auto-generated title:', finalTitle);
        } else if (existingWasAutoGenerated && finalTitle === autoGeneratedTitleWouldBe) {
            // Title matches what would be auto-generated and was previously auto-generated
            // Preserve the auto-generated flag
            isAutoGeneratedTitle = true;
            console.log('[HappeningsPage] Preserving auto-generated title flag (title unchanged)');
        }

        const metadata: any = {
            show_type: formState.showType,
            genres: formState.genres,
            artists: formState.selectedArtists,
            gear_requests: formState.gearRequests,
            rider_notes: formState.riderNotes.trim() || null,
            duration_minutes: formState.durationMinutes,
            archive_links: formState.archiveLinks,
            sets: formState.sets,
            tracklist: formState.tracklist.length > 0 ? formState.tracklist : undefined,
            place_id: formState.placeId || null,
            image_focus: formState.imageFocus !== 'center' ? formState.imageFocus : undefined,
            // Staff pick is a show-only display flag; write it explicitly (including false)
            // so unticking it clears the badge on the public site.
            is_staff_pick: isShow ? formState.isStaffPick : undefined,
        };

        // Preserve template_id if it exists (from template-based creation)
        if (existingTemplateId) {
            metadata.template_id = existingTemplateId;
        }

        // Add episode number for resident shows
        if (isResidentShow) {
            metadata.episode_number = formState.episodeNumber;
        }

        // Mark if title was auto-generated
        // If title is auto-generated, set flag to true
        // If title is manually provided, ensure flag is false/removed
        if (isAutoGeneratedTitle) {
            metadata.is_auto_generated_title = true;
            console.log('[HappeningsPage] Set is_auto_generated_title = true');
        } else if (isShow) {
            // Explicitly set to false when user provides a custom title
            metadata.is_auto_generated_title = false;
            console.log('[HappeningsPage] Set is_auto_generated_title = false');
        }
        console.log('[HappeningsPage] Final metadata:', JSON.stringify(metadata, null, 2));

        // Determine effective timezone: use happening timezone if set, otherwise use organization timezone
        const effectiveTimezone = formState.timezone || orgTimezone;

        // Convert datetime-local strings (in effective timezone) to UTC for backend
        const startTimeUtc = formState.startTime ? localDatetimeLocalToUtc(formState.startTime, effectiveTimezone) : null;

        // For types whose UI hides the end-time field, derive end_time from start + duration.
        let endTimeUtc: string | null = null;
        if (derivesEndFromDuration && startTimeUtc && formState.durationMinutes) {
            const start = new Date(startTimeUtc);
            const end = new Date(start.getTime() + formState.durationMinutes * 60 * 1000);
            endTimeUtc = end.toISOString();
        } else {
            endTimeUtc = formState.endTime ? localDatetimeLocalToUtc(formState.endTime, effectiveTimezone) : null;
        }

        const payload: HappeningCreate = {
            title: finalTitle || undefined,
            type: selectedType,
            slug: formState.slug.trim() || undefined,
            description: formState.description.trim() || null,
            image_url: formState.imageUrl.trim() || null,
            start_time: startTimeUtc,
            end_time: endTimeUtc,
            place_id: formState.placeId || null,
            timezone: formState.timezone || null, // Save timezone override (null means use org default)
            status: formState.status,
            is_public: formState.isPublic,
            metadata,
            /*
             * Choosing to email implies an artist-facing link — they cannot be separate
             * decisions, because a strategy without a link silently emails nobody. Only mint
             * when the show has none yet; regenerating would invalidate links already sent.
             */
            generateStubLink:
                formState.generateStubLink ||
                (formState.inviteEmailStrategy !== 'none' && !editingHappening?.metadata?.stub_token),
        };

        // When creating a new happening from a program context, stamp the program_id.
        // For edits we leave program_id alone so the existing assignment is preserved.
        if (!editingHappening && programIdForCreation) {
            (payload as HappeningCreate & { program_id?: string | null }).program_id = programIdForCreation;
        }

        /*
         * Everything past here is the actual save. It is split out so the invite
         * confirmation can sit in front of it: the dialog stashes this payload, asks the
         * one question, and then resumes exactly here with the answer.
         */
        if (inviteDecisionNeeded) {
            setPendingPayload(payload);
            setShowInviteConfirmDialog(true);
            return;
        }

        proceedWithSave(payload);
    };

    /**
     * Whether saving should stop and ask about the invite email.
     *
     * Asked once per show, while there are artists to invite and nothing has been emailed
     * yet. Forgetting to send the invite was the common failure — the strategy and the
     * artist-facing link used to be two separate controls with a silent dependency between
     * them, so a show could look configured and quietly email nobody. Making the send an
     * explicit yes/no removes both that trap and the opposite one, emailing by accident.
     */
    const inviteDecisionNeeded =
        inviteDecisionRef.current === null &&
        formState.selectedArtists.length > 0 &&
        combinedInviteCandidates.length > 0 &&
        !showHasBeenInvited &&
        !editingHappening?.metadata?.invite_email_strategy;

    /** Apply the answer and carry on with the save that was interrupted. */
    const resolveInviteDecision = (strategy: ShowFormState['inviteEmailStrategy']) => {
        inviteDecisionRef.current = strategy;
        setFormState(prev => ({ ...prev, inviteEmailStrategy: strategy }));
        setShowInviteConfirmDialog(false);

        const payload = pendingPayload;
        setPendingPayload(null);
        if (!payload) return;

        /*
         * Both answers need the artist-facing link: emailing sends it, and "I'll send it
         * myself" is a statement about *how* the link travels, not whether it exists. Minting
         * it here means the copy button is instant either way and there is no checkbox left to
         * forget. Never when the show already has a token — regenerating would invalidate
         * links already sitting in someone's inbox.
         */
        const needsLink = !editingHappening?.metadata?.stub_token;
        proceedWithSave(
            needsLink ? { ...payload, generateStubLink: true } : payload,
        );
    };

    /** The save itself, resumable once the invite question (if any) has been answered. */
    const proceedWithSave = (payload: HappeningCreate) => {
        if (editingHappening) {
            // Series instances only get the apply-to-series prompt when the series opted into
            // bulk edits. Otherwise the save is unambiguous: it applies to this show alone.
            if (editingHappening.series_id && seriesAllowsBulkEdits) {
                setPendingPayload(payload);
                setShowApplyToSeriesDialog(true);
                return;
            }

            saveHappening(payload, false);
            return;
        }

        // For new happenings, we'll create reminders after the happening is created
        createMutation.mutate(payload, {
            onSuccess: async (newHappening) => {
                // Sync artists to happening_participants table for normalized storage
                if (formState.selectedArtists.length > 0) {
                    await syncHappeningArtists(
                        newHappening.id,
                        formState.selectedArtists.map(a => ({ person_id: a.person_id }))
                    );
                }

                // Persist tags chosen while creating the show
                if (formState.tagIds.length > 0) {
                    await setHappeningTags(newHappening.id, formState.tagIds);
                }

                // Persist participants staged during creation
                if (formState.stagedParticipants.length > 0) {
                    await Promise.all(
                        formState.stagedParticipants
                            .filter((p) => p.assignee_id && p.role.trim())
                            .map((p) =>
                                createHappeningParticipant({
                                    happening_id: newHappening.id,
                                    role: p.role.trim(),
                                    person_id: p.source === 'people' ? p.assignee_id : null,
                                    user_id: p.source === 'users' ? p.assignee_id : null,
                                }),
                            ),
                    );
                }

                // Persist staffing role requirements
                await setHappeningRoleRequirements(newHappening.id, happeningRoleReqs);
                queryClient.invalidateQueries({ queryKey: ['happening-role-requirements', newHappening.id] });

                // Check if new public show has private artists
                checkAndPromptPublishArtists(false, !!payload.is_public);

                // Send invite emails based on selected strategy
                await handleSendInviteEmails(newHappening);

                // Create completion reminders if artists are present
                if (formState.selectedArtists.length > 0) {
                    await handleSaveCompletionReminders(newHappening.id, formState.selectedArtists, newHappening.start_time);
                    await handleSaveHappeningReminders(newHappening.id, formState.selectedArtists, newHappening.start_time);

                    // Save post-show reminders
                    await handleSavePostShowReminders(newHappening.id, formState.selectedArtists, newHappening.start_time);

                    // Only save profile reminders if not using combined strategy
                    // (combined strategy already handles profile reminders)
                    if (formState.inviteEmailStrategy !== 'combined') {
                        await handleSaveProfileReminders(newHappening.id, formState.selectedArtists, newHappening.start_time);
                    }
                }

                // Save post-show feedback URL in happening metadata if set
                if (postShowFeedbackUrl && newHappening) {
                    const currentMetadata = (newHappening.metadata || {}) as Record<string, unknown>;
                    await updateHappening(newHappening.id, {
                        metadata: { ...currentMetadata, post_show_feedback_url: postShowFeedbackUrl },
                    });
                }

                // Save notification roles
                if (newHappening?.account_id) {
                    await handleSaveNotificationRoles(newHappening.id, newHappening.account_id);
                }
            },
        });
    };

    const handleSendInviteEmails = async (happening: Happening) => {
        /*
         * The confirmation dialog's answer wins over form state: it is set in a ref moments
         * before the save runs, so reading form state here could still see the old value and
         * email the wrong way (or not at all).
         */
        const strategy = inviteDecisionRef.current ?? formState.inviteEmailStrategy;
        /*
         * Record the choice on the show itself. Two reasons: reopening the edit dialog can
         * then restore what was picked instead of resetting to "don't email", and the
         * combined-vs-separate mix becomes answerable with one SQL query rather than an
         * analytics vendor. Best-effort — a failed write must never block the save or the
         * emails, so it is deliberately not awaited into the happy path.
         */
        updateHappening(happening.id, {
            metadata: {
                invite_email_strategy: strategy,
                invite_email_strategy_set_at: new Date().toISOString(),
            },
        }).catch((error) => console.error('Failed to record invite strategy', error));

        // Check if stub link exists or is being generated
        const hasOrWillHaveStubLink = formState.generateStubLink || happening.metadata?.stub_token;

        if (!hasOrWillHaveStubLink || formState.selectedArtists.length === 0) {
            return;
        }

        // Skip if strategy is 'none'
        if (strategy === 'none') {
            return;
        }

        /*
         * The invite goes out once per show. The strategy is persisted on the show and
         * restored when the dialog reopens, so without this every later save — a typo fix,
         * a new genre — deleted the sent combined invite, queued a fresh one, and emailed
         * the artist again. Re-inviting is the explicit "Email invite" button (EmailInviteButton).
         */
        if (await inviteAlreadyQueued(happening.id, strategy)) {
            return;
        }

        if (strategy === 'combined' && formState.combinedInviteArtistId) {
            // Send combined invite to selected artist
            const selectedArtist = formState.selectedArtists.find(a => a.person_id === formState.combinedInviteArtistId);
            if (!selectedArtist || !selectedArtist.person_id) {
                console.warn('Selected artist for combined invite not found');
                return;
            }

            const person = await getPerson(selectedArtist.person_id);
            if (!person || !person.email) {
                console.warn(`Person ${selectedArtist.person_id} has no email, cannot send combined invite`);
                return;
            }

            // Create combined reminder
            await createCompletionReminder({
                reminder_type: 'combined_artist_show',
                entity_id: person.id, // person_id
                happening_id: happening.id,
                recipient_email: person.email,
                recipient_name: person.name || selectedArtist.name || selectedArtist.slug || person.email || 'Artist',
                offset_hours: 0, // immediate
            });

            console.log(`Created combined invite reminder for ${person.name}`);

            // Profile-only reminders for the rest of the bill. Unlike the candidate list
            // above, this stays restricted to artists whose profile is genuinely unfinished
            // — there is no reason to email someone asking them to fill in nothing.
            for (const { artist, person: otherPerson } of artistsWithIncompleteProfiles) {
                if (artist.person_id === formState.combinedInviteArtistId) continue; // Skip the one getting combined invite

                if (!otherPerson.email) continue;

                await createCompletionReminder({
                    reminder_type: 'profile',
                    entity_id: otherPerson.id,
                    happening_id: happening.id,
                    recipient_email: otherPerson.email,
                    recipient_name: otherPerson.name || artist.name || artist.slug || otherPerson.email || 'Artist',
                    offset_hours: 0, // immediate
                });

                console.log(`Created profile-only reminder for ${otherPerson.name}`);
            }
        } else if (strategy === 'separate') {
            // Original behavior: send separate show stub and profile emails
            if (formState.selectedArtists.length > 0 && formState.selectedArtists[0].person_id) {
                await handleSendStubEmail(happening, formState.selectedArtists[0]);
            }
            await handleSaveProfileReminders(happening.id, formState.selectedArtists, happening.start_time);
        }
        // 'none' strategy: don't send any emails
    };

    /** Whether this show already has an invite of this kind queued, sent, or parked. */
    const inviteAlreadyQueued = async (happeningId: string, strategy: 'combined' | 'separate'): Promise<boolean> => {
        const combined = await getCombinedRemindersForHappening(happeningId);
        if (combined.length > 0) return true;
        if (strategy === 'combined') return false;
        // The separate strategy's show email is an immediate 'happening' row.
        const showEmails = await getCompletionReminders('happening', happeningId);
        return showEmails.some(r => r.offset_hours === 0);
    };

    /**
     * Tokens an emailed link needs, minted only where missing. Never regenerates an existing
     * one — that would break links already sitting in someone's inbox. The minted show is put
     * back into the dialog so a later save sees the token and does not mint a second one.
     */
    const ensureInviteTokens = async (
        personId: string,
        happening: Happening,
        needs: { profileLink: boolean; showLink: boolean },
    ): Promise<{ person: { id: string; name: string | null; email: string | null } | null; happening: Happening }> => {
        // getPerson returns the raw row (profile_completion_token), updatePerson the mapped
        // Person (invite_token) — keep only the fields both shapes agree on.
        const row = await getPerson(personId);
        if (needs.profileLink && row && !row.profile_completion_token) {
            await updatePerson(row.id, { generateInviteLink: true });
            queryClient.invalidateQueries({ queryKey: ['people'] });
        }
        const person = row ? { id: row.id, name: row.name ?? null, email: row.email ?? null } : null;
        let current = happening;
        if (needs.showLink && !current.metadata?.stub_token) {
            current = await updateHappening(current.id, { generateStubLink: true });
            setEditingHappening(current);
            queryClient.invalidateQueries({ queryKey: ['happenings'] });
        }
        return { person, happening: current };
    };

    /** Run a "send now" action with shared busy/error state, keyed so each button reports its own. */
    const runSendNow = async (key: string, action: () => Promise<void>) => {
        setSendingNowKey(key);
        setSendNowError(null);
        try {
            await action();
        } catch (error) {
            console.error(`Failed to queue "${key}" email:`, error);
            setSendNowError({ key, message: error instanceof Error ? error.message : 'Could not queue the email' });
        } finally {
            setSendingNowKey(null);
        }
    };

    /** Queue a show-details completion reminder for the next scheduler run. */
    const handleSendShowReminderNow = () => {
        if (!editingHappening) return;
        const artist = formState.selectedArtists.find(a => a.person_id);
        if (!artist?.person_id) return;
        return runSendNow('show', async () => {
            const { person, happening } = await ensureInviteTokens(artist.person_id!, editingHappening, { profileLink: false, showLink: true });
            if (!person?.email) throw new Error(`${person?.name ?? artist.name ?? 'This artist'} has no email address`);
            await queueImmediateCompletionReminder({
                reminder_type: 'happening',
                entity_id: happening.id,
                happening_id: happening.id,
                recipient_email: person.email,
                recipient_name: person.name || artist.name || person.email,
            });
            queryClient.invalidateQueries({ queryKey: ['completion-reminders', happening.id, 'happening'] });
        });
    };

    /** Queue a profile completion reminder for one artist on the next scheduler run. */
    const handleSendProfileReminderNow = (personId: string) => {
        if (!editingHappening) return;
        return runSendNow(personId, async () => {
            const { person } = await ensureInviteTokens(personId, editingHappening, { profileLink: true, showLink: false });
            if (!person?.email) throw new Error(`${person?.name ?? 'This artist'} has no email address`);
            await queueImmediateCompletionReminder({
                reminder_type: 'profile',
                entity_id: person.id,
                happening_id: editingHappening.id,
                recipient_email: person.email,
                recipient_name: person.name || person.email,
            });
            queryClient.invalidateQueries({ queryKey: ['profile-reminders', editingHappening.id] });
        });
    };

    const handleSendStubEmail = async (happening: Happening, artist: HappeningArtist) => {
        if (!artist.person_id || !artist.name) {
            console.warn('Artist has no person_id or name, cannot send stub email');
            return;
        }

        try {
            // Fetch the actual Person record to get the email
            const person = await getPerson(artist.person_id);
            if (!person || !person.email) {
                console.warn(`Person ${artist.person_id} (${artist.name}) has no email, cannot send stub email`);
                return;
            }

            // Note: Direct email sending from the browser doesn't work due to CORS restrictions.
            // Instead, we create a completion reminder with offset_hours = 0 which will be picked up
            // by the scheduler very soon (next run, typically within 15 minutes).
            // For immediate sending, a backend API endpoint would be needed.
            await createCompletionReminder({
                reminder_type: 'happening',
                entity_id: happening.id,
                recipient_email: person.email,
                recipient_name: artist.name || artist.slug || person.email || 'Artist',
                offset_hours: 0, // Send immediately (on next scheduler run)
            });

            console.log(`Stub link email queued for ${person.email} (will be sent by scheduler)`);
        } catch (error) {
            console.error('Failed to queue stub email:', error);
            // Don't throw - let the form submission succeed even if email queuing fails
        }
    };

    /**
     * Rewrite one kind of a show's timed reminders on save without re-sending any.
     *
     * Saves used to delete every row and recreate it, which wiped sent_at. A timed reminder
     * stays inside the scheduler's send window for an hour after it fires, so a save in that
     * hour emailed the artist a second time. Now unsent rows are replaced and sent rows stay
     * as the record — unless the show moved and the row's new due time is still ahead, in
     * which case it is cleared so it goes out again at the new time.
     */
    const syncTimedReminders = async <R extends { id: string; offset_hours: number; sent_at: string | null }>(opts: {
        startTime: string | null | undefined;
        deleteUnsent: () => Promise<void>;
        loadRemaining: () => Promise<R[]>;
        deleteOne: (id: string) => Promise<void>;
        keyOf: (row: { offset_hours: number } & Partial<R>) => string;
        wanted: Array<{ key: string; create: () => Promise<unknown> }>;
    }) => {
        await opts.deleteUnsent();
        const start = opts.startTime ? new Date(opts.startTime).getTime() : null;
        const kept = new Set<string>();
        for (const row of await opts.loadRemaining()) {
            if (row.offset_hours === 0) continue; // one-off sends are not part of the schedule
            const dueLater = start !== null && start + row.offset_hours * 60 * 60 * 1000 > Date.now();
            if (row.sent_at && dueLater) {
                await opts.deleteOne(row.id);
                continue;
            }
            kept.add(opts.keyOf(row));
        }
        for (const item of opts.wanted) {
            if (!kept.has(item.key)) await item.create();
        }
    };

    const handleSaveCompletionReminders = async (happeningId: string, artists: HappeningArtist[], startTime: string | null | undefined) => {
        const validArtists = artists.filter(a => a.person_id);

        try {
            // Reminders go to the first artist; the happening's metadata.artists has no email.
            const firstArtist = validArtists[0];
            const person = firstArtist && completionReminders.length > 0 ? await getPerson(firstArtist.person_id) : null;
            if (firstArtist && completionReminders.length > 0 && !person?.email) {
                console.warn(`Person ${firstArtist.person_id} (${firstArtist.name}) has no email, cannot create completion reminders`);
                return;
            }

            // Immediate rows stay out of this: the stub invite handleSendStubEmail queued
            // moments ago is one, and deleting it meant it never sent.
            await syncTimedReminders({
                startTime,
                deleteUnsent: () => deleteCompletionReminders('happening', happeningId, { keepImmediate: true, keepSent: true }),
                loadRemaining: () => getCompletionReminders('happening', happeningId),
                deleteOne: deleteCompletionReminder,
                keyOf: (r) => String(r.offset_hours),
                wanted: person?.email
                    ? completionReminders.map(reminder => ({
                        key: String(reminder.offset_hours),
                        create: () => createCompletionReminder({
                            reminder_type: 'happening',
                            entity_id: happeningId,
                            recipient_email: person.email!,
                            recipient_name: person.name || firstArtist.slug || 'Artist',
                            offset_hours: reminder.offset_hours,
                            happening_id: null,
                        }),
                    }))
                    : [],
            });

            queryClient.invalidateQueries({ queryKey: ['completion-reminders', happeningId, 'happening'] });
        } catch (error) {
            console.error('Failed to save completion reminders:', error);
            // Don't throw - let the form submission succeed even if reminders fail
        }
    };

    const handleSaveHappeningReminders = async (happeningId: string, artists: HappeningArtist[], startTime: string | null | undefined) => {
        const validArtists = artists.filter(a => a.person_id);

        try {
            await syncTimedReminders({
                startTime,
                deleteUnsent: () => deleteHappeningReminders(happeningId, { keepSent: true }),
                loadRemaining: () => getHappeningReminders(happeningId),
                deleteOne: deleteHappeningReminder,
                keyOf: (r) => `${r.person_id}:${r.offset_hours}`,
                // One reminder per artist per offset
                wanted: validArtists.flatMap(artist => happeningReminders.map(reminder => ({
                    key: `${artist.person_id}:${reminder.offset_hours}`,
                    create: () => createHappeningReminder({
                        happening_id: happeningId,
                        person_id: artist.person_id!,
                        offset_hours: reminder.offset_hours,
                    }),
                }))),
            });

            queryClient.invalidateQueries({ queryKey: ['happening-reminders', happeningId] });
        } catch (error) {
            console.error('Failed to save happening reminders:', error);
            // Don't throw - let the form submission succeed even if reminders fail
        }
    };

    const handleSavePostShowReminders = async (happeningId: string, artists: HappeningArtist[], startTime: string | null | undefined) => {
        const validArtists = postShowEnabled ? artists.filter(a => a.person_id) : [];

        try {
            const wanted = [];
            for (const artist of validArtists) {
                const person = await getPerson(artist.person_id!);
                if (!person?.email) continue;
                wanted.push({
                    key: `${artist.person_id}:${postShowOffsetHours}`,
                    create: () => createCompletionReminder({
                        reminder_type: 'post_show',
                        entity_id: artist.person_id!,
                        recipient_email: person.email!,
                        recipient_name: person.name || artist.slug || 'Artist',
                        offset_hours: postShowOffsetHours,
                        happening_id: happeningId,
                    }),
                });
            }

            // A past "Send Now" (offset 0) stays put.
            await syncTimedReminders({
                startTime,
                deleteUnsent: () => deletePostShowRemindersForHappening(happeningId, { keepImmediate: true, keepSent: true }),
                loadRemaining: () => getPostShowRemindersForHappening(happeningId),
                deleteOne: deleteCompletionReminder,
                keyOf: (r) => `${r.entity_id}:${r.offset_hours}`,
                wanted,
            });

            queryClient.invalidateQueries({ queryKey: ['post-show-reminders', happeningId] });
        } catch (error) {
            console.error('Failed to save post-show reminders:', error);
        }
    };

    const handleSendPostShowNow = async (happeningId: string, artists: HappeningArtist[]) => {
        const validArtists = artists.filter(a => a.person_id);
        if (validArtists.length === 0) return;

        try {
            // Delete existing post-show reminders first to avoid duplicates
            await deletePostShowRemindersForHappening(happeningId);

            for (const artist of validArtists) {
                const person = await getPerson(artist.person_id!);
                if (!person?.email) continue;

                await createCompletionReminder({
                    reminder_type: 'post_show',
                    entity_id: artist.person_id!,
                    recipient_email: person.email,
                    recipient_name: person.name || artist.slug || 'Artist',
                    offset_hours: 0, // Immediate send
                    happening_id: happeningId,
                });
            }

            queryClient.invalidateQueries({ queryKey: ['post-show-reminders', happeningId] });
        } catch (error) {
            console.error('Failed to create immediate post-show reminders:', error);
        }
    };

    const handleSaveNotificationRoles = async (happeningId: string, accountId: string) => {
        try {
            await setCompletionNotificationRoles('happening', happeningId, notifyRoles, accountId);
            queryClient.invalidateQueries({ queryKey: ['completion-notification-roles', 'happening', happeningId] });
        } catch (error) {
            console.error('Failed to save notification roles:', error);
            // Don't throw - let the form submission succeed even if notification roles fail
        }
    };

    const handleSaveProfileReminders = async (happeningId: string, artists: HappeningArtist[], startTime: string | null | undefined) => {
        const hasArtists = artists.some(a => a.person_id);

        try {
            const wanted = [];
            for (const reminder of hasArtists ? profileReminders : []) {
                const person = await getPerson(reminder.person_id);
                if (!person || !person.email) {
                    console.warn(`Person ${reminder.person_id} has no email, skipping profile reminder`);
                    continue;
                }
                wanted.push({
                    key: `${reminder.person_id}:${reminder.offset_hours}`,
                    create: () => createCompletionReminder({
                        reminder_type: 'profile',
                        entity_id: reminder.person_id,
                        recipient_email: person.email!,
                        recipient_name: person.name || person.slug || 'Artist',
                        offset_hours: reminder.offset_hours,
                        happening_id: happeningId,
                    }),
                });
            }

            await syncTimedReminders({
                startTime,
                deleteUnsent: () => deleteProfileRemindersForHappening(happeningId, { keepImmediate: true, keepSent: true }),
                loadRemaining: () => getProfileRemindersForHappening(happeningId),
                deleteOne: deleteCompletionReminder,
                keyOf: (r) => `${r.entity_id}:${r.offset_hours}`,
                wanted,
            });

            queryClient.invalidateQueries({ queryKey: ['profile-reminders', happeningId] });
        } catch (error) {
            console.error('Failed to save profile reminders:', error);
            // Don't throw - let the form submission succeed even if reminders fail
        }
    };

    const openEditFlow = (happening: Happening) => {
        setEditingHappening(happening);
        setSelectedType(happening.type);
        const mapped = mapHappeningToFormState(happening, orgTimezone);
        setFormState(mapped);
        creationInitialRef.current = JSON.parse(JSON.stringify(mapped)); // Deep copy for comparison
        setSlugManuallyEdited(true);
        setDialogStep('form');
        setDialogOpen(true);
        // Reset reminder refs - they will be set when reminders load
        initialCompletionRemindersRef.current = [];
        initialHappeningRemindersRef.current = [];
        initialProfileRemindersRef.current = [];
    };

    const happeningsWithSummaries = useMemo(
        () =>
            filteredHappenings.map((happening) => ({
                happening,
                summary: summarizeHappening(happening),
            })),
        [filteredHappenings]
    );

    const isResidentShow = selectedType === 'show' && formState.showType === 'resident';
    const typeMeta = HAPPENING_TYPE_META[selectedType];

    const renderCardView = () => (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {happeningsWithSummaries.map(({ happening, summary }) => (
                <Card
                    key={happening.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openEditFlow(happening)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openEditFlow(happening);
                        }
                    }}
                    className="cursor-pointer border-muted transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary relative"
                >
                    {canDelete && (
                        <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={selectedHappeningIds.has(happening.id)}
                                onChange={() => toggleHappeningSelection(happening.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300"
                                disabled={checkingSingleDependencies.has(happening.id)}
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteClick(happening, e)}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                disabled={checkingSingleDependencies.has(happening.id)}
                                title={checkingSingleDependencies.has(happening.id) ? 'Checking dependencies...' : 'Delete'}
                            >
                                {checkingSingleDependencies.has(happening.id) ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                ) : (
                                    <Trash2 className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    )}
                    <CardHeader className="space-y-3">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl">{happening.title}</CardTitle>
                                <CardDescription>
                                    {happening.slug} • {happening.type}
                                    {happening.metadata?.show_type && (
                                        <>
                                            {' • '}
                                            <span className="font-medium capitalize">{happening.metadata.show_type}</span>
                                        </>
                                    )}
                                    {happening.metadata?.show_type === 'resident' && happening.metadata?.episode_number && (
                                        <> • Episode {happening.metadata.episode_number}</>
                                    )}
                                </CardDescription>
                            </div>
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${happening.status === 'confirmed'
                                    ? 'bg-green-badge text-green-badge-foreground'
                                    : happening.status === 'scheduled'
                                        ? 'bg-blue-badge text-blue-badge-foreground'
                                        : 'bg-secondary text-secondary-foreground'
                                    }`}
                            >
                                {happening.status}
                            </span>
                        </div>
                        {summary.missingFields.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {summary.missingFields.map((field) => (
                                    <span
                                        key={field}
                                        className="inline-flex items-center gap-1 rounded-full bg-yellow-badge text-yellow-badge-foreground px-2 py-0.5 text-xs"
                                    >
                                        <AlertTriangle className="h-3 w-3" />
                                        {field} missing
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-badge text-green-badge-foreground px-2 py-0.5 text-xs">
                                <Sparkles className="h-3 w-3" />
                                Ready to publish
                            </span>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {happening.start_time && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="h-4 w-4" />
                                {formatDateInTimezone(happening.start_time, happening.timezone || orgTimezone, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </div>
                        )}
                        {happening.metadata?.duration_minutes && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                {happening.metadata.duration_minutes} minutes
                            </div>
                        )}
                        {happening.tags && happening.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {happening.tags.map((tag) => (
                                    <TagBadge key={tag.id} tag={tag} />
                                ))}
                            </div>
                        )}
                        {happening.metadata?.genres && happening.metadata.genres.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {happening.metadata.genres.map((genre) => (
                                    <span
                                        key={genre}
                                        className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                                    >
                                        {genre}
                                    </span>
                                ))}
                            </div>
                        )}
                        {(() => {
                            const artists = getArtistsFromHappening(happening as HappeningWithArtists);
                            return artists.length > 0 && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <span>
                                        {artists.map((a, i) => (
                                            <span key={a.person_id}>
                                                {i > 0 && ', '}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/people?edit=${a.person_id}`);
                                                    }}
                                                    className="hover:underline hover:text-primary"
                                                >
                                                    {a.name || a.slug || 'Unnamed'}
                                                </button>
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            );
                        })()}
                        {/* Show required roles from template */}
                        {happening.metadata?.required_roles && happening.metadata.required_roles.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {happening.metadata.required_roles.map((role) => (
                                    <span
                                        key={role.role}
                                        className="inline-flex items-center gap-1 rounded-full bg-secondary text-secondary-foreground px-2 py-0.5 text-xs"
                                    >
                                        <Users className="h-3 w-3" />
                                        {role.role.replace(/_/g, ' ')}
                                    </span>
                                ))}
                            </div>
                        )}
                        {/* Show programmer */}
                        {(() => {
                            const happeningParticipants = participantsByHappening.get(happening.id) || [];
                            const programmer = happeningParticipants.find(p => p.role === 'programmer');
                            const programmerName = programmer?.user_name || programmer?.person_name;
                            if (!programmerName) return null;
                            return (
                                <div className="flex items-center gap-2 text-sm">
                                    <UserIcon className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium">Programmer:</span>
                                    <span>{programmerName}</span>
                                </div>
                            );
                        })()}
                        {happening.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                                {happening.description}
                            </p>
                        )}

                        {happening.metadata?.gear_requests && happening.metadata.gear_requests.length > 0 && (
                            <div className="flex items-center gap-1 rounded-md bg-destructive/10 border border-destructive/30 px-2 py-1.5">
                                <Music className="h-4 w-4 text-destructive" />
                                <span className="text-xs font-medium text-destructive">
                                    {happening.metadata.gear_requests.length} equipment request{happening.metadata.gear_requests.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                        {happening.metadata?.rider_notes && (
                            <div className="flex items-center gap-1 rounded-md bg-destructive/10 border border-destructive/30 px-2 py-1.5">
                                <span className="text-xs font-medium text-destructive">
                                    Has technical notes
                                </span>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <InviteLinkButton
                                happening={happening}
                                personId={pickShowFillerArtist(getArtistsFromHappening(happening))?.person_id ?? null}
                                appearance="card"
                                className="flex-1"
                            />
                            <EmailInviteButton
                                happening={happening}
                                personId={pickShowFillerArtist(getArtistsFromHappening(happening))?.person_id ?? null}
                                appearance="card"
                            />
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    const renderListView = () => (
        <div className="space-y-4">
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {canDelete && (
                                <TableHead className="w-12">
                                    <input
                                        type="checkbox"
                                        checked={selectedHappeningIds.size === filteredHappenings.length && filteredHappenings.length > 0}
                                        onChange={toggleSelectAll}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                </TableHead>
                            )}
                            <TableHead>Title</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Tags</TableHead>
                            <TableHead>Readiness</TableHead>
                            <TableHead>
                                <button
                                    type="button"
                                    onClick={() => setSortByDate(sortByDate === 'asc' ? 'desc' : 'asc')}
                                    className="flex items-center gap-1 hover:text-primary transition-colors"
                                >
                                    Start Date
                                    {sortByDate === 'asc' ? (
                                        <ArrowUp className="h-3 w-3" />
                                    ) : (
                                        <ArrowDown className="h-3 w-3" />
                                    )}
                                </button>
                            </TableHead>
                            <TableHead>Programmer</TableHead>
                            <TableHead>Artists</TableHead>
                            <TableHead>Tasks</TableHead>
                            <TableHead className="min-w-[10rem]">Stub Link</TableHead>
                            {canDelete && <TableHead className="w-12">Actions</TableHead>}
                        </TableRow>
                    </TableHeader>
                <TableBody>
                    {happeningsWithSummaries.map(({ happening, summary }) => (
                        <TableRow
                            key={happening.id}
                            className="cursor-pointer"
                            onClick={() => openEditFlow(happening)}
                        >
                            {canDelete && (
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={selectedHappeningIds.has(happening.id)}
                                        onChange={() => toggleHappeningSelection(happening.id)}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                </TableCell>
                            )}
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div>
                                        <p className="font-medium">{happening.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                            /{happening.slug}
                                            {happening.metadata?.show_type === 'resident' && happening.metadata?.episode_number && (
                                                <> • Episode {happening.metadata.episode_number}</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <span className="capitalize">{happening.type}</span>
                                    {happening.metadata?.show_type && (
                                        <div>
                                            <span className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium capitalize text-primary">
                                                {happening.metadata.show_type}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell>
                                <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${happening.status === 'confirmed'
                                        ? 'bg-green-badge text-green-badge-foreground'
                                        : happening.status === 'scheduled'
                                            ? 'bg-blue-badge text-blue-badge-foreground'
                                            : 'bg-secondary text-secondary-foreground'
                                        }`}
                                >
                                    {happening.status}
                                </span>
                            </TableCell>
                            <TableCell>
                                {happening.tags && happening.tags.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-w-[12rem]">
                                        {happening.tags.map((tag) => (
                                            <TagBadge key={tag.id} tag={tag} />
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-muted-foreground">—</span>
                                )}
                            </TableCell>
                            <TableCell>
                                {summary.missingFields.length > 0 ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-warning-muted px-2 py-0.5 text-xs text-warning-foreground">
                                        <AlertTriangle className="h-3 w-3" />
                                        {summary.missingFields.length} missing
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-success-muted px-2 py-0.5 text-xs text-success-muted-foreground">
                                        <Sparkles className="h-3 w-3" />
                                        Ready
                                    </span>
                                )}
                            </TableCell>
                            <TableCell>
                                {happening.start_time ? (
                                    <div className="space-y-0.5">
                                        <div className="text-sm font-medium">
                                            {formatDateInTimezone(happening.start_time, happening.timezone || orgTimezone, {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                            })}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {formatDateInTimezone(happening.start_time, happening.timezone || orgTimezone, {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-muted-foreground">—</span>
                                )}
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const happeningParticipants = participantsByHappening.get(happening.id) || [];
                                    const programmer = happeningParticipants.find(p => p.role === 'programmer');
                                    const programmerName = programmer?.user_name || programmer?.person_name;
                                    const programmerInitials = getInitials(programmerName);
                                    if (!programmerInitials) {
                                        return <span className="text-muted-foreground">—</span>;
                                    }
                                    return (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-xs font-semibold text-primary border border-primary/20">
                                            <UserIcon className="h-3 w-3" />
                                            {programmerInitials}
                                        </span>
                                    );
                                })()}
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const artists = getArtistsFromHappening(happening as HappeningWithArtists);
                                    return artists.length > 0 ? (
                                        <span className="text-sm">
                                            {artists.map((a, i) => (
                                                <span key={a.person_id}>
                                                    {i > 0 && ', '}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/people?edit=${a.person_id}`);
                                                        }}
                                                        className="hover:underline hover:text-primary"
                                                    >
                                                        {a.name || a.slug || 'Unnamed'}
                                                    </button>
                                                </span>
                                            ))}
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    );
                                })()}
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const taskCounts = taskCountsByHappening.get(happening.id) || { completed: 0, total: 0 };
                                    const hasIncomplete = taskCounts.completed < taskCounts.total;
                                    if (taskCounts.total === 0) {
                                        return <span className="text-muted-foreground">—</span>;
                                    }
                                    return (
                                        <span className={cn(
                                            "inline-flex items-center text-xs font-medium px-2 py-0.5 rounded",
                                            hasIncomplete
                                                ? "bg-orange-badge text-orange-badge-foreground"
                                                : "bg-green-badge text-green-badge-foreground"
                                        )}>
                                            {taskCounts.completed}/{taskCounts.total}
                                        </span>
                                    );
                                })()}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    {happening.metadata?.gear_requests && happening.metadata.gear_requests.length > 0 && (
                                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded bg-destructive/10 text-destructive border border-destructive/30 whitespace-nowrap">
                                            <Music className="h-3 w-3" />
                                            {happening.metadata.gear_requests.length} request{happening.metadata.gear_requests.length !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    <InviteLinkButton
                                        happening={happening}
                                        personId={pickShowFillerArtist(getArtistsFromHappening(happening))?.person_id ?? null}
                                        appearance="inline"
                                    />
                                    <EmailInviteButton
                                        happening={happening}
                                        personId={pickShowFillerArtist(getArtistsFromHappening(happening))?.person_id ?? null}
                                    />
                                </div>
                            </TableCell>
                            {canDelete && (
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => handleDeleteClick(happening, e)}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalHappeningsCount)} of {totalHappeningsCount} happenings
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            Page {currentPage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    // Helper function to extract initials from a name
    const getInitials = (name: string | null | undefined): string => {
        if (!name) return '';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 0) return '';
        if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const renderCalendarView = () => {

        // Convert happenings to CalendarItem format and sort by start time
        const calendarItems: (Happening & CalendarItem)[] = filteredHappenings
            .filter((h) => h.start_time)
            .sort((a, b) => {
                const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
                const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
                return aTime - bTime;
            });

        const getDateKey = (happening: Happening): string | null => {
            if (!happening.start_time) return null;
            const date = new Date(happening.start_time);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        const getTime = (happening: Happening): Date | null => {
            if (!happening.start_time) return null;
            return new Date(happening.start_time);
        };

        const getEndTime = (happening: Happening): Date | null => {
            if (!happening.end_time) return null;
            return new Date(happening.end_time);
        };

        const renderHappening = (happening: Happening) => {
            const taskCounts = taskCountsByHappening.get(happening.id) || { completed: 0, total: 0 };
            const hasIncomplete = taskCounts.completed < taskCounts.total;

            // Get template name if happening was created from a template, otherwise show type
            const templateId = happening.metadata?.template_id as string | undefined;
            const template = templateId && templates.length > 0 ? templates.find(t => t.id === templateId) : undefined;
            const showType = happening.metadata?.show_type as RadioShowType | undefined;

            // Display template name if available, otherwise show type
            // Only show template name if we found a matching template (to avoid showing undefined)
            const displayLabel = template ? template.name : (showType ? showType.charAt(0).toUpperCase() + showType.slice(1) : undefined);


            // Get programmer initials from participants
            const happeningParticipants = participantsByHappening.get(happening.id) || [];
            const programmer = happeningParticipants.find(p => p.role === 'programmer');
            const programmerName = programmer?.user_name || programmer?.person_name;
            const programmerInitials = getInitials(programmerName);

            return (
                <button
                    type="button"
                    onClick={() => openEditFlow(happening)}
                    className="block w-full rounded px-1.5 py-0.5 text-left text-xs transition hover:bg-primary/10"
                    style={{
                        borderLeft: `3px solid ${happening.status === 'confirmed'
                            ? '#10b981'
                            : happening.status === 'scheduled'
                                ? '#3b82f6'
                                : '#6b7280'
                            }`,
                    }}
                >
                    <div className="truncate font-medium">{happening.title}</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {displayLabel && (
                            <span className="text-[10px] text-muted-foreground">
                                {displayLabel}
                            </span>
                        )}
                        {programmerInitials && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-[10px] font-semibold text-primary border border-primary/20">
                                <UserIcon className="h-2.5 w-2.5" />
                                {programmerInitials}
                            </span>
                        )}
                        {happening.start_time && (
                            <div className="text-[10px] text-muted-foreground">
                                {new Date(happening.start_time).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                                {happening.end_time && (
                                    <>
                                        {' - '}
                                        {new Date(happening.end_time).toLocaleTimeString([], {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </>
                                )}
                            </div>
                        )}
                        {taskCounts.total > 0 && (
                            <span className={cn(
                                "inline-flex items-center text-[10px] font-medium",
                                hasIncomplete ? "text-orange-600" : "text-green-600"
                            )}>
                                {taskCounts.completed}/{taskCounts.total}
                            </span>
                        )}
                    </div>
                </button>
            );
        };

        return (
            <CalendarComponent
                items={calendarItems}
                onItemClick={openEditFlow}
                renderItem={renderHappening}
                getDateKey={getDateKey}
                getTime={getTime}
                getEndTime={getEndTime}
                emptyMessage="No happenings"
                defaultView={calendarViewType}
                onViewTypeChange={(viewType) => setCalendarViewType('happenings', viewType)}
                onVisibleRangeChange={setCalendarVisibleRange}
            />
        );
    };

    const renderKanbanView = () => {
        // Happening Status Workflow:
        // - draft: Initial state, work in progress, not yet finalized
        // - scheduled: Time/date set, but not all details confirmed (e.g., artist not confirmed)
        // - confirmed: All details locked in, ready to proceed (artists confirmed, gear ready, etc.)
        // - completed: Happening has finished
        // - cancelled: Happening was cancelled
        //
        // Note: Status is independent of is_public. A happening can be:
        // - Public (is_public=true): Visible on public website, triggers webhook updates
        // - Private (is_public=false): Only visible to internal team
        // You can have a public happening in any status (e.g., public draft for preview)
        const HAPPENING_STATUSES: HappeningStatus[] = ['draft', 'scheduled', 'confirmed', 'completed', 'cancelled'];
        const STATUS_LABELS: Record<HappeningStatus, string> = {
            draft: 'Draft',
            scheduled: 'Scheduled',
            confirmed: 'Confirmed',
            completed: 'Completed',
            cancelled: 'Cancelled',
        };

        return (
            <KanbanBoard
                items={filteredHappenings}
                statuses={HAPPENING_STATUSES}
                statusLabels={STATUS_LABELS}
                onItemClick={openEditFlow}
                onStatusChange={(happeningId, newStatus) => {
                    updateMutation.mutate({
                        id: happeningId,
                        patch: { status: newStatus as HappeningStatus },
                    });
                }}
                renderItem={(happening) => {
                    const taskCounts = taskCountsByHappening.get(happening.id) || { completed: 0, total: 0 };
                    const hasIncomplete = taskCounts.completed < taskCounts.total;
                    const happeningParticipants = participantsByHappening.get(happening.id) || [];
                    const programmer = happeningParticipants.find(p => p.role === 'programmer');
                    const programmerName = programmer?.user_name || programmer?.person_name;
                    const programmerInitials = getInitials(programmerName);
                    return (
                        <div className="space-y-2">
                            <div className="font-medium text-sm">{happening.title}</div>
                            {happening.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                    {happening.description}
                                </p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-muted-foreground capitalize">
                                    {happening.type}
                                </span>
                                {programmerInitials && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-xs font-semibold text-primary border border-primary/20">
                                        <UserIcon className="h-3 w-3" />
                                        {programmerInitials}
                                    </span>
                                )}
                                {happening.start_time && (
                                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <Calendar className="h-3 w-3" />
                                        {new Date(happening.start_time).toLocaleDateString()}
                                    </span>
                                )}
                                {taskCounts.total > 0 && (
                                    <span className={cn(
                                        "inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded",
                                        hasIncomplete
                                            ? "bg-orange-badge text-orange-badge-foreground"
                                            : "bg-green-badge text-green-badge-foreground"
                                    )}>
                                        {taskCounts.completed}/{taskCounts.total}
                                    </span>
                                )}
                                {happening.metadata?.gear_requests && happening.metadata.gear_requests.length > 0 && (
                                    <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded bg-destructive/10 text-destructive border border-destructive/30">
                                        <Music className="h-3 w-3" />
                                        {happening.metadata.gear_requests.length}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                }}
                emptyMessage="No happenings"
            />
        );
    };

    return (
        <div className={cn('space-y-8', viewMode === 'kanban' && 'w-full')} style={viewMode === 'kanban' ? { maxWidth: '100%', overflowX: 'hidden' } : undefined}>
            <div className="flex flex-wrap items-start sm:items-center justify-between gap-4">
                <div>
                    <p className="hidden sm:block text-sm uppercase tracking-wide text-muted-foreground">Programming</p>
                    <h1 className="text-2xl sm:text-3xl font-bold">Happenings</h1>
                    <p className="hidden sm:block text-muted-foreground">
                        Create radio shows, events, and recordings. Artists can fill in details via stub links.
                    </p>
                </div>
                <Button onClick={startCreateFlow} size={isMobile ? 'sm' : 'default'}>
                    <Plus className="mr-2 h-4 w-4" />
                    {isMobile ? 'Add' : 'Add Happening'}
                </Button>
            </div>

            <div className={cn('flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4', viewMode === 'kanban' && 'sticky top-0 z-20 bg-background pb-4 border-b mb-4 -mx-8 px-8')}>
                <SearchFilter
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search happenings..."
                    className="w-full sm:w-64"
                />
                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                    {user && (
                        <Button
                            variant={isMyHappeningsFilter ? 'default' : 'outline'}
                            size="sm"
                            onClick={toggleMyHappenings}
                            className="gap-2"
                        >
                            <span>My Happenings</span>
                            <span className="hidden sm:inline-flex items-center gap-0.5">
                                {typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? (
                                    <>
                                        <kbd className={cn(
                                            'inline-flex h-5 min-w-5 items-center justify-center rounded border text-[11px] font-medium',
                                            isMyHappeningsFilter
                                                ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
                                                : 'border-border bg-muted text-muted-foreground'
                                        )}>⇧</kbd>
                                        <kbd className={cn(
                                            'inline-flex h-5 min-w-5 items-center justify-center rounded border text-[11px] font-medium',
                                            isMyHappeningsFilter
                                                ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
                                                : 'border-border bg-muted text-muted-foreground'
                                        )}>⌘</kbd>
                                        <kbd className={cn(
                                            'inline-flex h-5 min-w-5 items-center justify-center rounded border text-[11px] font-medium',
                                            isMyHappeningsFilter
                                                ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
                                                : 'border-border bg-muted text-muted-foreground'
                                        )}>M</kbd>
                                    </>
                                ) : (
                                    <kbd className={cn(
                                        'inline-flex h-5 items-center rounded border px-1.5 text-[10px] font-medium',
                                        isMyHappeningsFilter
                                            ? 'border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground'
                                            : 'border-border bg-muted text-muted-foreground'
                                    )}>Ctrl+Shift+M</kbd>
                                )}
                            </span>
                        </Button>
                    )}
                    <ParticipantFilterDropdown
                        participantFilter={participantFilter}
                        onFilterChange={setParticipantFilter}
                        linkedPeople={linkedPeople}
                        linkedUsers={linkedUsers}
                        uniqueRoles={uniqueRoles}
                    />
                    {programs.length > 0 && (
                        <select
                            value={programFilter}
                            onChange={(e) => setProgramFilter(e.target.value)}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                            title="Filter by program"
                        >
                            <option value="all">All programs</option>
                            {programs.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    )}
                    {viewMode !== 'calendar' && (
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300"
                            />
                            <span className="hidden sm:inline">Show completed</span>
                            <span className="sm:hidden">Done</span>
                        </label>
                    )}
                    <ViewToggle
                        view={viewMode}
                        onViewChange={(mode) => setViewMode('happenings', mode)}
                        availableViews={isMobile ? ['list', 'card'] : ['list', 'card', 'calendar', 'kanban']}
                    />
                </div>
            </div>

            {/* Bulk actions bar */}
            {selectedHappeningIds.size > 0 && canDelete && (
                <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
                    <span className="text-sm font-medium">
                        {selectedHappeningIds.size} happening{selectedHappeningIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedHappeningIds(new Set())}
                            disabled={checkingBulkDependencies}
                        >
                            Clear selection
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBulkStatusDialogOpen(true)}
                            disabled={checkingBulkDependencies || bulkStatusMutation.isPending}
                        >
                            Change status…
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleBulkDeleteClick}
                            disabled={checkingBulkDependencies}
                        >
                            {checkingBulkDependencies ? (
                                <>
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    Checking dependencies...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete {selectedHappeningIds.size}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {viewMode === 'calendar' ? (
                // Calendar view: always render to preserve navigation state during loading
                <div className="relative">
                    {isLoading && (
                        <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                            <p>Loading...</p>
                        </div>
                    )}
                    {renderCalendarView()}
                </div>
            ) : isLoading ? (
                <p>Loading...</p>
            ) : filteredHappenings.length === 0 ? (
                <p className="text-muted-foreground">
                    {searchQuery ? 'No happenings match your search.' : 'No happenings yet.'}
                </p>
            ) : viewMode === 'card' ? (
                renderCardView()
            ) : viewMode === 'kanban' ? (
                renderKanbanView()
            ) : (
                renderListView()
            )}

            <Dialog
                open={showArchiveDialog}
                onOpenChange={(open) => {
                    setShowArchiveDialog(open);
                    if (!open) resetArchiveForm();
                }}
            >
                <DialogContent size="compact">
                    <DialogHeader>
                        <DialogTitle>Add archive/embed link</DialogTitle>
                        <DialogDescription>
                            Add a SoundCloud link or any external recording/embed and we’ll keep it with the show.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Link type</label>
                            <select
                                value={archiveForm.type}
                                onChange={(e) =>
                                    setArchiveForm((prev) => ({ ...prev, type: e.target.value as 'soundcloud' | 'custom' }))
                                }
                                className="w-full rounded-md border px-3 py-2 text-sm"
                            >
                                <option value="soundcloud">SoundCloud</option>
                                <option value="custom">Custom</option>
                            </select>
                        </div>

                        {archiveForm.type === 'custom' && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Label</label>
                                <input
                                    value={archiveForm.name}
                                    onChange={(e) => setArchiveForm((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder="YouTube, Mixcloud, Website..."
                                    className="w-full rounded-md border px-3 py-2 text-sm"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-sm font-medium">URL</label>
                            <input
                                value={archiveForm.url}
                                onChange={(e) => setArchiveForm((prev) => ({ ...prev, url: e.target.value }))}
                                placeholder={
                                    archiveForm.type === 'soundcloud'
                                        ? 'https://soundcloud.com/artist/track'
                                        : 'https://example.com/your-archive'
                                }
                                className="w-full rounded-md border px-3 py-2 text-sm"
                            />
                        </div>

                        {archiveForm.type === 'custom' && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Icon slug (optional)</label>
                                <input
                                    value={archiveForm.iconSlug}
                                    onChange={(e) => setArchiveForm((prev) => ({ ...prev, iconSlug: e.target.value }))}
                                    placeholder="youtube, spotify, mixcloud..."
                                    className="w-full rounded-md border px-3 py-2 text-sm"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Lowercase, hyphenated brand name from simpleicons.org (e.g., youtube, spotify, mixcloud).
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="sticky bottom-0 left-0 right-0 bg-background border-t mt-4">
                        <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleArchiveSave}
                            disabled={
                                !archiveForm.url.trim() ||
                                (archiveForm.type === 'custom' && !archiveForm.name.trim())
                            }
                        >
                            Save link
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unsaved changes confirm */}
            <Dialog open={showUnsavedConfirm} onOpenChange={setShowUnsavedConfirm}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Discard changes?</DialogTitle>
                        <DialogDescription>You have unsaved changes. Close without saving?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:justify-end">
                        <Button variant="outline" onClick={() => setShowUnsavedConfirm(false)}>
                            Keep editing
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowUnsavedConfirm(false);
                                closeDialog();
                                creationInitialRef.current = null;
                            }}
                        >
                            Discard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        attemptCloseDialog();
                    } else {
                        setDialogOpen(true);
                    }
                }}
            >
                <DialogContent size="large" className="h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto [&_form]:relative p-0 flex flex-col">
                    {dialogStep === 'type' ? (
                        <div className="space-y-6 p-6">
                            <DialogHeader>
                                <DialogTitle>What are you creating?</DialogTitle>
                                <DialogDescription>Pick the type to get the right form and fields.</DialogDescription>
                            </DialogHeader>

                            {/* Template option */}
                            {templates.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setUseTemplate(!useTemplate)}
                                    className={`w-full rounded-lg border p-4 text-left transition ${useTemplate
                                        ? 'border-primary bg-primary/5'
                                        : 'border-input hover:border-primary/60'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            <p className="text-base font-semibold">Create from Template</p>
                                        </div>
                                        {useTemplate && <CheckCircle2 className="h-4 w-4 text-primary" />}
                                    </div>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        Use a pre-configured template to auto-create tasks, set up gear requirements, and more.
                                    </p>
                                </button>
                            )}

                            {!useTemplate && (
                                <>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {(Object.keys(HAPPENING_TYPE_META) as HappeningType[]).map((type) => {
                                            const meta = HAPPENING_TYPE_META[type];
                                            const isSelected = selectedType === type;
                                            const Icon = meta.icon;
                                            return (
                                                <button
                                                    type="button"
                                                    key={type}
                                                    onClick={() => setSelectedType(type)}
                                                    className={`rounded-lg border p-4 text-left transition ${isSelected
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-input hover:border-primary/60'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Icon className="h-4 w-4" />
                                                            <p className="text-base font-semibold">{meta.label}</p>
                                                        </div>
                                                        {isSelected && <Sparkles className="h-4 w-4 text-primary" />}
                                                    </div>
                                                    <p className="mt-2 text-sm text-muted-foreground">{meta.description}</p>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Recurrence mode selection */}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-muted-foreground">Single, recurring series, or batch?</label>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setCreationMode('single')}
                                                className={cn(
                                                    'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                    creationMode === 'single'
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-input hover:border-primary/60'
                                                )}
                                            >
                                                Single
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCreationMode('series')}
                                                className={cn(
                                                    'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                    creationMode === 'series'
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-input hover:border-primary/60'
                                                )}
                                            >
                                                <Repeat className="inline-block h-3.5 w-3.5 mr-1" />
                                                Series
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCreationMode('batch')}
                                                className={cn(
                                                    'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                    creationMode === 'batch'
                                                        ? 'border-primary bg-primary/5 text-primary'
                                                        : 'border-input hover:border-primary/60'
                                                )}
                                            >
                                                Batch
                                            </button>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {creationMode === 'single' && 'Create a single happening.'}
                                            {creationMode === 'series' && 'Create a recurring series (e.g., every Wednesday). You set a pattern and a date range; every occurrence in that range is created, linked, and managed together.'}
                                            {creationMode === 'batch' && 'Generate independent happenings from a pattern and date range. Not linked as a series.'}
                                        </p>
                                    </div>
                                </>
                            )}

                            {useTemplate && (
                                <div className="space-y-4">
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium">Select Template</label>
                                        <div className="grid gap-2">
                                            {templates.filter(t => t.is_active).map((template) => (
                                                <button
                                                    type="button"
                                                    key={template.id}
                                                    onClick={() => {
                                                        setSelectedTemplateId(template.id);
                                                        setSelectedType(template.happening_type);
                                                        setTemplateRoleAssignments({}); // Reset assignments when template changes
                                                        setTemplateDuration(template.default_duration_minutes || null);
                                                    }}
                                                    className={`rounded-lg border p-3 text-left transition ${selectedTemplateId === template.id
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-input hover:border-primary/60'
                                                        }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-medium">{template.name}</p>
                                                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                                            {template.happening_type}
                                                        </span>
                                                    </div>
                                                    {template.description && (
                                                        <p className="mt-1 text-xs text-muted-foreground">{template.description}</p>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Recurrence mode for templates */}
                                    {selectedTemplateId && (
                                        <div className="space-y-2 pt-2 border-t">
                                            <label className="text-sm font-medium text-muted-foreground">Single, recurring series, or batch?</label>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setCreationMode('single')}
                                                    className={cn(
                                                        'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                        creationMode === 'single'
                                                            ? 'border-primary bg-primary/5 text-primary'
                                                            : 'border-input hover:border-primary/60'
                                                    )}
                                                >
                                                    Single
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCreationMode('series')}
                                                    className={cn(
                                                        'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                        creationMode === 'series'
                                                            ? 'border-primary bg-primary/5 text-primary'
                                                            : 'border-input hover:border-primary/60'
                                                    )}
                                                >
                                                    <Repeat className="inline-block h-3.5 w-3.5 mr-1" />
                                                    Series
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCreationMode('batch')}
                                                    className={cn(
                                                        'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition',
                                                        creationMode === 'batch'
                                                            ? 'border-primary bg-primary/5 text-primary'
                                                            : 'border-input hover:border-primary/60'
                                                    )}
                                                >
                                                    Batch
                                                </button>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {creationMode === 'single' && 'Create a single happening from this template.'}
                                                {creationMode === 'series' && 'Create a recurring series using this template. You set a pattern and a date range; tasks are auto-created for each instance.'}
                                                {creationMode === 'batch' && 'Generate independent happenings from this template over a pattern and date range. Not linked as a series.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <DialogFooter>
                                <Button
                                    onClick={handleTypeContinue}
                                    disabled={useTemplate && !selectedTemplateId}
                                >
                                    Continue
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : dialogStep === 'template' ? (
                        <div className="space-y-6 p-6">
                            <DialogHeader>
                                <DialogTitle>Configure from Template</DialogTitle>
                                <DialogDescription>
                                    Set up the date, time, and location. Tasks will be auto-created based on the template.
                                </DialogDescription>
                            </DialogHeader>

                            {/* Template info */}
                            {selectedTemplateId && (
                                <div className="rounded-md border bg-muted/30 p-3">
                                    <p className="font-medium">
                                        {templates.find(t => t.id === selectedTemplateId)?.name}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {templates.find(t => t.id === selectedTemplateId)?.description}
                                    </p>
                                </div>
                            )}

                            {/* Custom title (optional) */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Show Title</label>
                                <input
                                    type="text"
                                    value={templateTitle}
                                    onChange={(e) => setTemplateTitle(e.target.value)}
                                    placeholder={selectedTemplate?.name || 'Leave empty to use template name'}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Optional. Leave empty to use "{selectedTemplate?.name}" as the title.
                                </p>
                            </div>

                            {/* Date/Time */}
                            <div className="space-y-3">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">
                                            Start Time <span className="text-destructive">*</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={templateStartTime}
                                            onChange={(e) => setTemplateStartTime(e.target.value)}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                    </div>
                                    {useEndTime ? (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">End Time</label>
                                            <input
                                                type="datetime-local"
                                                value={templateEndTime}
                                                onChange={(e) => setTemplateEndTime(e.target.value)}
                                                min={templateStartTime}
                                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Duration</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    value={templateDuration || ''}
                                                    onChange={(e) => setTemplateDuration(e.target.value ? Number(e.target.value) : null)}
                                                    min={15}
                                                    step={15}
                                                    className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    placeholder="60"
                                                />
                                                <span className="text-sm text-muted-foreground">minutes</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setUseEndTime(!useEndTime)}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {useEndTime ? 'Use duration instead' : 'Set specific end time'}
                                </button>
                            </div>

                            {/* Place selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Location</label>
                                <select
                                    value={templatePlaceId || ''}
                                    onChange={(e) => setTemplatePlaceId(e.target.value || null)}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">No location selected</option>
                                    {places.map((place) => (
                                        <option key={place.id} value={place.id}>
                                            {place.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Timezone selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Timezone</label>
                                <select
                                    value={templateTimezone || ''}
                                    onChange={(e) => setTemplateTimezone(e.target.value || null)}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="">Use organization default ({orgTimezone})</option>
                                    {COMMON_TIMEZONES.map((tz) => (
                                        <option key={tz.value} value={tz.value}>
                                            {tz.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Availability indicator */}
                            {templatePlaceId && templateStartTime && (
                                <div className={`flex items-center gap-2 rounded-md border p-3 ${checkingAvailability ? 'border-muted bg-muted/30' :
                                    availability?.available ? 'bg-green-badge border border-green-badge-foreground/20' :
                                        'bg-yellow-badge border border-yellow-badge-foreground/20'
                                    }`}>
                                    {checkingAvailability ? (
                                        <>
                                            <Clock className="h-4 w-4 animate-pulse" />
                                            <span className="text-sm">Checking availability...</span>
                                        </>
                                    ) : availability?.available ? (
                                        <>
                                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                            <span className="text-sm text-emerald-700 dark:text-emerald-400">
                                                {places.find(p => p.id === templatePlaceId)?.name} is available
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                                            <div className="flex-1 text-sm">
                                                <p className="font-medium text-amber-700 dark:text-amber-400">
                                                    Conflict detected
                                                </p>
                                                {availability?.conflicts && availability.conflicts.length > 0 && (
                                                    <p className="text-xs text-amber-600 dark:text-amber-500">
                                                        Overlaps with: {availability.conflicts.map(c => c.title).join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Role Assignment */}
                            {selectedTemplate && selectedTemplate.required_roles && selectedTemplate.required_roles.length > 0 && (
                                <div className="space-y-3">
                                    <label className="text-sm font-medium">Assign Roles</label>
                                    <p className="text-xs text-muted-foreground">
                                        Assign team members and artists to roles. You can leave roles empty and fill them later.
                                    </p>
                                    <div className="rounded-md border bg-yellow-badge text-yellow-badge-foreground border border-yellow-badge-foreground/20 p-2 text-xs">
                                        Role assignments are recommended but optional at creation. Missing required roles will be highlighted to fix later.
                                    </div>
                                    <div className="space-y-3">
                                        {(selectedTemplate.required_roles as TemplateRole[]).map((roleConfig) => {
                                            const assigned = templateRoleAssignments[roleConfig.role] || [];
                                            const isRequired = (roleConfig.min_count ?? 0) > 0;
                                            const maxReached = roleConfig.max_count !== null && roleConfig.max_count > 0 && assigned.length >= roleConfig.max_count;

                                            // Determine source: 'users' for staff, 'people' for artists
                                            const assigneeSource = roleConfig.assignee_source ||
                                                (roleConfig.person_type === 'artist' ? 'people' : 'users');
                                            const isStaffRole = assigneeSource === 'users';

                                            const isEmpty = assigned.length === 0;
                                            const needsAttention = isRequired && assigned.length < (roleConfig.min_count ?? 0);

                                            return (
                                                <div key={roleConfig.role} className={`rounded-md border p-3 space-y-2 ${needsAttention ? 'bg-yellow-badge border border-yellow-badge-foreground/30' : isEmpty ? 'border-dashed border-muted-foreground/40' : ''}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium capitalize">{roleConfig.role.replace(/_/g, ' ')}</span>
                                                            {isRequired && <span className="text-destructive">*</span>}
                                                            <span className="text-xs text-muted-foreground">
                                                                ({isStaffRole ? 'staff' : 'artist'})
                                                            </span>
                                                            {needsAttention && (
                                                                <span className="text-xs text-amber-600 dark:text-amber-500">
                                                                    Required
                                                                </span>
                                                            )}
                                                            {!isRequired && isEmpty && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Optional
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-muted-foreground">
                                                            Assigned: {assigned.length}
                                                            {isRequired && <> • Required: {roleConfig.min_count}</>}
                                                            {roleConfig.max_count !== null && roleConfig.max_count > 0 && <> • Max: {roleConfig.max_count}</>}
                                                        </span>
                                                    </div>

                                                    {/* Assigned people/users */}
                                                    {assigned.length > 0 && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {assigned.map(assignment => (
                                                                <span
                                                                    key={assignment.id}
                                                                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs"
                                                                >
                                                                    {assignment.name}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setTemplateRoleAssignments(prev => ({
                                                                                ...prev,
                                                                                [roleConfig.role]: (prev[roleConfig.role] || []).filter(a => a.id !== assignment.id)
                                                                            }));
                                                                        }}
                                                                        className="hover:text-destructive"
                                                                    >
                                                                        <X className="h-3 w-3" />
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Add dropdown - async search */}
                                                    {!maxReached && (
                                                        <AsyncSearchableSelect
                                                            searchFn={isStaffRole ? searchUsers : searchPeople}
                                                            selectedIds={assigned.map(a => a.id)}
                                                            onSelect={(option) => {
                                                                setTemplateRoleAssignments(prev => ({
                                                                    ...prev,
                                                                    [roleConfig.role]: [
                                                                        ...(prev[roleConfig.role] || []),
                                                                        { id: option.id, name: option.name, source: assigneeSource }
                                                                    ]
                                                                }));
                                                            }}
                                                            placeholder={`Search ${roleConfig.role.replace(/_/g, ' ')}...`}
                                                            searchPlaceholder={`Search ${isStaffRole ? 'staff' : 'people'}...`}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {mutationError && (
                                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                                    <AlertTriangle className="h-4 w-4" />
                                    {mutationError.message}
                                </div>
                            )}

                            <DialogFooter className="flex-col gap-2 sm:flex-row sticky bottom-0 left-0 right-0 bg-background border-t px-0 pt-4 sm:gap-2 sm:justify-end">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setDialogStep('type')}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </Button>
                                <Button
                                    onClick={handleTemplateSubmit}
                                    disabled={!templateStartTime || isSaving}
                                >
                                    {isSaving ? 'Creating...' : 'Create Happening'}
                                    <Sparkles className="ml-2 h-4 w-4" />
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : dialogStep === 'recurrence' ? (
                        <div className="space-y-6 p-6">
                            <DialogHeader>
                                <DialogTitle>
                                    {creationMode === 'series' ? 'Configure Series' : 'Batch Generate'}
                                    {useTemplate && selectedTemplate && ` from "${selectedTemplate.name}"`}
                                </DialogTitle>
                                <DialogDescription>
                                    {creationMode === 'series'
                                        ? 'Set up the recurrence pattern for your series. Happenings will be linked and can be managed together.'
                                        : 'Generate multiple independent happenings from a recurrence pattern.'}
                                    {useTemplate && ' Tasks will be auto-created for each instance based on the template.'}
                                </DialogDescription>
                            </DialogHeader>

                            {/* Template info banner */}
                            {useTemplate && selectedTemplate && (
                                <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
                                    <div className="flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-primary" />
                                        <span className="font-medium text-primary">Using template: {selectedTemplate.name}</span>
                                    </div>
                                    {selectedTemplate.description && (
                                        <p className="mt-1 text-sm text-muted-foreground">{selectedTemplate.description}</p>
                                    )}
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        Each generated happening will inherit the template's tasks, gear requirements, and default settings.
                                    </p>
                                </div>
                            )}

                            {/* Batch/Series mode: Required roles from template */}
                            {(creationMode === 'batch' || creationMode === 'series') && useTemplate && selectedTemplate && selectedTemplate.required_roles.length > 0 && (
                                <div className="space-y-4 rounded-md bg-blue-badge border border-blue-badge-foreground/20 p-4">
                                    <h3 className="font-medium text-blue-900">Required Roles for This Template</h3>
                                    <p className="text-sm text-blue-800">
                                        Assign people to these roles. They will be added to each generated happening.
                                    </p>
                                    <div className="space-y-3">
                                        {selectedTemplate.required_roles.map((roleConfig) => {
                                            const assigned = templateRoleAssignments[roleConfig.role] || [];
                                            const isStaffRole = roleConfig.assignee_source === 'users' || roleConfig.person_type === 'staff';
                                            const maxReached = roleConfig.max_count !== null && assigned.length >= roleConfig.max_count;
                                            const coveredByRotation = isRequiredRoleSatisfiedByRotation(roleConfig.role);
                                            const rotatable = creationMode === 'series' && roleConfig.role === PROGRAMMER_ROTATION_ROLE;
                                            // Drop the required marker only once the rotation actually covers the role.
                                            // Keeping it while the pool is empty is what tells the user why the button
                                            // is still disabled — the pool is where they satisfy it, not this box.
                                            const minRequired = roleConfig.min_count > 0 && !coveredByRotation;

                                            return (
                                                <div key={roleConfig.role} className="space-y-2 rounded-md bg-white p-3">
                                                    <div className="flex items-baseline justify-between">
                                                        <label className="font-medium text-sm">
                                                            {roleConfig.role.replace(/_/g, ' ')}
                                                            {minRequired && <span className="text-destructive ml-1">*</span>}
                                                        </label>
                                                        <span className="text-xs text-muted-foreground">
                                                            {assigned.length} of {roleConfig.max_count || '∞'} {roleConfig.min_count > 1 ? `(min: ${roleConfig.min_count})` : ''}
                                                        </span>
                                                    </div>

                                                    {rotatable && (
                                                        <p className="text-xs text-blue-800">
                                                            {coveredByRotation
                                                                ? 'Filled by the programmer rotation below — a different person is assigned to each generated instance, so you do not need to assign one here. Anyone you do add is ignored for this role.'
                                                                : 'Use the programmer rotation below instead — it assigns a different person to each generated instance. Anyone added here is ignored for this role.'}
                                                        </p>
                                                    )}

                                                    {assigned.length > 0 && (
                                                        <div className="space-y-2">
                                                            {assigned.map((person, idx) => (
                                                                <div key={idx} className="flex items-center justify-between rounded-md bg-muted p-2">
                                                                    <span className="text-sm">{person.name}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setTemplateRoleAssignments(prev => ({
                                                                                ...prev,
                                                                                [roleConfig.role]: prev[roleConfig.role]!.filter((_, i) => i !== idx)
                                                                            }));
                                                                        }}
                                                                        className="text-muted-foreground hover:text-destructive"
                                                                    >
                                                                        <X className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {!maxReached && (
                                                        <AsyncSearchableSelect
                                                            searchFn={isStaffRole ? searchUsers : searchPeople}
                                                            selectedIds={assigned.map(a => a.id)}
                                                            onSelect={(option) => {
                                                                setTemplateRoleAssignments(prev => ({
                                                                    ...prev,
                                                                    [roleConfig.role]: [
                                                                        ...(prev[roleConfig.role] || []),
                                                                        { id: option.id, name: option.name, source: isStaffRole ? 'users' : 'people' }
                                                                    ]
                                                                }));
                                                            }}
                                                            placeholder={`Search ${roleConfig.role.replace(/_/g, ' ')}...`}
                                                            searchPlaceholder={`Search ${isStaffRole ? 'staff' : 'people'}...`}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Option to skip required roles */}
                                    {selectedTemplate.required_roles.some(role => !isRequiredRoleSatisfied(role)) && (
                                        <div className="mt-3 flex items-start gap-2 rounded-md bg-yellow-badge border border-yellow-badge-foreground/20 p-3">
                                            <input
                                                type="checkbox"
                                                id="skip-required-roles"
                                                checked={skipRequiredRoles}
                                                onChange={(e) => setSkipRequiredRoles(e.target.checked)}
                                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <label htmlFor="skip-required-roles" className="text-sm text-amber-900 cursor-pointer">
                                                <span className="font-medium">Create anyway without required roles</span>
                                                <p className="mt-0.5 text-xs text-amber-700">
                                                    Check this to create happenings even if required roles are not assigned. You can assign roles later.
                                                </p>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Series name (only for series mode) */}
                            {creationMode === 'series' && (
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">
                                            Series Name <span className="text-destructive">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={seriesName}
                                            onChange={(e) => setSeriesName(e.target.value)}
                                            placeholder={useTemplate && selectedTemplate ? `${selectedTemplate.name} Series` : "e.g., Wednesday Night Sessions"}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                        {useTemplate && selectedTemplate && !seriesName && (
                                            <button
                                                type="button"
                                                onClick={() => setSeriesName(`${selectedTemplate.name} Series`)}
                                                className="text-xs text-blue-600 hover:text-blue-800"
                                            >
                                                Use "{selectedTemplate.name} Series"
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">
                                            Default Title for Episodes
                                        </label>
                                        <input
                                            type="text"
                                            value={seriesDefaultTitle}
                                            onChange={(e) => setSeriesDefaultTitle(e.target.value)}
                                            placeholder="Leave empty to use series name"
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Each episode will use this title. If empty, the series name is used.
                                        </p>
                                    </div>

                                    {/* Show type (only for radio shows) */}
                                    {selectedType === 'show' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Show Type</label>
                                                <select
                                                    value={seriesDefaultShowType}
                                                    onChange={(e) => setSeriesDefaultShowType(e.target.value as RadioShowType)}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                >
                                                    <option value="resident">Resident Show</option>
                                                    <option value="guest">Guest Show</option>
                                                </select>
                                            </div>
                                            {/* Only resident shows are numbered; generateSeriesInstances ignores this
                                                for guest series, so asking for it there is a question with no effect. */}
                                            {seriesDefaultShowType === 'resident' && (
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium">Starting Episode #</label>
                                                    <NumberInput
                                                        min={1}
                                                        allowEmpty={false}
                                                        value={seriesStartingEpisode}
                                                        onChange={(n) => setSeriesStartingEpisode(Math.max(1, n ?? 1))}
                                                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Bulk editing is opt-in — see seriesAllowBulkEdits. */}
                                    <div className="rounded-md border bg-muted/30 p-3">
                                        <label className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={seriesAllowBulkEdits}
                                                onChange={(e) => setSeriesAllowBulkEdits(e.target.checked)}
                                                className="mt-1 h-4 w-4 rounded border-input"
                                            />
                                            <span>
                                                <span className="text-sm font-medium">Allow editing future shows in bulk</span>
                                                <p className="text-xs text-muted-foreground">
                                                    Off by default: every edit applies to the one show you are editing, with no
                                                    prompt. Turn this on only if the shows really are copies of each other and you
                                                    want the option to push a change to this and all future dates.
                                                </p>
                                            </span>
                                        </label>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Default Description</label>
                                        <textarea
                                            value={seriesDefaultDescription}
                                            onChange={(e) => setSeriesDefaultDescription(e.target.value)}
                                            placeholder="Description for each episode (optional)"
                                            rows={2}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                                        />
                                    </div>

                                    {/* Default participants to carry over */}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Default Role Assignments</label>
                                        <p className="text-xs text-muted-foreground">
                                            People and roles to assign to every episode (e.g., programmer, engineer, producer)
                                        </p>
                                        <div className="space-y-2">
                                            {seriesDefaultParticipants.map((p, idx) => (
                                                <div key={idx} className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
                                                    <span className="flex-1 text-sm">
                                                        <span className="font-medium">{p.person_name}</span>
                                                        <span className="text-muted-foreground"> • {p.role}</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSeriesDefaultParticipants(seriesDefaultParticipants.filter((_, i) => i !== idx));
                                                        }}
                                                        className="text-muted-foreground hover:text-destructive"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="flex gap-2">
                                                <select
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    value=""
                                                    onChange={(e) => {
                                                        const personId = e.target.value;
                                                        if (!personId) return;
                                                        const person = allPeople?.find(p => p.id === personId);
                                                        if (!person) return;
                                                        // Prompt for role
                                                        const role = prompt('Enter role (e.g., programmer, engineer, producer):');
                                                        if (!role?.trim()) return;
                                                        setSeriesDefaultParticipants([
                                                            ...seriesDefaultParticipants,
                                                            { person_id: person.id, role: role.trim(), person_name: person.name },
                                                        ]);
                                                        e.target.value = '';
                                                    }}
                                                >
                                                    <option value="">Add person with role...</option>
                                                    {allPeople?.map(person => (
                                                        <option key={person.id} value={person.id}>
                                                            {person.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Default artists for series (for shows) */}
                                    {selectedType === 'show' && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Default Artists</label>
                                            <p className="text-xs text-muted-foreground">
                                                Artists to feature on every episode of this series
                                            </p>
                                            <div className="space-y-2">
                                                {seriesDefaultArtists.map((artist, idx) => {
                                                    const person = allPeople?.find(p => p.id === artist.person_id);
                                                    return (
                                                        <div key={idx} className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
                                                            <span className="flex-1 text-sm font-medium">
                                                                {person?.name || artist.person_id}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSeriesDefaultArtists(seriesDefaultArtists.filter((_, i) => i !== idx));
                                                                }}
                                                                className="text-muted-foreground hover:text-destructive"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                                <select
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    value=""
                                                    onChange={(e) => {
                                                        const personId = e.target.value;
                                                        if (!personId) return;
                                                        // Check if already added
                                                        if (seriesDefaultArtists.some(a => a.person_id === personId)) {
                                                            e.target.value = '';
                                                            return;
                                                        }
                                                        // Send the shape HappeningArtist actually declares. 'type' was not
                                                        // a field on it, and the missing name used to cost the entry its
                                                        // place in the series' own default_metadata.
                                                        const person = allPeople?.find(p => p.id === personId);
                                                        setSeriesDefaultArtists([
                                                            ...seriesDefaultArtists,
                                                            {
                                                                person_id: personId,
                                                                name: person?.name ?? null,
                                                                slug: person?.slug ?? '',
                                                            },
                                                        ]);
                                                        e.target.value = '';
                                                    }}
                                                >
                                                    <option value="">Add artist...</option>
                                                    {allPeople?.filter(p => p.type === 'artist').map(person => (
                                                        <option key={person.id} value={person.id}>
                                                            {person.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {/* Show template artists if using template and no custom artists selected */}
                                                {useTemplate && selectedTemplate?.metadata?.default_artists && seriesDefaultArtists.length === 0 && (
                                                    <p className="text-xs text-muted-foreground italic">
                                                        Template default artists will be used if none selected
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Round-robin programmer pool */}
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Programmer rotation</label>
                                        <p className="text-xs text-muted-foreground">
                                            Programmers are assigned to instances in turn. The 1st occurrence goes to #1,
                                            the 2nd to #2, and so on, wrapping around when the list runs out.
                                            Generating again later restarts from #1.
                                        </p>
                                        <div className="space-y-2">
                                            {seriesProgrammerPool.map((member, idx) => (
                                                <div key={`${member.source}-${member.id}`} className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
                                                    <span className="w-6 text-center text-xs font-semibold text-muted-foreground">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="flex-1 text-sm font-medium">
                                                        {member.name}
                                                        {member.source === 'users' && (
                                                            <span className="ml-2 text-xs text-muted-foreground">(staff)</span>
                                                        )}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        aria-label="Move up"
                                                        disabled={idx === 0}
                                                        onClick={() => {
                                                            const next = [...seriesProgrammerPool];
                                                            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                                            setSeriesProgrammerPool(next);
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    >
                                                        <ChevronUp className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-label="Move down"
                                                        disabled={idx === seriesProgrammerPool.length - 1}
                                                        onClick={() => {
                                                            const next = [...seriesProgrammerPool];
                                                            [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                                                            setSeriesProgrammerPool(next);
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    >
                                                        <ChevronDown className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-label="Remove"
                                                        onClick={() => setSeriesProgrammerPool(seriesProgrammerPool.filter((_, i) => i !== idx))}
                                                        className="text-muted-foreground hover:text-destructive"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}

                                            <div className="grid grid-cols-2 gap-2">
                                                <select
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    value=""
                                                    onChange={(e) => {
                                                        const personId = e.target.value;
                                                        if (!personId) return;
                                                        const person = allPeople?.find(p => p.id === personId);
                                                        if (person && !seriesProgrammerPool.some(m => m.source === 'people' && m.id === personId)) {
                                                            setSeriesProgrammerPool([
                                                                ...seriesProgrammerPool,
                                                                { source: 'people', id: personId, name: person.name ?? personId },
                                                            ]);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                >
                                                    <option value="">Add person...</option>
                                                    {allPeople?.map(person => (
                                                        <option key={person.id} value={person.id}>
                                                            {person.name}
                                                        </option>
                                                    ))}
                                                </select>

                                                <select
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    value=""
                                                    onChange={(e) => {
                                                        const userId = e.target.value;
                                                        if (!userId) return;
                                                        const user = users?.find(u => u.id === userId);
                                                        if (user && !seriesProgrammerPool.some(m => m.source === 'users' && m.id === userId)) {
                                                            setSeriesProgrammerPool([
                                                                ...seriesProgrammerPool,
                                                                { source: 'users', id: userId, name: user.name ?? user.email ?? userId },
                                                            ]);
                                                        }
                                                        e.target.value = '';
                                                    }}
                                                >
                                                    <option value="">Add staff user...</option>
                                                    {users?.map(user => (
                                                        <option key={user.id} value={user.id}>
                                                            {user.name || user.email}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {seriesProgrammerPool.length > 0 && (
                                                <>
                                                    <div className="space-y-1 rounded-md border bg-muted/20 p-2">
                                                        <label className="text-xs font-medium" htmlFor="programmer-rotation-start">
                                                            Start the rotation with
                                                        </label>
                                                        {/* Picking a person rather than typing an index: the stored value is a
                                                            0-based offset, which is the wrong thing to show a user. */}
                                                        <select
                                                            id="programmer-rotation-start"
                                                            value={Math.min(seriesProgrammerOffset, seriesProgrammerPool.length - 1)}
                                                            onChange={(e) => setSeriesProgrammerOffset(Number(e.target.value))}
                                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                        >
                                                            {seriesProgrammerPool.map((member, idx) => (
                                                                <option key={`${member.source}-${member.id}`} value={idx}>
                                                                    {member.name}{idx === 0 ? ' (first in the list)' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <p className="text-xs text-muted-foreground">
                                                            Everyone still takes their turn in the order above — this only picks who
                                                            goes first. Running a second series over the same dates? Start it with a
                                                            different person so nobody is booked for both slots on the same day.
                                                        </p>
                                                    </div>

                                                    <p className="text-xs text-muted-foreground">
                                                        {seriesProgrammerPool.length} programmer{seriesProgrammerPool.length === 1 ? '' : 's'} in rotation.
                                                        Check the date preview below to see how many slots each will take.
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Happening type display */}
                            <div className="rounded-md border bg-muted/30 p-3">
                                <p className="text-sm">
                                    <span className="font-medium">Type:</span>{' '}
                                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                        {HAPPENING_TYPE_META[selectedType].label}
                                    </span>
                                    {creationMode === 'series' && selectedType === 'show' && (
                                        <>
                                            {' '}&bull;{' '}
                                            <span className="text-muted-foreground">
                                                {seriesDefaultShowType === 'resident'
                                                    ? `Resident show, starting at episode #${seriesStartingEpisode}`
                                                    : 'Guest show (not numbered)'}
                                            </span>
                                        </>
                                    )}
                                </p>
                            </div>

                            {/* Recurrence pattern picker */}
                            <RecurrencePatternPicker
                                pattern={recurrencePattern}
                                onChange={setRecurrencePattern}
                                startDate={recurrenceStartDate}
                                endDate={recurrenceEndDate}
                                onStartDateChange={setRecurrenceStartDate}
                                onEndDateChange={setRecurrenceEndDate}
                                startTime={recurrenceStartTime}
                                onStartTimeChange={setRecurrenceStartTime}
                                organizationPresets={orgRecurrencePresets}
                            />

                            <DialogFooter className="flex-col gap-2 sm:flex-row">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setDialogStep('type')}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    onClick={async () => {
                                        if (!recurrencePattern || !recurrenceStartDate || !recurrenceEndDate || !recurrenceStartTime) {
                                            return;
                                        }

                                        setIsGenerating(true);
                                        try {
                                            if (creationMode === 'series') {
                                                if (!seriesName.trim()) return;
                                                // Build participants list from template role assignments (both people and staff)
                                                // and merge with any manually added series participants
                                                const templateParticipants: Array<{ person_id?: string | null; user_id?: string | null; role: string }> = [];
                                                if (useTemplate && selectedTemplateId && Object.keys(templateRoleAssignments).length > 0) {
                                                    for (const [role, assignments] of Object.entries(templateRoleAssignments)) {
                                                        for (const assignment of assignments) {
                                                            if (assignment.source === 'users') {
                                                                templateParticipants.push({ user_id: assignment.id, role });
                                                            } else {
                                                                templateParticipants.push({ person_id: assignment.id, role });
                                                            }
                                                        }
                                                    }
                                                }
                                                const allParticipants = [
                                                    ...seriesDefaultParticipants.map(p => ({ person_id: p.person_id, role: p.role })),
                                                    ...templateParticipants,
                                                ];
                                                // Get defaults from template metadata if available
                                                const templatePlaceId = useTemplate && selectedTemplate?.metadata?.place_id as string | undefined;
                                                const templateArtists = useTemplate && selectedTemplate?.metadata?.default_artists as HappeningArtist[] | undefined;
                                                // Create series and generate instances
                                                const series = await createHappeningSeries({
                                                    name: seriesName.trim(),
                                                    happening_type: selectedType,
                                                    template_id: useTemplate ? selectedTemplateId : undefined,
                                                    default_title: seriesDefaultTitle.trim() || undefined,  // If empty, generateSeriesInstances will use series.name
                                                    default_description: seriesDefaultDescription.trim() || undefined,
                                                    default_show_type: selectedType === 'show' ? seriesDefaultShowType : undefined,
                                                    starting_episode_number: selectedType === 'show' ? seriesStartingEpisode : undefined,
                                                    default_duration_minutes: useTemplate && selectedTemplate ? selectedTemplate.default_duration_minutes : undefined,
                                                    default_place_id: templatePlaceId || undefined,
                                                    default_metadata: seriesDefaultArtists.length > 0 || templateArtists?.length || seriesDefaultGenres.length > 0
                                                        ? {
                                                            artists: seriesDefaultArtists.length > 0 ? seriesDefaultArtists : (templateArtists || undefined),
                                                            genres: seriesDefaultGenres.length > 0 ? seriesDefaultGenres : undefined,
                                                        }
                                                        : undefined,
                                                    default_participants: allParticipants.length > 0
                                                        ? allParticipants
                                                        : undefined,
                                                    allow_bulk_edits: seriesAllowBulkEdits,
                                                    rotating_participants: seriesProgrammerPool.length > 0
                                                        ? [{
                                                            role: PROGRAMMER_ROTATION_ROLE,
                                                            members: seriesProgrammerPool.map(m =>
                                                                m.source === 'users' ? { user_id: m.id } : { person_id: m.id }
                                                            ),
                                                            // Clamped: removing people after choosing a start can leave the
                                                            // stored index past the end of the pool.
                                                            start_offset: seriesProgrammerOffset % seriesProgrammerPool.length,
                                                        }]
                                                        : undefined,
                                                    default_artist_ids: seriesDefaultArtists.length > 0
                                                        ? seriesDefaultArtists.map(a => a.person_id)
                                                        : (templateArtists?.map(a => a.person_id) || undefined),
                                                    recurrence_pattern: recurrencePattern,
                                                    recurrence_start_date: recurrenceStartDate,
                                                    recurrence_end_date: recurrenceEndDate,
                                                    default_start_time: recurrenceStartTime + ':00',
                                                    generate_instances: true,
                                                });

                                            } else {
                                                // Batch generate independent happenings
                                                // Flatten role assignments to array format for template application
                                                const roleAssignments: RoleAssignment[] = [];
                                                if (useTemplate && selectedTemplateId && Object.keys(templateRoleAssignments).length > 0) {
                                                    for (const [role, assignments] of Object.entries(templateRoleAssignments)) {
                                                        for (const assignment of assignments) {
                                                            if (assignment.source === 'users') {
                                                                roleAssignments.push({ role, user_id: assignment.id });
                                                            } else {
                                                                roleAssignments.push({ role, person_id: assignment.id });
                                                            }
                                                        }
                                                    }
                                                }

                                                const happenings = await batchGenerateHappenings({
                                                    recurrence_pattern: recurrencePattern,
                                                    start_date: recurrenceStartDate,
                                                    end_date: recurrenceEndDate,
                                                    start_time: recurrenceStartTime + ':00',
                                                    happening_data: {
                                                        type: selectedType,
                                                        title: useTemplate && selectedTemplate ? selectedTemplate.name : undefined,
                                                        status: 'draft',
                                                        is_public: false,
                                                        metadata: useTemplate && selectedTemplateId ? {
                                                            template_id: selectedTemplateId,
                                                        } : undefined,
                                                    },
                                                    role_assignments: roleAssignments.length > 0 ? roleAssignments : undefined,
                                                });
                                            }
                                            queryClient.invalidateQueries({ queryKey: ['happenings'] });
                                            queryClient.invalidateQueries({ queryKey: ['tasks'] });
                                            closeDialog();
                                        } catch (error) {
                                            console.error('Failed to create happenings:', error);
                                        } finally {
                                            setIsGenerating(false);
                                        }
                                    }}
                                    disabled={
                                        isGenerating ||
                                        !recurrencePattern ||
                                        !recurrenceStartDate ||
                                        !recurrenceEndDate ||
                                        !recurrenceStartTime ||
                                        (creationMode === 'series' && !seriesName.trim()) ||
                                        ((creationMode === 'batch' || creationMode === 'series') && !skipRequiredRoles && useTemplate && selectedTemplate &&
                                            selectedTemplate.required_roles.some(role => !isRequiredRoleSatisfied(role)))
                                    }
                                >
                                    {isGenerating ? (
                                        <>
                                            Generating...
                                            <span className="ml-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                                        </>
                                    ) : (
                                        <>
                                            {creationMode === 'series' ? 'Create Series' : 'Generate Happenings'}
                                            <Sparkles className="ml-2 h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6" ref={dialogFormRef}>
                            {/* Sticky Header Section */}
                            <div ref={dialogHeaderRef} className="sticky top-0 z-30 px-6 pt-4 pb-3 bg-background border-b shadow-sm" style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'hsl(var(--background))',
                            }}>
                                <DialogHeader>
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                                            {typeMeta.label}
                                        </span>
                                        {editingHappening ? 'Editing existing' : 'Creating new'}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <DialogTitle>
                                            {editingHappening ? `Edit ${editingHappening.title}` : `Add ${typeMeta.label}`}
                                        </DialogTitle>
                                        {editingHappening && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    const url = `${window.location.origin}/happenings?id=${editingHappening.id}`;
                                                    await navigator.clipboard.writeText(url);
                                                    setCopiedEditLink(true);
                                                    setTimeout(() => setCopiedEditLink(false), 4000);
                                                }}
                                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                                title="Copy internal admin link — do NOT share with artists"
                                            >
                                                {copiedEditLink ? (
                                                    <span className="text-xs text-amber-600">Copied — admin-only link, do not share with artists!</span>
                                                ) : (
                                                    <>
                                                        <Lock className="h-3.5 w-3.5" />
                                                        <span>Internal link</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        {editingHappening && (
                                            <InviteLinkButton
                                                happening={editingHappening}
                                                personId={showFillerPersonId}
                                                alternates={coArtistProfileAlternates}
                                                appearance="inline"
                                            />
                                        )}
                                        {editingHappening && (
                                            <EmailInviteButton
                                                happening={editingHappening}
                                                personId={formState.combinedInviteArtistId ?? showFillerPersonId}
                                                onQueued={(reminder, happening) => {
                                                    setCombinedReminderForHappening(reminder);
                                                    setShowHasBeenInvited(true);
                                                    // Keep the minted link in the dialog so saving does not mint a second one.
                                                    setEditingHappening(happening);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <DialogDescription>{typeMeta.description}</DialogDescription>
                                </DialogHeader>

                                {/* Status and Quick Actions */}
                                <div className="mt-4 pb-3">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="flex-1">
                                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                                                <select
                                                    value={formState.status}
                                                    onChange={handleFieldChange('status')}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium"
                                                >
                                                    <option value="draft">Draft</option>
                                                    <option value="scheduled">Scheduled</option>
                                                    <option value="confirmed">Confirmed</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="cancelled">Cancelled</option>
                                                </select>
                                            </div>
                                            {editingHappening && (() => {
                                                const taskCounts = taskCountsByHappening.get(editingHappening.id) || { completed: 0, total: 0 };
                                                const hasIncomplete = taskCounts.completed < taskCounts.total;
                                                return (
                                                    <div className="flex-1">
                                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Tasks</label>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const tasksSection = document.getElementById('section-tasks');
                                                                if (tasksSection) {
                                                                    tasksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                }
                                                            }}
                                                            className={cn(
                                                                "w-full rounded-md border px-3 py-1.5 text-sm font-medium text-left hover:bg-accent transition-colors",
                                                                hasIncomplete ? "bg-orange-badge text-orange-badge-foreground border border-orange-badge-foreground/20" : "bg-green-badge text-green-badge-foreground border border-green-badge-foreground/20"
                                                            )}
                                                        >
                                                            {taskCounts.total > 0 ? (
                                                                <span>{taskCounts.completed}/{taskCounts.total} completed</span>
                                                            ) : (
                                                                <span className="text-muted-foreground">No tasks</span>
                                                            )}
                                                            {taskCounts.total > 0 && (
                                                                <span className="ml-2 text-xs">→</span>
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex flex-wrap items-center gap-4 pt-6">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        id="is-public-header"
                                                        type="checkbox"
                                                        checked={formState.isPublic}
                                                        onChange={handleCheckboxChange('isPublic')}
                                                        className="h-4 w-4"
                                                    />
                                                    <label htmlFor="is-public-header" className="text-sm font-medium">
                                                        Public
                                                    </label>
                                                </div>
                                                {selectedType === 'show' && (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            id="is-staff-pick-header"
                                                            type="checkbox"
                                                            checked={formState.isStaffPick}
                                                            onChange={handleCheckboxChange('isStaffPick')}
                                                            className="h-4 w-4"
                                                        />
                                                        <label
                                                            htmlFor="is-staff-pick-header"
                                                            className="text-sm font-medium"
                                                            title="Shows a ✨ Staff Pick badge on the public site"
                                                        >
                                                            ✨ Staff pick
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <FormSectionNav items={formSectionNavItems} className="mt-3" />
                            </div>

                            <div className="px-6 pb-6 space-y-10">
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-input px-4 py-3 text-sm text-muted-foreground">
                                    <div className="space-y-1">
                                        <p className="font-medium text-foreground">
                                            Admins can save with minimal info as a stub
                                        </p>
                                        <p>Artists will see required fields when they use the stub link.</p>
                                    </div>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => setDialogStep('type')}>
                                        <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                                        Change type
                                    </Button>
                                </div>

                                {/* Basic Information Section */}
                                <div id="section-basic" className="scroll-mt-4">
                                    <FormSectionHeading icon={FileText} title="Basic information" />

                                    {/* Show Type (Radio Shows only) */}
                                    {selectedType === 'show' && (
                                        <div className="space-y-3 mb-6">
                                            <label className="text-sm font-medium">Show Type</label>
                                            <div className="grid gap-3 sm:grid-cols-2">
                                                {SHOW_TYPE_OPTIONS.map((option) => (
                                                    <button
                                                        type="button"
                                                        key={option.value}
                                                        onClick={() => {
                                                            setFormState((prev) => {
                                                                const newState = { ...prev, showType: option.value };
                                                                // Reset slug when switching show types
                                                                setSlugManuallyEdited(false);
                                                                // If switching to resident and we have a title, generate slug
                                                                if (option.value === 'resident' && prev.title) {
                                                                    newState.slug = generateSlugWithDateAndTime(prev.title, prev.startTime, prev.episodeNumber);
                                                                } else if (option.value === 'guest') {
                                                                    // For guest shows, generate slug with date and time
                                                                    newState.slug = generateSlugWithDateAndTime(prev.title, prev.startTime);
                                                                }
                                                                return newState;
                                                            });
                                                        }}
                                                        className={`rounded-lg border p-3 text-left transition ${formState.showType === option.value
                                                            ? 'border-primary bg-primary/5'
                                                            : 'border-input hover:border-primary/60'
                                                            }`}
                                                    >
                                                        <p className="font-medium">{option.label}</p>
                                                        <p className="text-xs text-muted-foreground">{option.description}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Series actions. These live in the edit form because that is where
                                        series-wide problems get noticed — see the numbering fix. */}
                                    {editingHappening?.series_id && (
                                        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                                            <span className="text-sm text-muted-foreground">
                                                Part of a series
                                                {!seriesAllowsBulkEdits && ' — edits apply to this show only'}
                                            </span>
                                            <div className="ml-auto flex flex-wrap gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={seriesBulkEditToggle.isPending}
                                                    onClick={() => {
                                                        if (!editingHappening?.series_id) return;
                                                        seriesBulkEditToggle.mutate({
                                                            seriesId: editingHappening.series_id,
                                                            allow: !seriesAllowsBulkEdits,
                                                        });
                                                    }}
                                                >
                                                    {seriesAllowsBulkEdits ? 'Disable bulk edits' : 'Enable bulk edits'}
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setExtendSeriesDialogOpen(true)}
                                                >
                                                    Add more dates
                                                </Button>
                                                {isResidentShow && (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setRenumberDialogOpen(true)}
                                                    >
                                                        Renumber episodes
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Title and Episode Number (for resident shows) */}
                                    {selectedType === 'show' && isResidentShow && (
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">
                                                    Show Title <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    required
                                                    value={formState.title}
                                                    onChange={handleFieldChange('title')}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                    placeholder="Weekly Rinse"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">
                                                    Episode Number {(editingHappening?.series_id || creationMode === 'series') && <span className="text-destructive">*</span>}
                                                </label>
                                                <NumberInput
                                                    min={1}
                                                    allowEmpty={false}
                                                    required={!!editingHappening?.series_id || creationMode === 'series'}
                                                    value={formState.episodeNumber}
                                                    onChange={(n) => {
                                                        const numValue = n ?? 1;
                                                        setFormState((prev) => {
                                                            if (!slugManuallyEdited) {
                                                                const isResident = selectedType === 'show' && prev.showType === 'resident';
                                                                if (isResident && prev.title) {
                                                                    const newSlug = generateSlugWithDateAndTime(prev.title, prev.startTime, numValue);
                                                                    return { ...prev, episodeNumber: numValue, slug: newSlug };
                                                                }
                                                            }
                                                            return { ...prev, episodeNumber: numValue };
                                                        });
                                                    }}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                    placeholder="1"
                                                />
                                                <p className="text-xs text-muted-foreground">Auto-filled based on existing shows</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Title (for guest shows and other types) */}
                                    {(!isResidentShow || selectedType !== 'show') && (
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium">
                                                {selectedType === 'performance'
                                                    ? 'Performance Name'
                                                    : selectedType === 'tech'
                                                        ? 'Tech / Internal Name'
                                                        : 'Show Title'}{' '}
                                                {(isResidentShow || selectedType === 'performance' || selectedType === 'tech') && <span className="text-destructive">*</span>}
                                            </label>
                                            <input
                                                required={isResidentShow || selectedType === 'performance' || selectedType === 'tech'}
                                                value={formState.title}
                                                onChange={handleFieldChange('title')}
                                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                placeholder={selectedType === 'show' && !isResidentShow ? 'Leave empty to use artist name' : 'Give it a name'}
                                            />
                                            {selectedType === 'show' && !isResidentShow && (
                                                <p className="text-xs text-muted-foreground">
                                                    Optional. If left empty, will use the artist name as the title.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Slug */}
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between text-sm font-medium">
                                            <label>Slug</label>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSlugManuallyEdited(false);
                                                        // Regenerate slug based on current title and context
                                                        if (selectedType === 'show' && formState.showType === 'resident') {
                                                            const newSlug = generateSlugWithDateAndTime(formState.title, formState.startTime, formState.episodeNumber);
                                                            setFormState(prev => ({ ...prev, slug: newSlug }));
                                                        } else if (formState.title.trim()) {
                                                            // For guest shows and other types, include date and time
                                                            const newSlug = generateSlugWithDateAndTime(formState.title, formState.startTime);
                                                            setFormState(prev => ({ ...prev, slug: newSlug }));
                                                        }
                                                    }}
                                                    className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                                                >
                                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                    Regenerate
                                                </button>
                                                <span className="text-xs text-muted-foreground">Auto-generated</span>
                                            </div>
                                        </div>
                                        <input
                                            value={formState.slug}
                                            onChange={handleFieldChange('slug')}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2"
                                            placeholder="auto-generated-slug"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium">
                                                Description <span className="text-destructive">*</span>
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowMarkdownGuide(!showMarkdownGuide)}
                                                className="text-xs text-muted-foreground hover:text-foreground"
                                            >
                                                Markdown supported
                                            </button>
                                        </div>
                                        <textarea
                                            value={formState.description}
                                            onChange={handleFieldChange('description')}
                                            rows={6}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                                            placeholder="**Bold**, *italic*, [links](url), ## Heading, etc."
                                        />
                                        {showMarkdownGuide && <MarkdownGuide />}
                                        <p className="text-xs text-muted-foreground">
                                            Required for publishing. Markdown formatting supported.
                                        </p>
                                    </div>

                                </div>

                                {/* Schedule Section */}
                                <div id="section-schedule" className="scroll-mt-4">
                                    <FormSectionHeading icon={Calendar} title="Schedule" />

                                    {/* Date/Time and Duration */}
                                    {selectedType === 'show' || selectedType === 'performance' || selectedType === 'tech' ? (
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">
                                                    Start Time <span className="text-destructive">*</span>
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    required
                                                    value={formState.startTime}
                                                    onChange={handleFieldChange('startTime')}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                />
                                                <p className="text-xs text-muted-foreground">Required. Admin-set, locked for artists</p>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">
                                                    Duration (minutes) <span className="text-destructive">*</span>
                                                </label>
                                                <NumberInput
                                                    min={15}
                                                    max={480}
                                                    allowEmpty={false}
                                                    required
                                                    value={formState.durationMinutes}
                                                    onChange={(n) =>
                                                        setFormState((prev) => ({
                                                            ...prev,
                                                            durationMinutes: n ?? 60,
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                />
                                                <p className="text-xs text-muted-foreground">Required. End time will be calculated automatically</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid gap-4 sm:grid-cols-3">
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">Start Time</label>
                                                <input
                                                    type="datetime-local"
                                                    value={formState.startTime}
                                                    onChange={handleFieldChange('startTime')}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">End Time</label>
                                                <input
                                                    type="datetime-local"
                                                    value={formState.endTime}
                                                    onChange={handleFieldChange('endTime')}
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium">Duration (minutes)</label>
                                                <NumberInput
                                                    min={15}
                                                    max={480}
                                                    allowEmpty={false}
                                                    value={formState.durationMinutes}
                                                    onChange={(n) =>
                                                        setFormState((prev) => ({
                                                            ...prev,
                                                            durationMinutes: n ?? 60,
                                                        }))
                                                    }
                                                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Timezone */}
                                    <div className="space-y-2 rounded-lg border border-input bg-muted/30 p-4">
                                        <div className="flex items-center gap-2">
                                            <Clock className="h-4 w-4" />
                                            <label className="text-sm font-medium">
                                                Timezone
                                            </label>
                                        </div>
                                        <select
                                            value={formState.timezone || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    timezone: value === '' ? null : value,
                                                }));
                                                // If timezone changes, we need to re-convert the dates
                                                // This is handled by recalculating when the form submits
                                            }}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                            <option value="">Use organization default ({orgTimezone})</option>
                                            {COMMON_TIMEZONES.map((tz) => (
                                                <option key={tz.value} value={tz.value}>
                                                    {tz.label} {tz.value === orgTimezone ? '(same as org default)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {formState.timezone && formState.timezone !== orgTimezone && (
                                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                <span>This happening will use <strong>{formState.timezone}</strong> instead of the organization default ({orgTimezone})</span>
                                            </p>
                                        )}
                                        {!formState.timezone && (
                                            <p className="text-xs text-muted-foreground">
                                                Dates and times will be displayed and edited in the organization default timezone ({orgTimezone})
                                            </p>
                                        )}
                                    </div>

                                    {/* Location */}
                                    <div className="space-y-2 rounded-lg border border-input bg-muted/30 p-4">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4" />
                                            <label className="text-sm font-medium">
                                                Location <span className="text-destructive">*</span>
                                            </label>
                                        </div>
                                        <select
                                            value={formState.placeId || ''}
                                            onChange={(e) => {
                                                const newPlaceId = e.target.value || null;
                                                if (editingHappening && editingHappening.place_id && newPlaceId !== editingHappening.place_id) {
                                                    if (!window.confirm('Changing the location may affect equipment availability. Are you sure you want to change it?')) {
                                                        return;
                                                    }
                                                }
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    placeId: newPlaceId,
                                                }));
                                            }}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        >
                                            <option value="">Select location (required)</option>
                                            {places.map((place) => (
                                                <option key={place.id} value={place.id}>
                                                    {place.name}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-muted-foreground">
                                            Location determines equipment availability.
                                        </p>
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div id="section-content" className="scroll-mt-4">
                                    <FormSectionHeading icon={ListMusic} title="Content" />

                                    {/* Artists Selection — hidden for tech/internal happenings.
                                        Tech happenings use the role-based participants section below,
                                        which already supports staff users + people of any type. */}
                                    {selectedType !== 'tech' && (
                                    <div className="space-y-3 mb-6">
                                        <div>
                                            <label className="text-sm font-medium">
                                                Artists <span className="text-destructive">*</span>
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                At least one artist required. Search existing artists or add new ones first.
                                            </p>
                                        </div>
                                        <div className="relative">
                                            <input
                                                value={artistSearch}
                                                onChange={(e) => {
                                                    setArtistSearch(e.target.value);
                                                    setShowArtistDropdown(true);
                                                    setCreatingNewArtist(false);
                                                    setNewArtistEmail('');
                                                }}
                                                onFocus={() => setShowArtistDropdown(true)}
                                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                placeholder="Search artists..."
                                            />
                                            {showArtistDropdown && (artistResults.length > 0 || (artistSearch.trim().length >= 2 && !isSearchingArtists)) && (
                                                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-background shadow-lg">
                                                    {artistResults.map((person) => (
                                                        <button
                                                            key={person.id}
                                                            type="button"
                                                            onClick={() => handleAddArtist(person)}
                                                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted"
                                                        >
                                                            {person.picture_url && (
                                                                <img
                                                                    src={person.picture_url}
                                                                    alt=""
                                                                    className="h-6 w-6 rounded-full object-cover"
                                                                />
                                                            )}
                                                            <span>{person.name}</span>
                                                            <span className="text-xs text-muted-foreground">/{person.slug}</span>
                                                        </button>
                                                    ))}
                                                    {artistSearch.trim().length >= 2 && artistResults.length === 0 && !isSearchingArtists && (
                                                        <>
                                                            {!creatingNewArtist ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCreatingNewArtist(true)}
                                                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted text-primary"
                                                                >
                                                                    + Create &lsquo;{artistSearch.trim()}&rsquo; as new artist
                                                                </button>
                                                            ) : (
                                                                <div className="px-3 py-2 space-y-2">
                                                                    <p className="text-xs text-muted-foreground">
                                                                        Create &lsquo;{artistSearch.trim()}&rsquo; as a new artist
                                                                    </p>
                                                                    <input
                                                                        type="email"
                                                                        value={newArtistEmail}
                                                                        onChange={(e) => setNewArtistEmail(e.target.value)}
                                                                        className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                                                        placeholder="Email address"
                                                                        autoFocus
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Escape') {
                                                                                setCreatingNewArtist(false);
                                                                                setNewArtistEmail('');
                                                                            }
                                                                        }}
                                                                    />
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            disabled={!newArtistEmail.trim() || isCreatingArtist}
                                                                            onClick={async () => {
                                                                                setIsCreatingArtist(true);
                                                                                try {
                                                                                    const person = await createPerson({
                                                                                        name: artistSearch.trim(),
                                                                                        type: 'artist',
                                                                                        email: newArtistEmail.trim(),
                                                                                        generateInviteLink: true,
                                                                                    });
                                                                                    queryClient.invalidateQueries({ queryKey: ['people'] });
                                                                                    handleAddArtist(person);
                                                                                } catch (err) {
                                                                                    console.error('Failed to create artist:', err);
                                                                                } finally {
                                                                                    setIsCreatingArtist(false);
                                                                                }
                                                                            }}
                                                                            className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                                                        >
                                                                            {isCreatingArtist ? 'Creating...' : 'Create'}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setCreatingNewArtist(false);
                                                                                setNewArtistEmail('');
                                                                            }}
                                                                            className="rounded-md border px-3 py-1 text-xs hover:bg-muted"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {formState.selectedArtists.length > 0 && (
                                            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleReorderArtists}>
                                                <SortableContext items={sortableArtistIds} strategy={horizontalListSortingStrategy}>
                                                    <div className="flex flex-wrap gap-2">
                                                        {formState.selectedArtists.map((artist) => {
                                                            const currentPerson = allPeople.find(p => p.id === artist.person_id);
                                                            const displayName = currentPerson?.name || artist.name || artist.slug || 'Unnamed artist';
                                                            const displayPicture = currentPerson?.picture_url || artist.picture_url;

                                                            return (
                                                                <SortableArtistChip
                                                                    key={artist.person_id}
                                                                    artist={artist}
                                                                    displayName={displayName}
                                                                    displayPicture={displayPicture}
                                                                    onRemove={handleRemoveArtist}
                                                                    onNavigate={(id) => navigate(`/people?edit=${id}`)}
                                                                    onUsePhoto={handleUseArtistPhoto}
                                                                    showUsePhoto={!!displayPicture && !formState.imageUrl}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </SortableContext>
                                            </DndContext>
                                        )}
                                    </div>
                                    )}

                                    {/* Artist Invites Section - shown right after artist selection */}
                                    {formState.selectedArtists.length > 0 && (
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                <Mail className="h-3.5 w-3.5" />
                                                Invite the artists
                                            </h4>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Pick how they get their links. Most programmers should leave this on the first option.
                                            </p>
                                        </div>
                                    <div className="space-y-3 rounded-md border border-input bg-muted/30 p-4">
                                                {/* Status of a combined invite already queued or sent for this show */}
                                                {combinedReminderForHappening && (
                                                    <div className={`rounded-lg border p-3 ${combinedReminderForHappening.sent_at ? 'bg-emerald-50 border-emerald-200' : combinedReminderForHappening.error_message ? 'bg-destructive/10 border-destructive/30' : 'bg-amber-50 border-amber-200'}`}>
                                                        <p className={`text-xs ${combinedReminderForHappening.sent_at ? 'text-emerald-800' : combinedReminderForHappening.error_message ? 'text-destructive' : 'text-amber-800'}`}>
                                                            {combinedReminderForHappening.sent_at ? (
                                                                <>
                                                                    <strong>Combined invite email sent</strong>
                                                                    {' '}to {combinedReminderForHappening.recipient_email}
                                                                    {' '}on {new Date(combinedReminderForHappening.sent_at).toLocaleString()}.
                                                                </>
                                                            ) : combinedReminderForHappening.error_message ? (
                                                                <>
                                                                    <strong>Combined invite could not be sent</strong>
                                                                    {' '}to {combinedReminderForHappening.recipient_email}:
                                                                    {' '}{combinedReminderForHappening.error_message}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <strong>Combined invite queued</strong>
                                                                    {' '}for {combinedReminderForHappening.recipient_email} — not yet sent.
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Saving never re-sends an invite, so re-inviting is this button (same as the header's). */}
                                                {editingHappening && combinedInviteCandidates.length > 0 && (
                                                    <EmailInviteButton
                                                        happening={editingHappening}
                                                        personId={formState.combinedInviteArtistId ?? showFillerPersonId}
                                                        appearance="button"
                                                        label={combinedReminderForHappening ? 'Resend combined invite' : 'Send combined invite now'}
                                                        onQueued={(reminder, happening) => {
                                                            setCombinedReminderForHappening(reminder);
                                                            setShowHasBeenInvited(true);
                                                            setEditingHappening(happening);
                                                        }}
                                                    />
                                                )}


                                                {/*
                                                  * Strategy first, link generation second. Whether the show needs a
                                                  * stub link is a consequence of choosing to email, not a question
                                                  * the programmer should have to answer before the options appear —
                                                  * burying the radios behind that checkbox is most of why combined
                                                  * went unused.
                                                  */}
                                                <div className="grid gap-2 sm:grid-cols-3">
                                                    {/* Option: Combined */}
                                                    <label
                                                        htmlFor="strategy-combined"
                                                        className={`cursor-pointer rounded-lg border p-3 transition ${formState.inviteEmailStrategy === 'combined' ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-input hover:border-primary/40'}`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <input
                                                                id="strategy-combined"
                                                                type="radio"
                                                                name="inviteStrategy"
                                                                className="mt-0.5"
                                                                checked={formState.inviteEmailStrategy === 'combined'}
                                                                onChange={() => {
                                                                    setFormState(prev => ({
                                                                        ...prev,
                                                                        inviteEmailStrategy: 'combined',
                                                                        // Emailing implies a show link, so mint one rather than
                                                                        // making the programmer find the checkbox first. Never
                                                                        // when a token already exists — that would invalidate
                                                                        // links already sitting in someone's inbox.
                                                                        generateStubLink: editingHappening?.metadata?.stub_token
                                                                            ? prev.generateStubLink
                                                                            : true,
                                                                        combinedInviteArtistId:
                                                                            prev.combinedInviteArtistId || combinedInviteCandidates[0]?.person.id || null,
                                                                    }));
                                                                }}
                                                            />
                                                            <div className="flex-1">
                                                                <span className="block text-sm font-medium">One link, both jobs</span>
                                                                <span className="mt-0.5 inline-block rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                                                    Recommended
                                                                </span>
                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                    A single email with one link where {combinedInviteCandidates.find(c => c.person.id === formState.combinedInviteArtistId)?.artist.name ?? combinedInviteCandidates[0]?.artist.name ?? 'the artist'} completes their profile and fills in the show details.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </label>

                                                    {/* Option: Separate */}
                                                    <label
                                                        htmlFor="strategy-separate"
                                                        className={`cursor-pointer rounded-lg border p-3 transition ${formState.inviteEmailStrategy === 'separate' ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-input hover:border-primary/40'}`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <input
                                                                id="strategy-separate"
                                                                type="radio"
                                                                name="inviteStrategy"
                                                                className="mt-0.5"
                                                                checked={formState.inviteEmailStrategy === 'separate'}
                                                                onChange={() => setFormState(prev => ({
                                                                    ...prev,
                                                                    inviteEmailStrategy: 'separate',
                                                                    generateStubLink: editingHappening?.metadata?.stub_token
                                                                        ? prev.generateStubLink
                                                                        : true,
                                                                }))}
                                                            />
                                                            <div className="flex-1">
                                                                <span className="block text-sm font-medium">Two emails</span>
                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                    One email about the show, one about the profile. Use when different people handle each.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </label>

                                                    {/* Option: None */}
                                                    <label
                                                        htmlFor="strategy-none"
                                                        className={`cursor-pointer rounded-lg border p-3 transition ${formState.inviteEmailStrategy === 'none' ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-input hover:border-primary/40'}`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <input
                                                                id="strategy-none"
                                                                type="radio"
                                                                name="inviteStrategy"
                                                                className="mt-0.5"
                                                                checked={formState.inviteEmailStrategy === 'none'}
                                                                onChange={() => setFormState(prev => ({ ...prev, inviteEmailStrategy: 'none' }))}
                                                            />
                                                            <div className="flex-1">
                                                                <span className="block text-sm font-medium">Don't email now</span>
                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                    You can still copy links by hand from this dialog.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </label>
                                                </div>

                                                {/* Multi-artist: who holds the show link */}
                                                {formState.inviteEmailStrategy === 'combined' && combinedInviteCandidates.length > 1 && (
                                                    <div className="rounded border border-border bg-muted/50 p-3">
                                                        <label className="text-xs font-medium block mb-2">
                                                            Who should fill in the show details?
                                                        </label>
                                                        <select
                                                            value={formState.combinedInviteArtistId || ''}
                                                            onChange={(e) => setFormState(prev => ({
                                                                ...prev,
                                                                combinedInviteArtistId: e.target.value
                                                            }))}
                                                            className="w-full text-sm border rounded px-2 py-1"
                                                        >
                                                            {combinedInviteCandidates.map(({ artist, person, missingFields }) => (
                                                                <option key={person.id} value={person.id}>
                                                                    {artist.name}{missingFields.length > 0 ? ' — profile incomplete' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <p className="text-xs text-muted-foreground mt-2">
                                                            Everyone else gets a profile-only email.
                                                        </p>
                                                    </div>
                                                )}

                                                {formState.inviteEmailStrategy === 'combined' && everythingAlreadyComplete && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Everything's already filled in — the email still goes out, and the artist can review both.
                                                    </p>
                                                )}

                                                {/* Advanced: regenerating an existing link invalidates the old one */}
                                                <div className="flex items-start gap-3 border-t border-border pt-3">
                                                    <input
                                                        id="stub-link"
                                                        type="checkbox"
                                                        className="mt-1"
                                                        checked={formState.generateStubLink}
                                                        onChange={handleCheckboxChange('generateStubLink')}
                                                    />
                                                    <div className="space-y-1 text-sm flex-1">
                                                        <label htmlFor="stub-link" className="font-medium cursor-pointer">
                                                            {editingHappening && editingHappening.metadata?.stub_token
                                                                ? 'Regenerate artist-facing link'
                                                                : 'Generate artist-facing link'}
                                                        </label>
                                                        <p className="text-muted-foreground text-xs">
                                                            {editingHappening && editingHappening.metadata?.stub_token
                                                                ? 'Generate a new stub link. This will invalidate any previously sent links.'
                                                                : "We'll create a locked-down link so the artist can fill in their show details."}
                                                        </p>
                                                    </div>
                                                </div>
                                    </div>

                                    {/* Completion Notification Roles */}
                                    {formState.generateStubLink && uniqueParticipantRolesForNotification.length > 0 && (
                                        <div className={`space-y-3 rounded-lg border p-4 ${notifyRoles.length === 0 ? 'border-destructive bg-destructive/5' : 'border-input bg-muted/30'}`}>
                                            <div>
                                                <label className="text-sm font-medium flex items-center gap-2">
                                                    <Bell className="h-4 w-4" />
                                                    Completion Notifications
                                                    <span className="text-destructive">*</span>
                                                </label>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Select at least one role to notify when the artist completes show details
                                                </p>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                {uniqueParticipantRolesForNotification.map((role) => (
                                                    <label key={role} className="flex items-center gap-2 p-2 bg-background rounded border cursor-pointer hover:bg-muted/50">
                                                        <input
                                                            type="checkbox"
                                                            checked={notifyRoles.includes(role)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setNotifyRoles([...notifyRoles, role]);
                                                                } else {
                                                                    setNotifyRoles(notifyRoles.filter(r => r !== role));
                                                                }
                                                            }}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm capitalize">{role.replace(/_/g, ' ')}</span>
                                                    </label>
                                                ))}
                                            </div>

                                            {notifyRoles.length === 0 && (
                                                <p className="text-xs text-destructive">
                                                    At least one notification role is required when using stub links.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    </div>
                                    )}

                                    {/* Show Photo */}
                                    <PhotoUploadField
                                        label="Show Photo"
                                        helper="Optional. You can use an artist's photo by clicking 'Use photo' on an artist above."
                                        value={formState.imageUrl}
                                        placeholder="https://"
                                        onChange={(url) => setFormState((prev) => ({ ...prev, imageUrl: url }))}
                                        onUploadStateChange={setIsPhotoUploading}
                                        category="happening_photo"
                                        identifier={photoIdentifier}
                                        accountSlug={account?.slug}
                                        imageFocus={formState.imageFocus}
                                        preferredRatio={preferredImageRatio}
                                        onOrientationMismatch={({ mismatched }) => setImageMismatched(mismatched)}
                                        compact
                                        allowBypassSizeLimit
                                    />
                                    {formState.imageUrl && imageMismatched && (
                                        <ImageFocusSelect
                                            value={formState.imageFocus}
                                            onChange={(val) => setFormState((prev) => ({ ...prev, imageFocus: val }))}
                                        />
                                    )}

                                    {/* Generate the announcement cards from the show photo, the same
                                        panel the artist-facing form uses. Collapsed by default —
                                        opening it is what opts this save into (re)making the cards. */}
                                    {editingHappening && isSocialCardsEnabled(account?.slug) && (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div>
                                                    <label className="text-sm font-medium">Announcement cards</label>
                                                    <p className="text-xs text-muted-foreground">
                                                        The show photo, framed for Instagram and SoundCloud. They save
                                                        with the show.
                                                    </p>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setShowCardGenerator((open) => !open)}
                                                >
                                                    {showCardGenerator ? 'Close' : 'Generate cards'}
                                                </Button>
                                            </div>
                                            {showCardGenerator && (
                                                <ShowCardsPanel
                                                    ref={cardsPanelRef}
                                                    photoSrc={formState.imageUrl || null}
                                                    artistName={
                                                        formState.selectedArtists[0]?.name || formState.title
                                                    }
                                                    showName={formState.title}
                                                    variant={formState.showType === 'resident' ? 'resident' : 'guest'}
                                                    savedCards={editingHappening.metadata?.social_cards ?? {}}
                                                    accountSlug={account?.slug}
                                                    // Keep both previews on screen inside the dialog.
                                                    previewMaxHeight={340}
                                                    onSave={async (cards) => {
                                                        await saveShowSocialCards(
                                                            editingHappening.id,
                                                            cards,
                                                            editingHappening.metadata?.social_cards ?? {},
                                                        );
                                                        setEditingHappening((prev) =>
                                                            prev
                                                                ? { ...prev, metadata: { ...prev.metadata, social_cards: cards } }
                                                                : prev,
                                                        );
                                                        queryClient.invalidateQueries({ queryKey: ['happenings'] });
                                                    }}
                                                />
                                            )}
                                            {cardsError && <p className="text-sm text-destructive">{cardsError}</p>}
                                        </div>
                                    )}

                                    {/* Cards generated for this show (artist form or /social-cards) */}
                                    {editingHappening && (
                                        <SocialCardsManager
                                            happeningId={editingHappening.id}
                                            cards={editingHappening.metadata?.social_cards ?? {}}
                                            onChange={(remaining) => {
                                                // The dialog reads from local state, so patch it
                                                // too — invalidation alone leaves the thumbnail up.
                                                setEditingHappening((prev) =>
                                                    prev
                                                        ? { ...prev, metadata: { ...prev.metadata, social_cards: remaining } }
                                                        : prev,
                                                );
                                                queryClient.invalidateQueries({ queryKey: ['happenings'] });
                                            }}
                                        />
                                    )}

                                    {/* Genres */}
                                    <div className="space-y-3 mb-6">
                                        <div>
                                            <label className="text-sm font-medium">Genres</label>
                                            <p className="text-xs text-muted-foreground">
                                                Max 4 genres. Type and press Enter or comma to add.
                                            </p>
                                        </div>
                                        <div className="relative">
                                            <input
                                                value={formState.genreInput}
                                                onChange={(e) => {
                                                    setFormState((prev) => ({ ...prev, genreInput: e.target.value }));
                                                    setShowGenreDropdown(true);
                                                }}
                                                onKeyDown={handleGenreInputKeyDown}
                                                onFocus={() => setShowGenreDropdown(true)}
                                                disabled={formState.genres.length >= 4}
                                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                                                placeholder={
                                                    formState.genres.length >= 4 ? 'Max 4 genres reached' : 'Type a genre...'
                                                }
                                            />
                                            {showGenreDropdown && genreSuggestions.length > 0 && (
                                                <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-md border bg-background shadow-lg">
                                                    {genreSuggestions.map((genre) => (
                                                        <button
                                                            key={genre}
                                                            type="button"
                                                            onClick={() => handleAddGenre(genre)}
                                                            className="w-full px-3 py-2 text-left hover:bg-muted"
                                                        >
                                                            {genre}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {formState.genres.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {formState.genres.map((genre) => (
                                                    <div
                                                        key={genre}
                                                        className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                                                    >
                                                        {genre}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveGenre(genre)}
                                                            className="hover:text-primary/70"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Tags */}
                                    <div className="space-y-3 mb-6">
                                        <div>
                                            <label className="text-sm font-medium">Tags</label>
                                            <p className="text-xs text-muted-foreground">
                                                Account-wide labels shown on the show card and in the list, so
                                                bookers can see at a glance what applies to a slot.
                                            </p>
                                        </div>
                                        <TagPicker
                                            value={formState.tagIds}
                                            onChange={(tagIds) => setFormState((prev) => ({ ...prev, tagIds }))}
                                        />
                                    </div>

                                    {/* Archive/Embed Links */}
                                    {editingHappening && (
                                        <div className="space-y-3 mb-6">
                                            <div>
                                                <label className="text-sm font-medium">Social card</label>
                                                <p className="text-xs text-muted-foreground">
                                                    Build this show's announcement graphic and attach it to the show.
                                                </p>
                                            </div>
                                            {editingHappening.metadata?.social_cards && Object.keys(editingHappening.metadata.social_cards).length > 0 && (
                                                <div className="flex flex-wrap gap-3">
                                                    {Object.entries(editingHappening.metadata.social_cards).map(([tid, url]) => (
                                                        <a key={tid} href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                                                            {tid} card
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/social-cards?happening=${editingHappening.id}`)}
                                                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
                                            >
                                                Open card editor
                                            </button>
                                        </div>
                                    )}

                                    <div className="space-y-3 mb-6">
                                        <div>
                                            <label className="text-sm font-medium">Archive/Embed Links</label>
                                            <p className="text-xs text-muted-foreground">
                                                Add links to archived recordings, embeds, or external content (e.g., SoundCloud, YouTube, custom platforms).
                                            </p>
                                        </div>

                                        {/* Existing links */}
                                        {formState.archiveLinks.length > 0 && (
                                            <div className="space-y-2">
                                                {formState.archiveLinks.map((link, index) => (
                                                    <div key={index} className="flex items-start gap-2 rounded-md border p-3">
                                                        <div className="flex-1 space-y-2">
                                                            {link.type === 'soundcloud' ? (
                                                                <>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                                            SoundCloud
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-sm">
                                                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate block">
                                                                            {link.url}
                                                                        </a>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-sm font-medium">{link.name}</span>
                                                                        {link.icon_slug && (
                                                                            <img
                                                                                src={`https://cdn.jsdelivr.net/npm/simple-icons@v15/icons/${link.icon_slug}.svg`}
                                                                                alt=""
                                                                                className="h-4 w-4"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="text-sm">
                                                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate block">
                                                                            {link.url}
                                                                        </a>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setFormState((prev) => ({
                                                                    ...prev,
                                                                    archiveLinks: prev.archiveLinks.filter((_, i) => i !== index),
                                                                }));
                                                            }}
                                                            className="text-muted-foreground hover:text-destructive"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <Button type="button" variant="outline" onClick={() => setShowArchiveDialog(true)}>
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add archive/embed link
                                        </Button>
                                        <p className="text-xs text-muted-foreground">
                                            Add SoundCloud, YouTube, Mixcloud, or any other embed/recording link.
                                        </p>
                                    </div>

                                    {/* Running order: where each set starts in a takeover's full recording */}
                                    {selectedType === 'show' && (formState.showType === 'takeover' || formState.sets.length > 0) && (
                                        <div className="space-y-3 mb-6">
                                            <div>
                                                <label className="text-sm font-medium">Running order</label>
                                                <p className="text-xs text-muted-foreground">
                                                    Where each artist's set starts in the full-show recording. Lets listeners jump to a set,
                                                    gives each artist a link to their own set in the archive email, and lets after hours
                                                    play single sets. Fill this in before adding the archive link so the email includes it.
                                                </p>
                                            </div>
                                            <RunningOrderEditor
                                                value={formState.sets}
                                                onChange={(sets) => setFormState((prev) => ({ ...prev, sets }))}
                                                artists={formState.selectedArtists}
                                                soundcloudUrl={formState.archiveLinks.find((l) => l.type === 'soundcloud')?.url}
                                            />
                                        </div>
                                    )}

                                    {/* Tracklist */}
                                    <div className="space-y-3 mb-6">
                                        <div>
                                            <label className="text-sm font-medium flex items-center gap-1.5">
                                                <ListMusic className="h-4 w-4" />
                                                Tracklist
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                Paste a list of tracks played during this show (one per line, format: Artist - Track Name).
                                            </p>
                                        </div>

                                        {/* Bulk paste textarea */}
                                        <div className="space-y-2">
                                            <textarea
                                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                                rows={4}
                                                placeholder={"Aphex Twin - Windowlicker\nBoards of Canada - Roygbiv\nAutechre - Gantz Graf"}
                                                value={tracklistPasteText}
                                                onChange={(e) => setTracklistPasteText(e.target.value)}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                disabled={!tracklistPasteText.trim()}
                                                onClick={() => {
                                                    const lines = tracklistPasteText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
                                                    const parsed: TracklistItem[] = lines.map(line => {
                                                        const separatorIndex = line.indexOf(' - ');
                                                        if (separatorIndex > 0) {
                                                            return {
                                                                artist: line.substring(0, separatorIndex).trim(),
                                                                title: line.substring(separatorIndex + 3).trim(),
                                                                timestamp: null,
                                                            };
                                                        }
                                                        return { artist: '', title: line, timestamp: null };
                                                    });
                                                    setFormState(prev => ({
                                                        ...prev,
                                                        tracklist: [...prev.tracklist, ...parsed],
                                                    }));
                                                    setTracklistPasteText('');
                                                }}
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Parse Tracklist
                                            </Button>
                                        </div>

                                        {/* Parsed tracks table */}
                                        {formState.tracklist.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="rounded-md border">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead className="w-16"></TableHead>
                                                                <TableHead className="w-10">#</TableHead>
                                                                <TableHead>Artist</TableHead>
                                                                <TableHead>Title</TableHead>
                                                                <TableHead className="w-28">Timestamp</TableHead>
                                                                <TableHead className="w-10"></TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {formState.tracklist.map((track, index) => (
                                                                <TableRow key={index}>
                                                                    <TableCell className="p-1">
                                                                        <div className="flex items-center gap-0.5">
                                                                            <button
                                                                                type="button"
                                                                                disabled={index === 0}
                                                                                onClick={() => {
                                                                                    setFormState(prev => {
                                                                                        const t = [...prev.tracklist];
                                                                                        [t[index - 1], t[index]] = [t[index], t[index - 1]];
                                                                                        return { ...prev, tracklist: t };
                                                                                    });
                                                                                }}
                                                                                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                                            >
                                                                                <ArrowUp className="h-3.5 w-3.5" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                disabled={index === formState.tracklist.length - 1}
                                                                                onClick={() => {
                                                                                    setFormState(prev => {
                                                                                        const t = [...prev.tracklist];
                                                                                        [t[index], t[index + 1]] = [t[index + 1], t[index]];
                                                                                        return { ...prev, tracklist: t };
                                                                                    });
                                                                                }}
                                                                                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                                            >
                                                                                <ArrowDown className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell className="text-muted-foreground text-xs">{index + 1}</TableCell>
                                                                    <TableCell className="text-sm">{track.artist || <span className="text-muted-foreground italic">—</span>}</TableCell>
                                                                    <TableCell className="text-sm">{track.title}</TableCell>
                                                                    <TableCell>
                                                                        <input
                                                                            type="text"
                                                                            className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                                                            placeholder="HH:MM:SS"
                                                                            value={track.timestamp || ''}
                                                                            onChange={(e) => {
                                                                                setFormState(prev => ({
                                                                                    ...prev,
                                                                                    tracklist: prev.tracklist.map((t, i) =>
                                                                                        i === index ? { ...t, timestamp: e.target.value || null } : t
                                                                                    ),
                                                                                }));
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setFormState(prev => ({
                                                                                    ...prev,
                                                                                    tracklist: prev.tracklist.filter((_, i) => i !== index),
                                                                                }));
                                                                            }}
                                                                            className="text-muted-foreground hover:text-destructive"
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive"
                                                    onClick={() => setFormState(prev => ({ ...prev, tracklist: [] }))}
                                                >
                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                    Clear all tracks
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Related Tasks - Backlinks */}
                                {editingHappening && (
                                    <div id="section-tasks" className="space-y-4 scroll-mt-4">
                                        <FormSectionHeading
                                            icon={ListTodo}
                                            title="Related tasks"
                                            badge={happeningTasks.length}
                                        />
                                        <div className="space-y-3 rounded-md border border-input p-4">
                                        <div className="flex items-center justify-between">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    // Create a new task object with related_happening_id pre-filled
                                                    const newTask: Task = {
                                                        id: 'new', // Temporary ID to indicate it's a new task
                                                        title: '',
                                                        type: 'booking',
                                                        description: null,
                                                        status: 'todo',
                                                        priority: 'medium',
                                                        due_date: null,
                                                        parent_task_id: null,
                                                        sort_order: 0,
                                                        template_task_key: null,
                                                        related_happening_id: editingHappening.id,
                                                        assignee_role: null,
                                                        assigned_to_id: null,
                                                        assigned_to_user_id: null,
                                                        account_id: editingHappening.account_id,
                                                        created_at: new Date().toISOString(),
                                                        updated_at: new Date().toISOString(),
                                                    };
                                                    setEditingTask(newTask);
                                                }}
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Task
                                            </Button>
                                        </div>
                                        {happeningTasks.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">
                                                No tasks linked to this happening yet. Click "Add Task" to create one.
                                            </p>
                                        ) : (
                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                {happeningTasks.map((task) => (
                                                    <div
                                                        key={task.id}
                                                        onClick={() => setEditingTask(task)}
                                                        className="flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    updateTaskMutation.mutate({
                                                                        id: task.id,
                                                                        data: { status: task.status === 'done' ? 'todo' : 'done' },
                                                                    });
                                                                }}
                                                                className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors hover:border-emerald-500 ${task.status === 'done'
                                                                    ? 'bg-emerald-500 border-emerald-500'
                                                                    : task.status === 'in_progress'
                                                                        ? 'border-blue-500'
                                                                        : task.status === 'blocked'
                                                                            ? 'border-red-500'
                                                                            : 'border-gray-300'
                                                                    }`}
                                                                title={task.status === 'done' ? 'Mark as todo' : 'Mark as done'}
                                                            >
                                                                {task.status === 'done' && (
                                                                    <CheckCircle2 className="h-3 w-3 text-white" />
                                                                )}
                                                            </button>
                                                            <span className={task.status === 'done' ? 'line-through text-muted-foreground' : ''}>
                                                                {task.title}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${task.priority === 'urgent'
                                                                    ? 'bg-red-100 text-red-900'
                                                                    : task.priority === 'high'
                                                                        ? 'bg-orange-100 text-orange-900'
                                                                        : task.priority === 'medium'
                                                                            ? 'bg-blue-100 text-blue-900'
                                                                            : 'bg-secondary text-secondary-foreground'
                                                                    }`}
                                                            >
                                                                {task.priority}
                                                            </span>
                                                            {task.due_date && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Due {new Date(task.due_date).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        </div>
                                    </div>
                                )}

                                {/* Reminders Section */}
                                <div id="section-reminders" className="space-y-4 scroll-mt-4">
                                    <FormSectionHeading icon={Bell} title="Reminders" />

                                    {/* Completion Reminders - Only show if artists are present */}
                                    {formState.selectedArtists.length > 0 && (
                                        <div className="space-y-3 mb-6 rounded-lg border border-input bg-muted/30 p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Mail className="h-4 w-4" />
                                                        Completion Reminders
                                                    </label>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Email reminders will be sent to {formState.selectedArtists[0].name} when the show is incomplete
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                {completionReminders.map((reminder, index) => {
                                                    const absHours = Math.abs(reminder.offset_hours);
                                                    const offsetDesc = reminder.offset_hours === 0
                                                        ? 'On start date'
                                                        : reminder.offset_hours < 0
                                                            ? absHours < 24
                                                                ? `${absHours} hour${absHours !== 1 ? 's' : ''} before`
                                                                : absHours < 168
                                                                    ? `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} before`
                                                                    : `${Math.floor(absHours / 168)} week${Math.floor(absHours / 168) !== 1 ? 's' : ''} before`
                                                            : absHours < 24
                                                                ? `${absHours} hour${absHours !== 1 ? 's' : ''} after`
                                                                : `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} after`;

                                                    return (
                                                        <div key={index} className="flex items-center gap-2 p-2 bg-background rounded border">
                                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                                            <span className="flex-1 text-sm">{offsetDesc}</span>
                                                            {existingCompletionReminders.find(r => r.id === reminder.id)?.sent_at && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Sent: {new Date(existingCompletionReminders.find(r => r.id === reminder.id)!.sent_at!).toLocaleString()}
                                                                </span>
                                                            )}
                                                            {existingCompletionReminders.find(r => r.id === reminder.id)?.error_message && (
                                                                <span className="text-xs text-destructive" title={existingCompletionReminders.find(r => r.id === reminder.id)!.error_message!}>
                                                                    Not sent — {existingCompletionReminders.find(r => r.id === reminder.id)!.error_message}
                                                                </span>
                                                            )}
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setCompletionReminders(completionReminders.filter((_, i) => i !== index));
                                                                }}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}

                                                {completionReminders.length === 0 && (
                                                    <p className="text-xs text-muted-foreground text-center py-2">
                                                        No reminders configured. Click "Add Reminder" to add one.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <select
                                                    id="new-completion-reminder-offset"
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    defaultValue="-120"
                                                >
                                                    <option value="-168">1 week before</option>
                                                    <option value="-120">5 days before</option>
                                                    <option value="-72">3 days before</option>
                                                    <option value="-48">2 days before</option>
                                                    <option value="-24">1 day before</option>
                                                    <option value="-12">12 hours before</option>
                                                    <option value="-6">6 hours before</option>
                                                    <option value="-1">1 hour before</option>
                                                </select>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        const select = document.getElementById('new-completion-reminder-offset') as HTMLSelectElement;
                                                        const offsetHours = parseInt(select.value, 10);
                                                        // Check if this offset already exists
                                                        if (!completionReminders.some(r => r.offset_hours === offsetHours)) {
                                                            setCompletionReminders([...completionReminders, { offset_hours: offsetHours }]);
                                                        }
                                                    }}
                                                >
                                                    <Plus className="h-4 w-4 mr-1" />
                                                    Add Reminder
                                                </Button>
                                            </div>

                                            {/* One-off send, outside the schedule. Needs a saved show to point the link at. */}
                                            {editingHappening && (
                                                <div className="space-y-1 border-t border-input pt-3">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            disabled={sendingNowKey !== null}
                                                            onClick={handleSendShowReminderNow}
                                                        >
                                                            <Mail className="h-4 w-4 mr-1" />
                                                            {sendingNowKey === 'show' ? 'Queuing…' : 'Send reminder immediately'}
                                                        </Button>
                                                        <ImmediateReminderStatus reminder={existingCompletionReminders.find(r => r.offset_hours === 0)} />
                                                    </div>
                                                    {sendNowError?.key === 'show' && (
                                                        <p className="text-xs text-destructive">{sendNowError.message}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Happening Reminders - Only show if artists and start_time are present */}
                                    {formState.selectedArtists.length > 0 && formState.startTime && (
                                        <div className="space-y-3 mb-6 rounded-lg border border-input bg-muted/30 p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Clock className="h-4 w-4" />
                                                        Show Reminders
                                                    </label>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Email reminders about the upcoming show (location, time, arrival info) will be sent to all artists
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                {happeningReminders.map((reminder, index) => {
                                                    const absHours = Math.abs(reminder.offset_hours);
                                                    const offsetDesc = reminder.offset_hours === 0
                                                        ? 'On start time'
                                                        : reminder.offset_hours < 0
                                                            ? absHours < 24
                                                                ? `${absHours} hour${absHours !== 1 ? 's' : ''} before`
                                                                : absHours < 168
                                                                    ? `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} before`
                                                                    : `${Math.floor(absHours / 168)} week${Math.floor(absHours / 168) !== 1 ? 's' : ''} before`
                                                            : absHours < 24
                                                                ? `${absHours} hour${absHours !== 1 ? 's' : ''} after`
                                                                : `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} after`;

                                                    return (
                                                        <div key={index} className="flex items-center gap-2 p-2 bg-background rounded border">
                                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                                            <span className="flex-1 text-sm">{offsetDesc}</span>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setHappeningReminders(happeningReminders.filter((_, i) => i !== index));
                                                                }}
                                                                className="h-8 w-8 p-0"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    );
                                                })}

                                                {happeningReminders.length === 0 && (
                                                    <p className="text-xs text-muted-foreground text-center py-2">
                                                        No reminders configured. Click "Add Reminder" to add one.
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex gap-2">
                                                <select
                                                    id="new-happening-reminder-offset"
                                                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                    defaultValue="-72"
                                                >
                                                    <option value="-168">1 week before</option>
                                                    <option value="-72">3 days before</option>
                                                    <option value="-48">2 days before</option>
                                                    <option value="-24">1 day before</option>
                                                    <option value="-12">12 hours before</option>
                                                    <option value="-6">6 hours before</option>
                                                    <option value="-1">1 hour before</option>
                                                    <option value="0">On start time</option>
                                                </select>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        const select = document.getElementById('new-happening-reminder-offset') as HTMLSelectElement;
                                                        const offsetHours = parseInt(select.value, 10);
                                                        // Check if this offset already exists
                                                        if (!happeningReminders.some(r => r.offset_hours === offsetHours)) {
                                                            setHappeningReminders([...happeningReminders, { offset_hours: offsetHours }]);
                                                        }
                                                    }}
                                                >
                                                    <Plus className="h-4 w-4 mr-1" />
                                                    Add Reminder
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Post-Show Email - Only show if artists and start_time are present */}
                                    {formState.selectedArtists.length > 0 && formState.startTime && (
                                        <div className="space-y-3 mb-6 rounded-lg border border-input bg-muted/30 p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <label className="text-sm font-medium flex items-center gap-2">
                                                        <Mail className="h-4 w-4" />
                                                        Post-Show Email
                                                    </label>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Send artists a follow-up email after the show with a tracklist publish link and optional feedback form
                                                    </p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={postShowEnabled}
                                                        onChange={(e) => setPostShowEnabled(e.target.checked)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                                </label>
                                            </div>

                                            {postShowEnabled && (
                                                <div className="space-y-3">
                                                    <div className="space-y-1">
                                                        <label className="text-xs font-medium text-muted-foreground">Send delay</label>
                                                        <select
                                                            value={postShowOffsetHours}
                                                            onChange={(e) => setPostShowOffsetHours(parseInt(e.target.value, 10))}
                                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                        >
                                                            <option value="1">1 hour after show</option>
                                                            <option value="6">6 hours after show</option>
                                                            <option value="12">12 hours after show</option>
                                                            <option value="24">1 day after show</option>
                                                            <option value="48">2 days after show</option>
                                                            <option value="72">3 days after show</option>
                                                        </select>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="text-xs font-medium text-muted-foreground">Feedback form URL (optional, overrides account default)</label>
                                                        <input
                                                            type="url"
                                                            value={postShowFeedbackUrl}
                                                            onChange={(e) => setPostShowFeedbackUrl(e.target.value)}
                                                            placeholder="https://forms.example.com/feedback"
                                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                        />
                                                    </div>

                                                    {/* Show sent status if reminders have been sent */}
                                                    {existingPostShowReminders.length > 0 && existingPostShowReminders.some(r => r.sent_at) && (
                                                        <div className="flex items-center gap-2 text-xs text-green-600">
                                                            <CheckCircle2 className="h-3 w-3" />
                                                            Post-show emails sent
                                                        </div>
                                                    )}

                                                    {/* Show pending status if reminders exist but not sent */}
                                                    {existingPostShowReminders.length > 0 && !existingPostShowReminders.some(r => r.sent_at) && (
                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                            <Clock className="h-3 w-3" />
                                                            Post-show emails scheduled
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Manual "Send Now" button for past shows */}
                                            {formState.startTime && new Date(formState.startTime) < new Date() && (
                                                <div className="pt-2 border-t border-input">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={existingPostShowReminders.some(r => r.sent_at)}
                                                        onClick={() => {
                                                            if (editingHappening) {
                                                                handleSendPostShowNow(editingHappening.id, formState.selectedArtists);
                                                            }
                                                        }}
                                                    >
                                                        <Mail className="h-4 w-4 mr-1" />
                                                        {existingPostShowReminders.some(r => r.sent_at) ? 'Already sent' : 'Send Now'}
                                                    </Button>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Sends the post-show email immediately to all artists on the next scheduler run
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Artist Profile Reminders - Show when artists are linked and at least one has incomplete profile */}
                                    {formState.selectedArtists.length > 0 && formState.startTime && (() => {
                                        // Find artists with incomplete profiles
                                        const artistsWithIncompleteProfiles = formState.selectedArtists
                                            .filter(artist => {
                                                if (!artist.person_id) return false;
                                                const person = allPeople.find(p => p.id === artist.person_id);
                                                if (!person) return false;
                                                const summary = summarizePerson(person);
                                                return summary.missingFields.length > 0;
                                            })
                                            .map(artist => {
                                                const person = allPeople.find(p => p.id === artist.person_id)!;
                                                const summary = summarizePerson(person);
                                                return { ...artist, person, missingFields: summary.missingFields };
                                            });

                                        if (artistsWithIncompleteProfiles.length === 0) return null;

                                        return (
                                            <div className="space-y-3 mb-6 rounded-lg border border-input bg-muted/30 p-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <label className="text-sm font-medium flex items-center gap-2">
                                                            <UserIcon className="h-4 w-4" />
                                                            Artist Profile Reminders
                                                        </label>
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            Email reminders for artists to complete their profiles before the show
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Show artists with incomplete profiles */}
                                                <div className="space-y-3">
                                                    {artistsWithIncompleteProfiles.map(artist => {
                                                        const artistReminders = profileReminders.filter(r => r.person_id === artist.person_id);

                                                        return (
                                                            <div key={artist.person_id} className="rounded border bg-background p-3">
                                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                                    <div>
                                                                        <span className="text-sm font-medium">{artist.name}</span>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            Missing: {artist.missingFields.join(', ')}
                                                                        </p>
                                                                        <ImmediateReminderStatus
                                                                            reminder={existingProfileReminders.find(r => r.entity_id === artist.person_id && r.offset_hours === 0)}
                                                                        />
                                                                        {sendNowError?.key === artist.person_id && (
                                                                            <p className="text-xs text-destructive">{sendNowError.message}</p>
                                                                        )}
                                                                    </div>
                                                                    {editingHappening && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="text-xs h-7 shrink-0"
                                                                            disabled={sendingNowKey !== null}
                                                                            onClick={() => handleSendProfileReminderNow(artist.person_id!)}
                                                                        >
                                                                            <Mail className="h-3 w-3 mr-1" />
                                                                            {sendingNowKey === artist.person_id ? 'Queuing…' : 'Remind immediately'}
                                                                        </Button>
                                                                    )}
                                                                </div>

                                                                {/* Existing reminders for this artist */}
                                                                {artistReminders.length > 0 && (
                                                                    <div className="space-y-1 mb-2">
                                                                        {artistReminders.map((reminder, index) => {
                                                                            const absHours = Math.abs(reminder.offset_hours);
                                                                            const offsetDesc = reminder.offset_hours === 0
                                                                                ? 'On start time'
                                                                                : reminder.offset_hours < 0
                                                                                    ? absHours < 24
                                                                                        ? `${absHours} hour${absHours !== 1 ? 's' : ''} before`
                                                                                        : absHours < 168
                                                                                            ? `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} before`
                                                                                            : `${Math.floor(absHours / 168)} week${Math.floor(absHours / 168) !== 1 ? 's' : ''} before`
                                                                                    : absHours < 24
                                                                                        ? `${absHours} hour${absHours !== 1 ? 's' : ''} after`
                                                                                        : `${Math.floor(absHours / 24)} day${Math.floor(absHours / 24) !== 1 ? 's' : ''} after`;

                                                                            return (
                                                                                <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                                                                                    <Clock className="h-3 w-3 text-muted-foreground" />
                                                                                    <span className="flex-1 text-xs">{offsetDesc}</span>
                                                                                    {existingProfileReminders.find(r => r.id === reminder.id)?.sent_at && (
                                                                                        <span className="text-xs text-muted-foreground">
                                                                                            Sent: {new Date(existingProfileReminders.find(r => r.id === reminder.id)!.sent_at!).toLocaleString()}
                                                                                        </span>
                                                                                    )}
                                                                                    {existingProfileReminders.find(r => r.id === reminder.id)?.error_message && (
                                                                                        <span className="text-xs text-destructive" title={existingProfileReminders.find(r => r.id === reminder.id)!.error_message!}>
                                                                                            Not sent — {existingProfileReminders.find(r => r.id === reminder.id)!.error_message}
                                                                                        </span>
                                                                                    )}
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="ghost"
                                                                                        size="sm"
                                                                                        onClick={() => {
                                                                                            setProfileReminders(profileReminders.filter(r =>
                                                                                                !(r.person_id === artist.person_id && r.offset_hours === reminder.offset_hours)
                                                                                            ));
                                                                                        }}
                                                                                        className="h-6 w-6 p-0"
                                                                                    >
                                                                                        <X className="h-3 w-3" />
                                                                                    </Button>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}

                                                                {/* Add reminder dropdown */}
                                                                <div className="flex gap-2">
                                                                    <select
                                                                        id={`new-profile-reminder-offset-${artist.person_id}`}
                                                                        className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs"
                                                                        defaultValue="-120"
                                                                    >
                                                                        <option value="-168">1 week before</option>
                                                                        <option value="-120">5 days before</option>
                                                                        <option value="-72">3 days before</option>
                                                                        <option value="-48">2 days before</option>
                                                                        <option value="-24">1 day before</option>
                                                                    </select>
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="text-xs h-7"
                                                                        onClick={() => {
                                                                            const select = document.getElementById(`new-profile-reminder-offset-${artist.person_id}`) as HTMLSelectElement;
                                                                            const offsetHours = parseInt(select.value, 10);
                                                                            // Check if this offset already exists for this artist
                                                                            if (!artistReminders.some(r => r.offset_hours === offsetHours)) {
                                                                                setProfileReminders([...profileReminders, {
                                                                                    offset_hours: offsetHours,
                                                                                    person_id: artist.person_id!
                                                                                }]);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Plus className="h-3 w-3 mr-1" />
                                                                        Add
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Resources Section */}
                                <div id="section-resources" className="scroll-mt-4">
                                    <FormSectionHeading icon={Music} title="Resources" />

                                    {/* Gear Requests */}
                                    <div className={`space-y-3 mb-6 rounded-md p-3 -mx-3 ${formState.gearRequests.length > 0 ? 'border border-destructive/30 bg-destructive/5' : ''}`}>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Required Gear
                                                {formState.gearRequests.length > 0 && (
                                                    <span className="ml-2 text-xs font-semibold text-destructive">
                                                        {formState.gearRequests.length} request{formState.gearRequests.length !== 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                Select equipment the artist will need for this show.
                                            </p>
                                        </div>
                                        {/* Selected gear requests */}
                                        {formState.gearRequests.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {formState.gearRequests.map((g) => (
                                                    <span key={g.thing_id} className="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs font-medium">
                                                        {g.name}
                                                        <button type="button" onClick={() => handleRemoveGearRequest(g.thing_id)} className="hover:text-destructive">
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {/* Async search to add gear */}
                                        <AsyncSearchableSelect
                                            searchFn={searchThings}
                                            selectedIds={formState.gearRequests.map((g) => g.thing_id)}
                                            onSelect={handleAddGearRequestOption}
                                            placeholder="Search equipment to add..."
                                            searchPlaceholder="Type to search equipment..."
                                        />
                                    </div>

                                    {/* Rider Notes */}
                                    <div className={`space-y-2 mb-6 rounded-md p-3 -mx-3 ${formState.riderNotes.trim() ? 'border border-destructive/30 bg-destructive/5' : ''}`}>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Additional Technical Notes
                                                {formState.riderNotes.trim() && (
                                                    <span className="ml-2 text-xs font-semibold text-destructive">Has notes</span>
                                                )}
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                Technical requirements, special setup needs, or notes from the artist.
                                            </p>
                                        </div>
                                        <textarea
                                            value={formState.riderNotes}
                                            onChange={(e) => setFormState((prev) => ({ ...prev, riderNotes: e.target.value }))}
                                            rows={3}
                                            className={`w-full rounded-md border bg-background px-3 py-2 text-sm ${formState.riderNotes.trim() ? 'border-destructive/30' : 'border-input'}`}
                                            placeholder="Any other technical requirements, special setup needs, or notes for the engineer..."
                                        />
                                    </div>
                                </div>

                                {/* Staffing Requirements Section — how many of each role this happening needs */}
                                <div id="section-staffing-requirements" className="space-y-4 scroll-mt-4">
                                    <FormSectionHeading
                                        icon={UserIcon}
                                        title="Staffing requirements"
                                        action={
                                            stageRoleDefaults.length > 0 ? (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setHappeningRoleReqs(
                                                            stageRoleDefaults.map((d) => ({ role_id: d.role_id, count: d.count })),
                                                        )
                                                    }
                                                >
                                                    Apply stage defaults
                                                </Button>
                                            ) : null
                                        }
                                    />
                                    <div className="space-y-3 rounded-md border border-input p-4">
                                        <p className="text-xs text-muted-foreground">
                                            How many of each role this performance needs. On a new happening these prefill
                                            from the stage's defaults. Set a count to 0 to mark a role explicitly not needed
                                            (e.g. a soundcheck with no stage manager).
                                        </p>
                                        <RoleRequirementsEditor
                                            value={happeningRoleReqs}
                                            onChange={setHappeningRoleReqs}
                                        />
                                    </div>
                                </div>

                                {/* Participants & Roles Section — creation mode (staged locally, persisted post-save) */}
                                {!editingHappening && (
                                    <div id="section-participants-create" className="space-y-4 scroll-mt-4">
                                        <FormSectionHeading
                                            icon={Users}
                                            title="Participants & roles"
                                            badge={formState.stagedParticipants.length}
                                        />
                                        <div className="space-y-3 rounded-md border border-input p-4">
                                            <p className="text-xs text-muted-foreground">
                                                Add team members or external people. They'll be saved once the happening is created.
                                            </p>

                                            {formState.stagedParticipants.length > 0 && (
                                                <div className="space-y-2">
                                                    {formState.stagedParticipants.map((p, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                                    {p.role.replace(/_/g, ' ')}
                                                                </span>
                                                                <span>{p.assignee_name}</span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    ({p.source === 'users' ? 'staff' : 'external'})
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setFormState((prev) => ({
                                                                        ...prev,
                                                                        stagedParticipants: prev.stagedParticipants.filter((_, i) => i !== idx),
                                                                    }))
                                                                }
                                                                className="text-muted-foreground hover:text-destructive"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="border-t pt-3 mt-3 space-y-2">
                                                <p className="text-sm font-medium">Add participant</p>
                                                <div className="flex gap-2 flex-wrap">
                                                    <input
                                                        type="text"
                                                        placeholder="Role (e.g., host, engineer)"
                                                        value={newParticipantRole}
                                                        onChange={(e) => setNewParticipantRole(e.target.value)}
                                                        className="flex-1 min-w-[140px] rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                                    />
                                                    <select
                                                        value={newParticipantSource}
                                                        onChange={(e) => {
                                                            setNewParticipantSource(e.target.value as 'users' | 'people');
                                                            setNewParticipantId('');
                                                        }}
                                                        className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                                                    >
                                                        <option value="users">Staff (admin/owner)</option>
                                                        <option value="people">External (people)</option>
                                                    </select>
                                                    <div className="flex-1 min-w-[200px]">
                                                        <AsyncSearchableSelect
                                                            searchFn={async (query) => {
                                                                if (newParticipantSource === 'users') {
                                                                    const users = await listUsers({ is_active: true, query, limit: 50 });
                                                                    return users
                                                                        .filter((u) => u.role === 'admin' || u.role === 'owner')
                                                                        .map((u) => ({ id: u.id, name: u.name, subtitle: u.role }));
                                                                }
                                                                const people = await listPeople({ query, limit: 20 });
                                                                return people.map((p) => ({ id: p.id, name: p.name, subtitle: p.type }));
                                                            }}
                                                            onSelect={(option) => {
                                                                if (!newParticipantRole.trim()) return;
                                                                setFormState((prev) => ({
                                                                    ...prev,
                                                                    stagedParticipants: [
                                                                        ...prev.stagedParticipants,
                                                                        {
                                                                            role: newParticipantRole.trim(),
                                                                            source: newParticipantSource,
                                                                            assignee_id: option.id,
                                                                            assignee_name: option.name,
                                                                        },
                                                                    ],
                                                                }));
                                                                setNewParticipantRole('');
                                                                setNewParticipantId('');
                                                            }}
                                                            placeholder="Search and pick assignee..."
                                                            searchPlaceholder={`Search ${newParticipantSource === 'users' ? 'staff' : 'people'}...`}
                                                        />
                                                    </div>
                                                </div>
                                                {!newParticipantRole.trim() && (
                                                    <p className="text-xs text-muted-foreground">Enter a role first, then pick a person.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Participants & Roles Section - Collapsed by default */}
                                {editingHappening && (
                                    <div id="section-participants" className="space-y-4 scroll-mt-4">
                                        <FormSectionHeading
                                            icon={Users}
                                            title="Participants & roles"
                                            badge={happeningParticipants.length}
                                            action={
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                                    onClick={() => setRolesExpanded(!rolesExpanded)}
                                                    aria-expanded={rolesExpanded}
                                                >
                                                    {rolesExpanded ? 'Hide' : 'Show'}
                                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${rolesExpanded ? 'rotate-180' : ''}`} />
                                                </button>
                                            }
                                        />
                                        {rolesExpanded && (
                                        <div className="space-y-3 rounded-md border border-input p-4">

                                        {/* Template-defined roles as fillable slots */}
                                        {editingHappening.metadata?.required_roles && editingHappening.metadata.required_roles.length > 0 ? (
                                            <div className="space-y-4">
                                                {editingHappening.metadata.required_roles.map((roleSpec) => {
                                                    const assignedParticipants = happeningParticipants.filter(p => p.role === roleSpec.role);
                                                    const isStaffRole = roleSpec.assignee_source === 'users' || roleSpec.person_type !== 'artist';

                                                    return (
                                                        <div key={roleSpec.role} className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary capitalize">
                                                                    {roleSpec.role.replace(/_/g, ' ')}
                                                                </span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    ({isStaffRole ? 'Staff' : 'Artist'})
                                                                    {roleSpec.min_count > 0 && ` • Required: ${roleSpec.min_count}`}
                                                                </span>
                                                            </div>

                                                            {/* Always show assignment row (real or placeholder) */}
                                                            {assignedParticipants.length === 0 ? (
                                                                <div className="ml-4 flex items-center gap-2 rounded-md border border-dashed bg-muted/10 p-2">
                                                                    <span className="text-sm text-muted-foreground">Unassigned</span>
                                                                    {isStaffRole ? (
                                                                        <select
                                                                            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm"
                                                                            defaultValue=""
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                if (value) {
                                                                                    addParticipantMutation.mutate({
                                                                                        happening_id: editingHappening!.id,
                                                                                        role: roleSpec.role,
                                                                                        user_id: value,
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            <option value="">Select staff…</option>
                                                                            {users.map((u) => (
                                                                                <option key={u.id} value={u.id}>
                                                                                    {u.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <div className="flex-1">
                                                                            <AsyncSearchableSelect
                                                                                searchFn={searchArtists}
                                                                                onSelect={(option) => {
                                                                                    addParticipantMutation.mutate({
                                                                                        happening_id: editingHappening!.id,
                                                                                        role: roleSpec.role,
                                                                                        person_id: option.id,
                                                                                    });
                                                                                }}
                                                                                placeholder="Select artist…"
                                                                                searchPlaceholder="Search artists…"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                assignedParticipants.map((p) => (
                                                                    <div key={p.id} className="ml-4 flex items-center gap-2 rounded-md border bg-muted/5 p-2">
                                                                        <span className="flex-1 text-sm font-medium">{p.user_name || p.person_name || 'Unknown'}</span>
                                                                        {isStaffRole ? (
                                                                            <select
                                                                                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                                                                                value={p.user_id || ''}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;
                                                                                    updateParticipantMutation.mutate({
                                                                                        id: p.id,
                                                                                        patch: { user_id: value || null },
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <option value="">Unassigned</option>
                                                                                {users.map((u) => (
                                                                                    <option key={u.id} value={u.id}>
                                                                                        {u.name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <div className="w-40">
                                                                                <AsyncSearchableSelect
                                                                                    searchFn={searchArtists}
                                                                                    onSelect={(option) => {
                                                                                        updateParticipantMutation.mutate({
                                                                                            id: p.id,
                                                                                            patch: { person_id: option.id },
                                                                                        });
                                                                                    }}
                                                                                    placeholder={p.person_name || 'Select…'}
                                                                                    searchPlaceholder="Search…"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeParticipantMutation.mutate(p.id)}
                                                                            className="text-muted-foreground hover:text-destructive"
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            /* Fallback for happenings without template roles - manual assignment */
                                            <>
                                                {happeningParticipants.length === 0 ? (
                                                    <p className="text-sm text-muted-foreground">
                                                        No roles defined. This happening wasn't created from a template.
                                                    </p>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {happeningParticipants.map((participant) => (
                                                            <div
                                                                key={participant.id}
                                                                className="flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                                        {participant.role.replace(/_/g, ' ')}
                                                                    </span>
                                                                    <span>{participant.user_name || participant.person_name || 'Unknown'}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeParticipantMutation.mutate(participant.id)}
                                                                    className="text-muted-foreground hover:text-destructive"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Manual add for non-template happenings */}
                                                <div className="border-t pt-3 mt-3 space-y-2">
                                                    <p className="text-sm font-medium">Add Role Assignment</p>
                                                    <div className="flex gap-2 flex-wrap">
                                                        <input
                                                            type="text"
                                                            placeholder="Role name"
                                                            value={newParticipantRole}
                                                            onChange={(e) => setNewParticipantRole(e.target.value)}
                                                            className="flex-1 min-w-[120px] rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                                                        />
                                                        <select
                                                            value={newParticipantSource}
                                                            onChange={(e) => {
                                                                setNewParticipantSource(e.target.value as 'users' | 'people');
                                                                setNewParticipantId('');
                                                            }}
                                                            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                                                        >
                                                            <option value="users">Staff</option>
                                                            <option value="people">External</option>
                                                        </select>
                                                        <div className="flex-1 min-w-[200px]">
                                                            <AsyncSearchableSelect
                                                                searchFn={newParticipantSource === 'users' ? searchUsers : searchArtists}
                                                                onSelect={(option) => setNewParticipantId(option.id)}
                                                                placeholder="Search assignee..."
                                                                searchPlaceholder={`Search ${newParticipantSource === 'users' ? 'staff' : 'people'}...`}
                                                            />
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            disabled={!newParticipantRole.trim() || !newParticipantId || addParticipantMutation.isPending}
                                                            onClick={() => {
                                                                const placeholder = happeningParticipants.find(p => !p.user_id && !p.person_id && p.role === newParticipantRole.trim());
                                                                if (placeholder) {
                                                                    updateParticipantMutation.mutate({
                                                                        id: placeholder.id,
                                                                        patch: newParticipantSource === 'users' ? { user_id: newParticipantId } : { person_id: newParticipantId }
                                                                    });
                                                                    setNewParticipantRole('');
                                                                    setNewParticipantId('');
                                                                } else {
                                                                    addParticipantMutation.mutate({
                                                                        happening_id: editingHappening.id,
                                                                        role: newParticipantRole.trim(),
                                                                        ...(newParticipantSource === 'users'
                                                                            ? { user_id: newParticipantId }
                                                                            : { person_id: newParticipantId }
                                                                        ),
                                                                    }, {
                                                                        onSuccess: () => {
                                                                            setNewParticipantRole('');
                                                                            setNewParticipantId('');
                                                                        },
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <Plus className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <p className="text-xs text-muted-foreground mt-2">
                                            Assigning someone to a role will auto-assign related tasks to them.
                                        </p>
                                        </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {mutationError && (
                                <p className="text-sm font-medium text-destructive px-6">{mutationError.message}</p>
                            )}

                            {editingHappening && canDelete && (
                                <div className="px-6">
                                    <DangerZone
                                        entityLabel="happening"
                                        onDelete={() => {
                                            handleDeleteClick(editingHappening);
                                        }}
                                        disabled={deleteMutation.isPending}
                                    />
                                </div>
                            )}

                            <DialogFooter className="gap-2 px-6 pb-6 sticky bottom-0 left-0 right-0 z-30 bg-background border-t pt-4 sm:justify-between sm:items-center">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="create-another"
                                        checked={createAnother}
                                        onChange={(e) => setCreateAnother(e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                                        disabled={editingHappening !== null}
                                    />
                                    <label htmlFor="create-another" className="text-sm font-medium cursor-pointer">
                                        Create another
                                    </label>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" onClick={attemptCloseDialog}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSaving || isPhotoUploading || (formState.generateStubLink && formState.selectedArtists.length > 0 && uniqueParticipantRolesForNotification.length > 0 && notifyRoles.length === 0)}
                                        variant={isHappeningDirty ? 'default' : 'secondary'}
                                    >
                                        {isSaving ? 'Saving…' : isPhotoUploading ? 'Uploading photo…' : isHappeningDirty ? 'Save changes (unsaved)' : editingHappening ? 'Save changes' : 'Create happening'}
                                    </Button>
                                </div>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Task Edit Dialog - overlays on top of happening dialog */}
            <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
                <DialogContent size="large" className="flex flex-col h-full sm:h-auto sm:max-h-[90vh] overflow-hidden p-0">
                    {editingTask && (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.currentTarget);
                                const assignedToUserId = formData.get('assigned_to_user_id') as string;
                                const taskData = {
                                    title: formData.get('title') as string,
                                    type: formData.get('type') as TaskType,
                                    description: formData.get('description') as string || null,
                                    status: formData.get('status') as TaskStatus,
                                    priority: formData.get('priority') as TaskPriority,
                                    due_date: formData.get('due_date') as string || null,
                                    assigned_to_user_id: assignedToUserId || null,
                                };

                                // Check if this is a new task (id === 'new')
                                if (editingTask.id === 'new') {
                                    createTaskMutation.mutate({
                                        ...taskData,
                                        related_happening_id: editingTask.related_happening_id || editingHappening?.id || null,
                                    });
                                } else {
                                    updateTaskMutation.mutate({
                                        id: editingTask.id,
                                        data: taskData,
                                    });
                                }
                            }}
                            className="flex flex-col flex-1 min-h-0"
                        >
                            {/* Sticky Header */}
                            <div className="sticky top-0 z-30 px-6 pt-6 pb-4 bg-background border-b shadow-lg" style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'hsl(var(--background))',
                            }}>
                                <DialogHeader>
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                                            {getTaskTypeLabel(editingTask.id === 'new' ? 'booking' : editingTask.type)}
                                        </span>
                                        {editingTask.id === 'new' ? 'Creating new' : 'Editing existing'}
                                    </div>
                                    <DialogTitle>{editingTask?.id === 'new' ? 'Create Task' : 'Edit Task'}</DialogTitle>
                                    <DialogDescription>
                                        {editingTask?.id === 'new' ? 'Create a new task linked to this happening.' : 'Update task details and status.'}
                                    </DialogDescription>
                                </DialogHeader>

                                {/* Quick status and priority controls */}
                                <div className="mt-4 pb-3">
                                    <div className="flex items-center gap-3">
                                        {/* Status dropdown */}
                                        <div className="flex-1">
                                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                                            <select
                                                name="status"
                                                defaultValue={editingTask.id === 'new' ? 'todo' : editingTask.status}
                                                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium"
                                            >
                                                <option value="todo">To Do</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="blocked">Blocked</option>
                                                <option value="done">Done</option>
                                            </select>
                                        </div>

                                        {/* Priority dropdown */}
                                        <div className="flex-1">
                                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
                                            <select
                                                name="priority"
                                                defaultValue={editingTask.id === 'new' ? 'medium' : editingTask.priority}
                                                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium"
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                                <option value="urgent">Urgent</option>
                                            </select>
                                        </div>

                                        {/* Related happening context */}
                                        {editingHappening && (
                                            <div className="flex-1">
                                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Happening</label>
                                                <div className="w-full rounded-md border px-3 py-1.5 text-xs bg-muted/30 text-muted-foreground overflow-hidden">
                                                    <div className="font-medium truncate">{editingHappening.title}</div>
                                                    {editingHappening.start_time && (
                                                        <div className="mt-0.5">
                                                            {new Date(editingHappening.start_time).toLocaleString(undefined, {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable Form Body */}
                            <div className="px-6 py-6 space-y-4 flex-1 min-h-0 overflow-y-auto">
                            <div>
                                <label className="text-sm font-medium">Title</label>
                                <input
                                    name="title"
                                    required
                                    defaultValue={editingTask.id === 'new' ? '' : editingTask.title}
                                    placeholder={editingTask.id === 'new' ? 'Enter task title...' : ''}
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Description</label>
                                <textarea
                                    name="description"
                                    defaultValue={editingTask.description || ''}
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Type</label>
                                <select
                                    name="type"
                                    defaultValue={editingTask.id === 'new' ? 'booking' : editingTask.type}
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                                >
                                    <option value="booking">Booking</option>
                                    <option value="preparation">Preparation</option>
                                    <option value="follow_up">Follow Up</option>
                                    <option value="technical">Technical</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Due Date</label>
                                <input
                                    name="due_date"
                                    type="datetime-local"
                                    defaultValue={editingTask.due_date ? editingTask.due_date.slice(0, 16) : ''}
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Assignee</label>
                                {editingTask.assignee_role && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                        (Role: {editingTask.assignee_role.replace(/_/g, ' ')})
                                    </span>
                                )}
                                <select
                                    name="assigned_to_user_id"
                                    defaultValue={editingTask.assigned_to_user_id || ''}
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                                >
                                    <option value="">Unassigned</option>
                                    {users.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.role})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            </div>

                            {editingTask.id !== 'new' && canDelete && (
                                <div className="px-6 pb-2">
                                    <DangerZone
                                        entityLabel="task"
                                        onDelete={() => handleTaskDeleteFromModal(editingTask)}
                                        disabled={deleteTaskMutation.isPending}
                                    />
                                </div>
                            )}

                            {/* Sticky Footer */}
                            <DialogFooter className="sticky bottom-0 left-0 right-0 bg-background border-t px-6 pb-6 pt-4">
                                <Button type="button" variant="outline" onClick={() => setEditingTask(null)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={createTaskMutation.isPending || updateTaskMutation.isPending}>
                                    {createTaskMutation.isPending || updateTaskMutation.isPending
                                        ? 'Saving...'
                                        : editingTask.id === 'new'
                                            ? 'Create Task'
                                            : 'Save Changes'}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Task Delete (from nested task modal danger zone) */}
            {deletingTaskRef && (
                <DeleteConfirmationDialog
                    open={taskDeleteDialogOpen}
                    onOpenChange={setTaskDeleteDialogOpen}
                    onConfirm={() => deleteTaskMutation.mutate(deletingTaskRef.id)}
                    entityName={deletingTaskRef.title}
                    entityType="task"
                    dependencies={taskDeleteDependencies || undefined}
                    isDeleting={deleteTaskMutation.isPending}
                />
            )}

            {/* Delete Confirmation Dialog */}
            {deletingHappening && (
                <DeleteConfirmationDialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    onConfirm={handleDeleteConfirm}
                    entityName={deletingHappening.title}
                    entityType="happening"
                    dependencies={deleteDependencies || undefined}
                    selectableDependencies={{ tasks: true, participants: false }}
                    isDeleting={deleteMutation.isPending}
                    episodeRenumber={
                        deleteLaterEpisodeCount > 0
                            ? {
                                  laterEpisodeCount: deleteLaterEpisodeCount,
                                  episodeNumber: deletingHappening.metadata?.episode_number as number | undefined,
                              }
                            : undefined
                    }
                    imageDeletion={
                        deleteImageSharing?.imageUrl
                            ? {
                                  url: deleteImageSharing.imageUrl,
                                  isShared: deleteImageSharing.isShared,
                                  usedBySeriesDefault: deleteImageSharing.usedBySeriesDefault,
                                  sharedLabels: deleteImageSharing.sharedWith.map(
                                      (h) => h.title || h.slug || h.id,
                                  ),
                              }
                            : undefined
                    }
                />
            )}

            {/* Episode Renumbering Dialog */}
            {editingHappening?.series_id && (
                <RenumberEpisodesDialog
                    open={renumberDialogOpen}
                    onOpenChange={setRenumberDialogOpen}
                    seriesId={editingHappening.series_id}
                    seriesName={editingHappening.title}
                    fromDate={editingHappening.start_time || undefined}
                    onApplied={() => {
                        notifyShowsUpdated();
                    }}
                />
            )}

            {/* Extend Series Dialog */}
            {editingHappening?.series_id && (
                <ExtendSeriesDialog
                    open={extendSeriesDialogOpen}
                    onOpenChange={setExtendSeriesDialogOpen}
                    seriesId={editingHappening.series_id}
                    onExtended={() => {
                        notifyShowsUpdated();
                    }}
                />
            )}

            {/* Bulk Delete Confirmation Dialog */}
            {selectedHappeningIds.size > 0 && (
                <DeleteConfirmationDialog
                    open={bulkDeleteDialogOpen}
                    onOpenChange={setBulkDeleteDialogOpen}
                    onConfirm={handleBulkDeleteConfirm}
                    entityName=""
                    entityType="happening"
                    entityCount={selectedHappeningIds.size}
                    dependencies={(() => {
                        // Aggregate dependencies from all selected happenings
                        const allDeps: DependencyCheckResult = {
                            hasDependencies: false,
                            dependencies: [],
                        };
                        const depMap = new Map<string, number>();
                        const depItems = new Map<string, Array<{ id: string; name: string }>>();

                        bulkDeleteDependencies.forEach((result) => {
                            if (result.hasDependencies) {
                                allDeps.hasDependencies = true;
                                result.dependencies.forEach((dep) => {
                                    const key = dep.type;
                                    depMap.set(key, (depMap.get(key) || 0) + dep.count);
                                    if (!depItems.has(key)) {
                                        depItems.set(key, []);
                                    }
                                    depItems.get(key)!.push(...dep.items);
                                });
                            }
                        });

                        depMap.forEach((count, type) => {
                            allDeps.dependencies.push({
                                type,
                                count,
                                items: depItems.get(type) || [],
                            });
                        });

                        return allDeps;
                    })()}
                    isDeleting={bulkDeleteMutation.isPending}
                />
            )}

            {/* Bulk Status Change Dialog */}
            <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Change status for {selectedHappeningIds.size} happening{selectedHappeningIds.size !== 1 ? 's' : ''}</DialogTitle>
                        <DialogDescription>
                            This sets the status on each selected happening. Series defaults and future instances are not affected.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <label className="text-sm font-medium block mb-2">New status</label>
                        <select
                            value={bulkStatusValue}
                            onChange={(e) => setBulkStatusValue(e.target.value as HappeningStatus)}
                            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        >
                            <option value="draft">Draft</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setBulkStatusDialogOpen(false)}
                            disabled={bulkStatusMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() =>
                                bulkStatusMutation.mutate({
                                    ids: Array.from(selectedHappeningIds),
                                    status: bulkStatusValue,
                                })
                            }
                            disabled={bulkStatusMutation.isPending || selectedHappeningIds.size === 0}
                        >
                            {bulkStatusMutation.isPending ? 'Updating…' : `Apply to ${selectedHappeningIds.size}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Invite confirmation — the one question that must be answered before saving a
                show with artists, so the email is never sent or skipped by accident. */}
            <SendInviteDialog
                open={showInviteConfirmDialog}
                onOpenChange={(open) => {
                    setShowInviteConfirmDialog(open);
                    // Dismissing without choosing abandons the save rather than guessing.
                    if (!open) setPendingPayload(null);
                }}
                artistName={
                    combinedInviteCandidates.find(c => c.person.id === formState.combinedInviteArtistId)?.artist.name
                    ?? combinedInviteCandidates[0]?.artist.name
                    ?? 'the artist'
                }
                otherArtistCount={
                    artistsWithIncompleteProfiles.filter(
                        c => c.person.id !== formState.combinedInviteArtistId,
                    ).length
                }
                willCreateLink={!editingHappening?.metadata?.stub_token}
                onSendEmail={() => resolveInviteDecision(orgDefaultInviteEmailStrategy === 'separate' ? 'separate' : 'combined')}
                onSendManually={() => resolveInviteDecision('none')}
                isLoading={createMutation.isPending || updateMutation.isPending}
            />

            {/* Apply to Series Dialog */}
            <ApplyToSeriesDialog
                open={showApplyToSeriesDialog}
                onOpenChange={(open) => {
                    setShowApplyToSeriesDialog(open);
                    if (!open) setPendingPayload(null);
                }}
                onApplyToThis={() => {
                    if (pendingPayload) {
                        saveHappening(pendingPayload, false);
                    }
                }}
                onApplyToFuture={() => {
                    if (pendingPayload) {
                        saveHappening(pendingPayload, true);
                    }
                }}
                isLoading={updateMutation.isPending || applyToFutureLoading}
                episodeRenumber={pendingEpisodeRenumber}
            />

            {/* Make Artists Public Dialog */}
            <Dialog open={showMakeArtistsPublicDialog} onOpenChange={setShowMakeArtistsPublicDialog}>
                <DialogContent size="medium">
                    <DialogHeader>
                        <DialogTitle>Make artists public?</DialogTitle>
                        <DialogDescription>
                            This show is now public, but the following artist{privateArtistsToPublish.length > 1 ? 's are' : ' is'} still private and won't be visible on the website:
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="list-disc pl-6 py-2">
                        {privateArtistsToPublish.map(p => (
                            <li key={p.id}>{p.name}</li>
                        ))}
                    </ul>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setShowMakeArtistsPublicDialog(false);
                                setPrivateArtistsToPublish([]);
                            }}
                        >
                            No, keep them private
                        </Button>
                        <Button type="button" onClick={handlePublishArtists}>
                            Yes, make them public
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
